import React, { Component } from "react";
import http from "../modules/api";
import SelectBoxV2 from "../modules/common/SelectBoxV2";
import TextInput from "../modules/common/TextInput";
import CheckBox from "../modules/common/CheckBox";
import RadioButton from '../modules/common/RadioButton';
import SaveDialogV2 from "../modules/dialogs/SaveDialogV2";
import BasicDialogV2 from "../modules/dialogs/BasicDialogV2";
import WarningDialog from "../modules/dialogs/WarningDialog";
import ConfirmDialogV2 from '../modules/dialogs/ConfirmDialogV2';
import TitleContentDialogV2 from '../modules/dialogs/TitleContentDialogV2';
import PopupFrameV2 from '../modules/popup/PopupFrameV2';
import AICopywriterModal from './component/AICopywriterModal';
import InstagramHashtagEditor from './component/InstagramHashtagEditor';
import DatePicker from "react-datepicker";
import { ko } from "date-fns/locale";
import { format } from "date-fns";
import "react-datepicker/dist/react-datepicker.css";
import AiImageStudioWorkflowClass from "../modules/common/AiImageStudioWorkflowClass";

const FACEBOOK_APP_ID = "780456611206591";
const BASEDOMAIN = document.domain.indexOf("127.0.0.1") >= 0 ? "https://127.0.0.1:8092" : window.location.origin;
const REDIRECT_URI = BASEDOMAIN + "/rest/shop/instagram/callbackForRefleshParent";
const SCOPES = [
  "pages_show_list",
  "business_management",
  "instagram_basic",
  "instagram_content_publish",
  "instagram_manage_insights",
  "instagram_manage_contents",
  "public_profile",
].join(",");

const isMobileFacebookOAuth = () => {
  try {
    const userAgent = navigator.userAgent || "";
    const isMobileUserAgent = /Android|iPhone|iPad|iPod|Mobile/i.test(userAgent);
    const isIPadOS = navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1;
    return isMobileUserAgent || isIPadOS;
  } catch (e) {
    return false;
  }
};

/**
 * 기존 새 탭 OAuth 흐름을 유지하면서 GET form submit으로 Facebook OAuth를 시작한다.
 * callbackForRefleshParent에서 기존 부모 창 갱신/팝업 처리 후 새 탭이 닫히는 흐름을 그대로 사용한다.
 */
const submitFacebookOAuthForm = ({ redirectUri } = {}) => {
  const form = document.createElement("form");
  form.method = "GET";
  form.action = "https://www.facebook.com/v24.0/dialog/oauth";
  form.target = "_blank";
  form.setAttribute("rel", "opener");
  form.style.display = "none";

  const oauthParams = {
    client_id: FACEBOOK_APP_ID,
    redirect_uri: redirectUri,
    scope: SCOPES,
    response_type: "code",
  };

  // 모바일에서는 reauthenticate가 Facebook 앱 전환을 유발할 수 있어 제외한다.
  // PC에서는 기존 동작대로 재로그인을 유도한다.
  if (!isMobileFacebookOAuth()) {
    oauthParams.auth_type = "reauthenticate";
  }

  Object.entries(oauthParams).forEach(([name, value]) => {
    const input = document.createElement("input");
    input.type = "hidden";
    input.name = name;
    input.value = value;
    form.appendChild(input);
  });

  document.body.appendChild(form);
  form.submit();
};

const PUBLISH_CYCLE_OPTIONS = [
  { label: "매일", value: "DAILY" },
  { label: "매주 선택한 요일", value: "WEEKLY" },
  { label: "격일", value: "BIDAY" },
  // { label: "주 3회", value: "THREETIMES" },
];

const AI_STUDIO_APPLY_AUTOMATION_RULE_STORAGE_KEY = "instagram_ai_studio_apply_automation_rule";
const AI_AUTOMATION_TUTORIAL_EVENT = "instagram-tutorial:ai-automation-setting-action";

const DAY_LABELS = ["일", "월", "화", "수", "목", "금", "토"];
const DAY_TO_INDEX = { "일": 0, "월": 1, "화": 2, "수": 3, "목": 4, "금": 5, "토": 6 };
const INDEX_TO_DAY = { 0: "일", 1: "월", 2: "화", 3: "수", 4: "목", 5: "금", 6: "토" };

const BASIC_AI_PRESET = Object.freeze({
  id: "",
  title: "베이직 AI",
  description: "AI 카피라이터 설정 없이 기본 방식으로 인스타그램 문장을 생성합니다.",
  voice: "",
  learning: "",
  weight: "",
  isBasic: true,
});

const createBasicAIPreset = () => ({ ...BASIC_AI_PRESET });
const COPYWRITER_NOT_SELECTED = "NONE";

const normalizeCopywriterPresetSeq = (value) => {
  const normalized = String(value ?? "").trim();
  return !normalized || normalized === "0" ? null : normalized;
};

const getLabelByValue = (list, value) => {
  const found = list.find((item) => item.value === value);
  return found ? found.label : "";
};

const parseBackendISO = (value) => {
  if (!value) return null;
  const fixed = String(value).replace(/([+-]\d{2})(\d{2})$/, "$1:$2").replace("+00:00", "Z");
  const date = new Date(fixed);
  return Number.isNaN(date.getTime()) ? null : date;
};

const parseStartDateString = (value) => {
  if (!value) return format(new Date(), "yyyy-MM-dd");
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  const fixed = String(value).replace(/([+-]\d{2})(\d{2})$/, "$1:$2");
  const date = new Date(fixed);
  return Number.isNaN(date.getTime()) ? format(new Date(), "yyyy-MM-dd") : format(date, "yyyy-MM-dd");
};

const parseTimeStringHHmm = (value) => {
  if (!value) return "00:00";
  const match = String(value).trim().match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
  if (match) {
    return `${match[1].padStart(2, "0")}:${match[2].padStart(2, "0")}`;
  }
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return `${String(value.getHours()).padStart(2, "0")}:${String(value.getMinutes()).padStart(2, "0")}`;
  }
  return "00:00";
};


const sanitizeHashtagInput = (rawValue) =>
  String(rawValue || "").replace(/[^#A-Za-z0-9ㄱ-ㅎㅏ-ㅣ가-힣_\s]/g, "");

const normalizeHashtagToken = (rawValue) => {
  const normalized = String(rawValue || "")
    .replace(/^#+/, "")
    .replace(/[^A-Za-z0-9ㄱ-ㅎㅏ-ㅣ가-힣_]/g, "")
    .trim();

  return normalized ? `#${normalized}` : "";
};

const HASHTAG_ONLY_PATTERN = /^(?:\s*#[A-Za-z0-9ㄱ-ㅎㅏ-ㅣ가-힣_]+)+\s*$/;
const HASHTAG_TOKEN_PATTERN = /#[A-Za-z0-9ㄱ-ㅎㅏ-ㅣ가-힣_]+/g;

// 기존 저장값은 전체가 정상적인 #해시태그 형식일 때만 칩으로 분리한다.
// #이 없거나 일반 문구가 섞인 값은 기존 발행 결과를 바꾸지 않도록 원문 그대로 보존한다.
const parseStoredHashtagText = (value) => {
  const rawValue = String(value || "");

  if (!rawValue.trim()) {
    return { type: "EMPTY", rawValue: "", tags: [] };
  }

  if (!HASHTAG_ONLY_PATTERN.test(rawValue)) {
    return { type: "LEGACY_RAW", rawValue, tags: [] };
  }

  return {
    type: "HASHTAGS",
    rawValue,
    tags: rawValue.match(HASHTAG_TOKEN_PATTERN) || [],
  };
};

// 신규 입력과 즐겨찾는 태그는 # 유무와 관계없이 새 규칙에 맞춰 정규화한다.
const parseNewHashtagInput = (value) => {
  const result = [];

  String(value || "")
    .split(/[#\s]+/)
    .map(normalizeHashtagToken)
    .filter(Boolean)
    .forEach((tag) => {
      if (!result.includes(tag)) result.push(tag);
    });

  return result;
};

const styles = `
.fa {
  font-family: FontAwesome !important;
  font-style: normal;
  font-weight: normal;
}
.InstagramCreateAiImageAutomation_v2 {
  --primary-mint: #00e2e6;
  --primary-mint-hover: #00bfc2;
  --deep-navy: #0A1A2F;
  --deep-navy-hover: #111C3A;
  --bg-color: #f9fafb;
  --border-color: #e5e7eb;
  --text-main: #0A1A2F;
  --text-sub: #6b7280;
  --white: #ffffff;
  --radius-lg: 16px;
  --radius-md: 12px;
  --radius-sm: 8px;

  padding-top: 72px !important;
  max-width: 100% !important;

  display: flex;
  flex-direction: column;
  height: 100vh;
}
.InstagramCreateAiImageAutomation_v2 * { box-sizing: border-box; }
.InstagramCreateAiImageAutomation_v2 .header-row { position: sticky; top: 0; z-index: 1000; display: flex; align-items: center; justify-content: space-between; background: var(--white); padding: 16px 32px; border-bottom: 1px solid var(--border-color); box-shadow: 0 2px 10px rgba(10, 26, 47, 0.05); }
.InstagramCreateAiImageAutomation_v2 .page-title { font-size: 24px; font-weight: 700; display: flex; align-items: center; gap: 10px; color: var(--deep-navy); }
.InstagramCreateAiImageAutomation_v2 .page-title::before { content: ''; width: 6px; height: 24px; background: var(--primary-mint); border-radius: 3px; display: block; }
.InstagramCreateAiImageAutomation_v2 .header-actions { display: flex; align-items: center; gap: 12px; }
.InstagramCreateAiImageAutomation_v2 .main-container-wrapper { width: 100%; flex:1; overflow-y: auto; }
.InstagramCreateAiImageAutomation_v2 .main-container { padding: 40px 24px; max-width: 1040px; width: 100%; margin: 0 auto; }
.InstagramCreateAiImageAutomation_v2 .alert-banner { background: rgba(0, 226, 230, 0.05); border: 1px solid var(--primary-mint); border-radius: var(--radius-md); padding: 16px 24px; margin-bottom: 24px; display: flex; justify-content: space-between; align-items: center; gap: 16px; }
.InstagramCreateAiImageAutomation_v2 .alert-banner-text { color: var(--deep-navy); font-size: 14px; font-weight: 500; display: flex; align-items: center; gap: 10px; line-height: 1.2; }
.InstagramCreateAiImageAutomation_v2 .alert-banner-text strong { font-weight: 700; }
.InstagramCreateAiImageAutomation_v2 .btn-banner { background: var(--white); border: 1px solid var(--border-color); color: var(--deep-navy); border-radius: var(--radius-sm); padding: 0 16px; height: 34px; font-size: 13px; font-weight: 700; cursor: pointer; transition: 0.2s; white-space: nowrap; }
.InstagramCreateAiImageAutomation_v2 .btn-banner:hover { border-color: var(--primary-mint); background: #f0fdfe; }
.InstagramCreateAiImageAutomation_v2 .title-input-box { background: var(--white); border-radius: var(--radius-md); padding: 24px 32px; margin-bottom: 24px; border: 1px solid var(--border-color); box-shadow: 0 2px 8px rgba(10, 26, 47, 0.02); transition: all 0.2s; }
.InstagramCreateAiImageAutomation_v2 .title-input-box:focus-within { border-color: var(--primary-mint); box-shadow: 0 4px 12px rgba(0, 226, 230, 0.1); }
.InstagramCreateAiImageAutomation_v2 .title-label { font-size: 13px; font-weight: 600; color: var(--primary-mint-hover); margin-bottom: 4px; display: block; }
.InstagramCreateAiImageAutomation_v2 .title-input { width: 100% !important; border: none !important; border-bottom: 2px solid var(--border-color) !important; padding-bottom: 8px; font-size: 20px !important; font-weight: 700 !important; color: var(--deep-navy) !important; background: transparent !important; transition: 0.2s !important; outline: none !important; }
.InstagramCreateAiImageAutomation_v2 .title-input:focus { border-bottom-color: var(--primary-mint); }
.InstagramCreateAiImageAutomation_v2 .title-input::placeholder { color: #cbd5e1; font-weight: 500; }
.InstagramCreateAiImageAutomation_v2 .btn { height: 42px; padding: 0 20px; border-radius: var(--radius-sm); font-size: 14px; font-weight: 700; cursor: pointer; border: none; transition: 0.2s; display: inline-flex; align-items: center; justify-content: center; gap: 6px; white-space: nowrap; }
.InstagramCreateAiImageAutomation_v2 .btn-primary { background: var(--primary-mint); color: var(--deep-navy) !important; box-shadow: 0 2px 8px rgba(0, 226, 230, 0.2); }
.InstagramCreateAiImageAutomation_v2 .btn-primary:hover { background: var(--primary-mint-hover); transform: translateY(-1px); box-shadow: 0 4px 12px rgba(0, 226, 230, 0.3); }
.InstagramCreateAiImageAutomation_v2 .btn-primary:active { background: var(--primary-mint-hover) !important; transform: translateY(-1px); box-shadow: 0 4px 12px rgba(0, 226, 230, 0.3); }
.InstagramCreateAiImageAutomation_v2 .btn-secondary { background: var(--deep-navy); color: var(--white); box-shadow: 0 2px 8px rgba(10, 26, 47, 0.2); }
.InstagramCreateAiImageAutomation_v2 .btn-secondary:hover { background: var(--deep-navy-hover); transform: translateY(-1px); }
.InstagramCreateAiImageAutomation_v2 .btn-outline { background: var(--white); border: 1px solid #cbd5e1; color: var(--text-main); }
.InstagramCreateAiImageAutomation_v2 .btn-outline:hover { background: var(--bg-color); border-color: var(--deep-navy); }
.InstagramCreateAiImageAutomation_v2 .btn-sm { height: 34px; padding: 0 14px; font-size: 13px; }
.InstagramCreateAiImageAutomation_v2 .card { background: var(--white); border-radius: var(--radius-md); border: 1px solid var(--border-color); padding: 36px; margin-bottom: 24px; box-shadow: 0 2px 12px rgba(10, 26, 47, 0.03); }
.InstagramCreateAiImageAutomation_v2 .card-header { margin-bottom: 24px; background-color: transparent !important; border-bottom: none !important; padding: 0 !important; }
.InstagramCreateAiImageAutomation_v2 .card-title { font-size: 18px; font-weight: 800; color: var(--deep-navy); margin-bottom: 8px; display: flex; align-items: center; gap: 8px; }
.InstagramCreateAiImageAutomation_v2 .card-subtitle { font-size: 14px; color: var(--text-sub); line-height: 1.5; font-weight: 400; }
.InstagramCreateAiImageAutomation_v2 .selection-grid { display: grid; gap: 16px; margin-bottom: 24px; }
.InstagramCreateAiImageAutomation_v2 .grid-cols-2 { grid-template-columns: repeat(2, 1fr); }
.InstagramCreateAiImageAutomation_v2 .grid-cols-3 { grid-template-columns: repeat(3, 1fr); }
.InstagramCreateAiImageAutomation_v2 .grid-cols-4 { grid-template-columns: repeat(4, 1fr); }
.InstagramCreateAiImageAutomation_v2 .selection-card { display: flex; flex-direction: column; align-items: flex-start; text-align: left; padding: 24px 20px; border: 1px solid #cbd5e1; border-radius: 10px; background: var(--white); cursor: pointer; transition: all 0.2s; position: relative; justify-content: center; min-height: 110px; }
.InstagramCreateAiImageAutomation_v2 .selection-card:hover { border-color: var(--primary-mint); background: #f0fdfe; }
.InstagramCreateAiImageAutomation_v2 .selection-card.active { border-color: var(--primary-mint); background: rgba(0,226,230,0.05); box-shadow: 0 0 0 1px var(--primary-mint); }
.InstagramCreateAiImageAutomation_v2 .selection-card.active::after { content: '✓'; position: absolute; top: 16px; right: 16px; width: 20px; height: 20px; background: var(--primary-mint); color: var(--deep-navy); border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 12px; font-weight: 800; }
.InstagramCreateAiImageAutomation_v2 .selection-title { font-size: 17px; font-weight: 800; color: var(--deep-navy); margin-bottom: 8px; line-height: 1.3; }
.InstagramCreateAiImageAutomation_v2 .selection-desc { font-size: 13px; color: var(--text-sub); line-height: 1.4; word-break: keep-all; }
.InstagramCreateAiImageAutomation_v2 .slide-options { margin-top: 0; padding-top: 16px; border-top: 1px dashed #cbd5e1; display: none; width: 100%; }
.InstagramCreateAiImageAutomation_v2 .slide-options.show { display: block; }
.InstagramCreateAiImageAutomation_v2 .form-label { display: flex; gap: 4px; font-size: 14px; font-weight: 700; margin-bottom: 8px; color: var(--deep-navy); }
.InstagramCreateAiImageAutomation_v2 .form-control, .form-select { width: 100%; padding: 10px 14px; border: 1px solid #cbd5e1; border-radius: var(--radius-sm); font-size: 14px; color: var(--text-main); background: var(--white); font-family: inherit; transition: 0.2s; box-sizing: border-box; }
.InstagramCreateAiImageAutomation_v2 .form-control:focus, .form-select:focus { outline: none; border-color: var(--primary-mint); box-shadow: 0 0 0 2px rgba(0, 226, 230, 0.15); }
.InstagramCreateAiImageAutomation_v2 .form-control:placeholder { font-weight: 500; }
.InstagramCreateAiImageAutomation_v2 .applied-ai-box { background: #f0fdfe; border: 2px solid var(--primary-mint); border-radius: var(--radius-md); padding: 28px; position: relative; display: flex; flex-direction: column; gap: 16px; }
.InstagramCreateAiImageAutomation_v2 .applied-ai-header { display: flex; justify-content: space-between; align-items: flex-start; flex-wrap: wrap; gap: 16px; }
.InstagramCreateAiImageAutomation_v2 .applied-ai-title { font-size: 18px; font-weight: 800; color: var(--deep-navy); }
.InstagramCreateAiImageAutomation_v2 .applied-ai-tags { display: flex; gap: 8px; flex-wrap: wrap; }
.InstagramCreateAiImageAutomation_v2 .applied-tag { background: white; border: 1px solid #a5f3fc; color: #0891b2; padding: 6px 12px; border-radius: 6px; font-size: 13px; font-weight: 700; }
.InstagramCreateAiImageAutomation_v2 .text-input-group { display: flex; gap: 8px; margin-bottom: 8px; align-items: flex-start; }
.InstagramCreateAiImageAutomation_v2 .text-input-group > *:first-child { flex: 1; min-width: 0; }
.InstagramCreateAiImageAutomation_v2 .rotation-hint { font-size: 13px; font-weight: 600; color: var(--text-sub); margin-top: 8px; display: flex; align-items: center; gap: 6px; }
.InstagramCreateAiImageAutomation_v2 .radio-group { display: flex; flex-direction: row; gap: 32px; align-items: center; }
.InstagramCreateAiImageAutomation_v2 .radio-label { display: flex; align-items: center; gap: 4px; cursor: pointer; font-size: 15px; font-weight: 700; color: var(--deep-navy); }
.InstagramCreateAiImageAutomation_v2 .modal-overlay { display: none; position: fixed; top: 0; left: 0; width: 100%; height: 100%; background: rgba(10,26,47,0.6); z-index: 9999; align-items: center; justify-content: center; }
.InstagramCreateAiImageAutomation_v2 .modal-overlay.active { display: flex; }
.InstagramCreateAiImageAutomation_v2 .modal-box { background: var(--white); width: 740px; max-width: 90vw; border-radius: var(--radius-md); padding: 32px; box-shadow: 0 20px 50px rgba(0,0,0,0.2); display: flex; flex-direction: column; max-height: 85vh; }
.InstagramCreateAiImageAutomation_v2 .ai-preset-list { display: flex; flex-direction: column; gap: 12px; overflow-y: auto; padding-right: 4px; margin-bottom: 24px; }
.InstagramCreateAiImageAutomation_v2 .ai-preset-item { display: flex; justify-content: space-between; align-items: center; padding: 20px; border: 2px solid #e5e7eb; border-radius: 12px; background: white; cursor: pointer; transition: 0.2s; position: relative; }
.InstagramCreateAiImageAutomation_v2 .ai-preset-item.selected { border-color: var(--primary-mint); background: #f0fdfe; box-shadow: 0 0 0 1px var(--primary-mint); }
.InstagramCreateAiImageAutomation_v2 .day-button-wrap { display: flex; gap: 8px; flex-wrap: wrap; }
.InstagramCreateAiImageAutomation_v2 .day-button { min-width: 44px; height: 40px; border-radius: 8px; border: 1px solid #cbd5e1; background: #fff; cursor: pointer; font-weight: 700; }
.InstagramCreateAiImageAutomation_v2 .day-button.active { background: rgba(0,226,230,0.08); border-color: var(--primary-mint); color: var(--deep-navy); }
.InstagramCreateAiImageAutomation_v2 .inline-box { background: #f8fafc; border: 1px solid #e5e7eb; border-radius: var(--radius-md); padding: 18px; }

.InstagramCreateAiImageAutomation_v2 .favorite-hashtag-list { display: flex; flex-wrap: wrap; gap: 10px; }
.InstagramCreateAiImageAutomation_v2 .phrase-chip { padding: 8px 16px; border-radius: 20px; font-size: 13px; font-weight: 500; cursor: pointer; background: var(--bg-color); border: 1px solid transparent; color: #374151; display: inline-flex; align-items: center; gap: 6px; transition: 0.2s; }
.InstagramCreateAiImageAutomation_v2 button.phrase-chip { font-family: inherit; }
.InstagramCreateAiImageAutomation_v2 .phrase-chip:hover { background: #e2e8f0; border-color: #cbd5e1; }
.InstagramCreateAiImageAutomation_v2 .chip-hashtag { color: #374151 !important; background: #fff !important; border: 1px solid #7c8390 !important; }
.InstagramCreateAiImageAutomation_v2 .chip-new { border: 1px dashed #9ca3af; background: transparent; color: #374151; }
.InstagramCreateAiImageAutomation_v2 .chip-new:hover { border-color: var(--primary-mint) !important; background: #f0fdfe !important; }
.InstagramCreateAiImageAutomation_v2 .chip-delete { font-size: 14px; color: #9ca3af; padding-left: 2px; transition: 0.2s; }
.InstagramCreateAiImageAutomation_v2 .chip-delete:hover { color: #dc2626; transform: scale(1.2); }
.InstagramCreateAiImageAutomation_v2 .hashtag-set-list-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 12px; gap: 12px; }
.InstagramCreateAiImageAutomation_v2 .hashtag-set-list { display: flex; flex-direction: column; gap: 16px; }
.InstagramCreateAiImageAutomation_v2 .hashtag-set-card { padding: 18px; background: #fff; border: 1px solid #dbe2ea; border-radius: 10px; }
.InstagramCreateAiImageAutomation_v2 .hashtag-set-card-header { display: flex; align-items: center; justify-content: space-between; gap: 12px; margin-bottom: 12px; color: var(--deep-navy); font-size: 14px; }
.InstagramCreateAiImageAutomation_v2 .hashtag-set-card-body { border: 1px solid #e5e7eb; border-radius: 8px; padding: 12px 16px; background: #fff; }
.InstagramCreateAiImageAutomation_v2 .hashtag-set-delete-button { width: 30px; height: 30px; border-radius: 6px; border: 1px solid #fecaca; background: #fff; color: #ef4444; font-size: 18px; cursor: pointer; display: inline-flex; align-items: center; justify-content: center; }
.InstagramCreateAiImageAutomation_v2 .hashtag-set-delete-button:hover { background: #fef2f2; }
.InstagramCreateAiImageAutomation_v2 .hashtag-ai-guide { margin-top: 16px; background: rgba(0, 226, 230, 0.05); color: #009ca0; padding: 12px 16px; border-radius: var(--radius-sm); font-size: 13px; font-weight: 700; display: flex; align-items: flex-start; gap: 8px; line-height: 1.4; }
.InstagramCreateAiImageAutomation_v2 .favorite-tag-modal { width: 430px; max-width: 90vw; }
.InstagramCreateAiImageAutomation_v2 .favorite-tag-modal-title { font-size: 18px; font-weight: 800; color: var(--deep-navy); margin-bottom: 10px; }
.InstagramCreateAiImageAutomation_v2 .favorite-tag-modal-description { font-size: 13px; color: #64748b; margin-bottom: 12px; line-height: 1.5; }
.InstagramCreateAiImageAutomation_v2 .favorite-tag-modal-actions { display: flex; justify-content: flex-end; margin-top: 20px; gap: 8px; }
.InstagramCreateAiImageAutomation_v2 .account-card { display: flex; align-items: center; justify-content: space-between; gap: 16px; background: #fff8e1; border: 1px solid #ffd54f; border-radius: 12px; padding: 18px; }
.InstagramCreateAiImageAutomation_v2 .account-card-left { display: flex; align-items: center; gap: 14px; flex: 1; }
.InstagramCreateAiImageAutomation_v2 .account-alert-icon { width: 40px; height: 40px; border-radius: 50%; background: #ff9800; color: white; display: flex; align-items: center; justify-content: center; font-weight: 800; flex-shrink: 0; }
.InstagramCreateAiImageAutomation_v2 .feed-item-button { font-size: 14px; font-weight: 600; width: 100%; text-align: left; padding: 12px; border: 1px solid #e5e7eb; border-radius: 10px; background: #fff; cursor: pointer; }
.InstagramCreateAiImageAutomation_v2 .feed-item-button + .feed-item-button { margin-top: 8px; }
.InstagramCreateAiImageAutomation_v2 .feed-item-button.active { border-color: var(--primary-mint); background: #f0fdfe; }
.InstagramCreateAiImageAutomation_v2 .preview-card { background: #f8fafc; border: 1px solid #e5e7eb; border-radius: var(--radius-md); padding: 18px; margin-top: 16px; }
.InstagramCreateAiImageAutomation_v2 .preview-caption { font-size: 14px; line-height: 1.7; white-space: pre-wrap; }
.InstagramCreateAiImageAutomation_v2 .preview-hashtags { margin-top: 12px; color: #2563eb; font-size: 13px; word-break: break-all; }
.InstagramCreateAiImageAutomation_v2 .helper-text { font-size: 12px; color: #6b7280; margin-top: 8px; }
.InstagramCreateAiImageAutomation_v2 .hidden { display: none; }
.InstagramCreateAiImageAutomation_v2 .react-datepicker-wrapper { width: 100%; }
.InstagramCreateAiImageAutomation_v2 .ratio-container { display: flex; align-items: center; justify-content: space-between; background: #f8fafc; padding: 12px 20px; border-radius: var(--radius-sm); margin-bottom: 20px; border: 1px solid var(--border-color); flex-wrap: wrap; gap: 12px;}
.InstagramCreateAiImageAutomation_v2 .ratio-title { font-weight: 700; color: var(--deep-navy); display: flex; align-items: center; gap: 8px; font-size: 14px; }
.InstagramCreateAiImageAutomation_v2 .ratio-btn-group { display: flex; gap: 8px; flex-wrap: wrap;}
.InstagramCreateAiImageAutomation_v2 .ratio-btn { padding: 8px 16px; background: var(--white); border: 1px solid #cbd5e1; border-radius: var(--radius-sm); font-size: 13px; font-weight: 600; color: var(--text-sub); cursor: pointer; display: flex; align-items: center; gap: 6px; transition: 0.2s; }
.InstagramCreateAiImageAutomation_v2 .ratio-btn.active { border-color: var(--primary-mint); color: var(--deep-navy); box-shadow: 0 0 0 1px var(--primary-mint); background: rgba(0,226,230,0.05); font-weight: 700;}
.InstagramCreateAiImageAutomation_v2 .ratio-btn:hover { border-color: var(--primary-mint) !important; background: rgba(0,226,230,0.05); }
.InstagramCreateAiImageAutomation_v2 .advanced-filter-box { background: #f8fafc; border: 1px solid #e5e7eb; border-radius: var(--radius-md); padding: 28px; margin-bottom: 24px; animation: slideDown 0.3s ease; }
.InstagramCreateAiImageAutomation_v2 .filter-row { display: flex; gap: 24px; margin-bottom: 24px; align-items: flex-end; }
.InstagramCreateAiImageAutomation_v2 .filter-row > div { flex: 1; }
.InstagramCreateAiImageAutomation_v2 .range-grid { display: grid; grid-template-columns: 100px 1fr 120px; align-items: center; gap: 16px; margin-bottom: 16px; }
.InstagramCreateAiImageAutomation_v2 .range-label { font-size: 14px; font-weight: 700; color: var(--deep-navy); }
.InstagramCreateAiImageAutomation_v2 .range-inputs { display: flex; align-items: center; gap: 8px; }
.InstagramCreateAiImageAutomation_v2 .range-inputs input { text-align: right; }
.InstagramCreateAiImageAutomation_v2 .range-inputs .unit { font-size: 13px; color: var(--text-sub); font-weight: 600; white-space: nowrap; }
.InstagramCreateAiImageAutomation_v2 .filter-checkbox { display: flex; align-items: center; gap: 6px; font-size: 14px; font-weight: 600; color: var(--deep-navy); cursor: pointer; }
.InstagramCreateAiImageAutomation_v2 .filter-checkbox input { accent-color: var(--primary-mint); width: 18px; height: 18px; cursor: pointer; }
.InstagramCreateAiImageAutomation_v2 .filter-warning { width:100%; display: inline-flex; align-items: center; gap: 6px; font-size: 13px; font-weight: 700; color: #dc2626; background: #fef2f2; padding: 12px 16px; border-radius: 8px; border: 1px solid #fecaca; margin-top: 16px; }
.InstagramCreateAiImageAutomation_v2 .background-white { background-color: var(--white) !important; }
.InstagramCreateAiImageAutomation_v2 .setting-link { display: inline-flex; align-items: center; gap: 4px; font-size: 13px; font-weight: 600; color: var(--text-sub); background: var(--bg-color); padding: 8px 16px; border-radius: 20px; border: 1px solid #cbd5e1; cursor: pointer; transition: 0.2s; text-decoration: none; line-height: 0;}
.InstagramCreateAiImageAutomation_v2 .setting-link:hover { border-color: var(--deep-navy); color: var(--deep-navy); background: white; }
.InstagramCreateAiImageAutomation_v2 #scheduledTime input, .InstagramCreateAiImageAutomation_v2 #startDate input { height: 43px; }
/* RoboMD State Dropdown Box */
.InstagramCreateAiImageAutomation_v2 .robomd-state-container { background: #f8fafc; border: 1px solid #cbd5e1; border-radius: var(--radius-md); padding: 24px; margin-bottom: 24px; animation: slideDown 0.3s ease; }
.InstagramCreateAiImageAutomation_v2 .robomd-state-box { display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 12px; background: white; padding: 16px 20px; border-radius: var(--radius-sm); border: 1px solid #e5e7eb; }
.InstagramCreateAiImageAutomation_v2 .state-desc { font-size: 13px; color: var(--text-sub); line-height: 1.5; margin-bottom: 16px; }
.InstagramCreateAiImageAutomation_v2 .robomd-list {
  background: #f9fafb;
  border-radius: 12px;
  padding: 16px;
  margin-top: 16px;

  .robomd-item {
    width: 100%;
    padding: 12px 16px;
    margin-bottom: 8px;
    background: #fff;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    text-align: left;
    cursor: pointer;
    transition: all .2s;
    font-size: 14px;
    white-space: pre-line;
  }

  .robomd-item:last-of-type {
    margin-bottom: 0px !important;
  }
}

.InstagramCreateAiImageAutomation_v2 .robomd-item:hover { 
  border-color: var(--primary-mint); 
  box-shadow: 0 4px 12px rgba(0, 226, 230, 0.05); 
  background: #f9fafb; 
}
.InstagramCreateAiImageAutomation_v2 .robomd-item.active {
  box-shadow: inset 0 0 0 1px #00e2e6;
  background: linear-gradient(135deg,#eff6ff 0%,rgba(0, 226, 230, 0.05) 100%);
  border-color: var(--primary-mint);
  color: #0A1A2F;
  font-weight: 600;
}
/* 툴팁 */
.InstagramCreateAiImageAutomation_v2 .tooltip-wrap { position: relative; display: inline-flex; align-items: center; margin-left: 6px; }
.InstagramCreateAiImageAutomation_v2 .tooltip-icon { display: inline-flex; align-items: center; justify-content: center; width: 18px; height: 18px; background: #e5e7eb; color: #4b5563; border-radius: 50%; font-size: 12px; font-weight: 800; cursor: help; transition: 0.2s; }
.InstagramCreateAiImageAutomation_v2 .tooltip-icon:hover { background: var(--deep-navy); color: white; }
.InstagramCreateAiImageAutomation_v2 .tooltip-content { 
    visibility: hidden; opacity: 0; position: absolute; bottom: 100%; left: 50%; transform: translateX(-50%) translateY(-4px); 
    background: var(--deep-navy); color: white; padding: 8px 12px; border-radius: 6px; font-size: 13px; font-weight: 400; 
    white-space: nowrap; z-index: 100; transition: all 0.2s; box-shadow: 0 4px 6px rgba(0,0,0,0.1); pointer-events: none;
}
.InstagramCreateAiImageAutomation_v2 .tooltip-content::after { content: ''; position: absolute; top: 100%; left: 50%; transform: translateX(-50%); border-width: 5px; border-style: solid; border-color: var(--deep-navy) transparent transparent transparent; }
.InstagramCreateAiImageAutomation_v2 .tooltip-wrap:hover .tooltip-content { visibility: visible; opacity: 1; transform: translateX(-50%) translateY(-8px); }

.InstagramCreateAiImageAutomation_v2 .btn:focus { box-shadow: none !important; }

.InstagramCreateAiImageAutomation_v2 .common_radio_button { gap: 8px !important; }
.InstagramCreateAiImageAutomation_v2 .common_radio_button .radio-label { margin: 0 !important; }

.InstagramCreateAiImageAutomation_v2 .rule-applied-container {
  background: #f8fafc;
  border-radius: var(--radius-md);
  padding: 24px;
  display: flex;
  gap: 24px;
  align-items: stretch;
  border: 1px solid var(--border-color);
}

.InstagramCreateAiImageAutomation_v2 .rule-type-box {
    width: 160px;
    border: 2px solid #67e8f9;
    border-radius: 12px;
    background: #ecfeff;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 16px;
    flex-shrink: 0;
    position: relative;
    overflow: hidden;
}

.InstagramCreateAiImageAutomation_v2 .rule-type-box.img-mode {
    padding: 0;
    background: transparent;
    border: none;
    display: block;
}

.InstagramCreateAiImageAutomation_v2 .rule-type-box.img-mode img {
    display: block;
    width: 160px;
    height: auto;
    border-radius: 12px;
}

.InstagramCreateAiImageAutomation_v2 .rule-prompt-box {
    flex: 1;
    background: var(--white);
    border: 1px solid var(--border-color);
    border-radius: var(--radius-sm);
    padding: 20px;
}

.InstagramCreateAiImageAutomation_v2 .rule-prompt-title {
  font-size: 13px;
  font-weight: 500;
  color: var(--deep-navy);
  margin-bottom: 3px;
  display: flex;
  justify-content: space-between;
  border-bottom: 1px solid var(--border-color);
  padding-bottom: 4px;
}

.InstagramCreateAiImageAutomation_v2 .rule-prompt-list {
  font-size: 14px;
  color: var(--deep-navy);
  margin: 0;
  padding-left: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.InstagramCreateAiImageAutomation_v2 .rule-prompt-list li {
  background: #f8fafc;
  padding: 12px 16px;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.InstagramCreateAiImageAutomation_v2 .rule-badge {
  display: inline-block;
  font-size: 11px;
  font-weight: 700;
  background: #e2e8f0;
  color: #475569;
  padding: 2px 8px;
  border-radius: 12px;
  margin-right: 6px;
}

.InstagramCreateAiImageAutomation_v2 .rule-badge.img {
  background: #dbeafe;
  color: #0369a1;
}

.InstagramCreateAiImageAutomation_v2 .rule-badge.txt {
  background: #fce7f3;
  color: #be185d;
}


/* 캐러셀 슬라이드 일관성 설정 */
.InstagramCreateAiImageAutomation_v2 .series-consistency-box {
  margin-bottom: 18px;
  padding: 18px 20px;
  border: 1px solid var(--border-color);
  border-radius: var(--radius-md);
  background: #f8fafc;
}
.InstagramCreateAiImageAutomation_v2 .series-consistency-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 20px;
}
.InstagramCreateAiImageAutomation_v2 .series-consistency-title {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
  color: var(--deep-navy);
  font-size: 15px;
  font-weight: 800;
}
.InstagramCreateAiImageAutomation_v2 .series-consistency-desc {
  margin: 0;
  color: var(--text-sub);
  font-size: 13px;
  line-height: 1.6;
}
.InstagramCreateAiImageAutomation_v2 .series-consistency-switch {
  flex: 0 0 auto;
  min-width: 74px;
  height: 38px;
  padding: 0 14px;
  border: 1px solid #cbd5e1;
  border-radius: 999px;
  background: #fff;
  color: #64748b;
  font-size: 13px;
  font-weight: 800;
  cursor: pointer;
}
.InstagramCreateAiImageAutomation_v2 .series-consistency-switch.active {
  border-color: var(--primary-mint);
  background: var(--primary-mint);
  color: var(--deep-navy);
}
.InstagramCreateAiImageAutomation_v2 .series-consistency-note {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px dashed #cbd5e1;
  color: #475569;
  font-size: 12px;
  line-height: 1.6;
}
.InstagramCreateAiImageAutomation_v2 .slide-anchor-label {
  display: inline-flex;
  align-items: center;
  margin-left: 8px;
  padding: 2px 8px;
  border-radius: 999px;
  background: #e0fafa;
  color: #087f83;
  font-size: 11px;
  font-weight: 800;
}

/* 슬라이드별 AI 이미지 생성 규칙 카드 */
.InstagramCreateAiImageAutomation_v2 .slide-rules-container {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
  overflow-x: hidden;
  padding-bottom: 0;
}
.InstagramCreateAiImageAutomation_v2 .slide-rule-card {
  min-width: 0;
  background: var(--white);
  border: 1px solid var(--border-color);
  border-radius: var(--radius-md);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  transition: 0.2s;
  box-shadow: 0 2px 10px rgba(10,26,47,0.02);
}
.InstagramCreateAiImageAutomation_v2 .slide-rule-card:hover {
  border-color: #cbd5e1;
  box-shadow: 0 6px 20px rgba(10,26,47,0.05);
}
.InstagramCreateAiImageAutomation_v2 .slide-rule-card.configured {
  border-color: var(--primary-mint);
  box-shadow: 0 0 0 1px rgba(0,226,230,0.2);
}
.InstagramCreateAiImageAutomation_v2 .slide-header {
  padding: 16px;
  background: #f8fafc;
  border-bottom: 1px solid var(--border-color);
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  font-weight: 800;
  font-size: 15px;
  color: var(--deep-navy);
}
.InstagramCreateAiImageAutomation_v2 .slide-badge {
  background: var(--deep-navy);
  color: var(--white);
  padding: 2px 10px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 800;
  white-space: nowrap;
}
.InstagramCreateAiImageAutomation_v2 .slide-badge.success {
  background: var(--primary-mint);
  color: var(--deep-navy);
}
.InstagramCreateAiImageAutomation_v2 .slide-empty {
  padding: 40px 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  text-align: center;
  flex: 1;
  min-height: 190px;
}
.InstagramCreateAiImageAutomation_v2 .slide-empty p {
  font-size: 13px;
  color: var(--text-sub);
  margin: 0;
}
.InstagramCreateAiImageAutomation_v2 .slide-filled {
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  flex: 1;
}
.InstagramCreateAiImageAutomation_v2 .slide-configured-body {
  display: flex;
  gap: 14px;
  align-items: flex-start;
}
.InstagramCreateAiImageAutomation_v2 .slide-configured-thumb {
  width: 96px;
  height: 96px;
  min-width: 96px;
  border-radius: var(--radius-sm);
  background-size: cover;
  background-position: center;
  border: 1px solid var(--border-color);
  position: relative;
  background-color: #ecfeff;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #0891b2;
  font-weight: 800;
  font-size: 12px;
  text-align: center;
  overflow: hidden;
}
.InstagramCreateAiImageAutomation_v2 .rule-count-badge {
  position: absolute;
  bottom: 6px;
  right: 6px;
  background: rgba(10,26,47,0.8);
  color: var(--white);
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 700;
}
.InstagramCreateAiImageAutomation_v2 .slide-configured-prompt-list {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
}
.InstagramCreateAiImageAutomation_v2 .slide-configured-desc {
  font-size: 13px;
  color: var(--text-sub);
  background: #f1f5f9;
  padding: 8px 12px;
  border-radius: 6px;
  font-weight: 600;
  line-height: 1.45;
  word-break: keep-all;
  border: 1px solid #e2e8f0;
}
.InstagramCreateAiImageAutomation_v2 .slide-configured-desc strong {
  display: block;
  color: var(--primary-mint-hover);
  font-size: 12px;
  margin-bottom: 4px;
}
.InstagramCreateAiImageAutomation_v2 .slide-action-area {
  padding: 16px;
  border-top: 1px solid var(--border-color);
  background: var(--white);
  display: flex;
  gap: 8px;
}
.InstagramCreateAiImageAutomation_v2 .slide-action-area .btn {
  flex: 1;
}

@media (max-width: 800px) {
  .InstagramCreateAiImageAutomation_v2 {
    @supports (height: 100dvh) {
      height: 100dvh;
    }
  }
  .InstagramCreateAiImageAutomation_v2#page-wrapper {
      padding-top: 60px !important;
      padding-bottom: 72px !important;
      padding-left: 0 !important;
      padding-right: 0 !important;
  }
  .InstagramCreateAiImageAutomation_v2 .main-container-wrapper { overflow-x: hidden; }    

  .InstagramCreateAiImageAutomation_v2 .slide-rules-container { grid-template-columns: 1fr !important; }
  .InstagramCreateAiImageAutomation_v2 .slide-configured-body { flex-direction: column; }
  .InstagramCreateAiImageAutomation_v2 .slide-configured-thumb { width: 120px; height: 120px; min-width: 120px; margin: 0 auto; }
  .InstagramCreateAiImageAutomation_v2 .card { padding: 16px; }
  .InstagramCreateAiImageAutomation_v2 #category_filter { grid-template-columns: repeat(1, 1fr) !important; }
  .InstagramCreateAiImageAutomation_v2 #category_filter_button { width: 100% !important; }
  .InstagramCreateAiImageAutomation_v2 .filter-row { flex-wrap: wrap !important; }
  .InstagramCreateAiImageAutomation_v2 .filter-col { width: 100% !important; flex: none !important;}

  .InstagramCreateAiImageAutomation_v2 .header-row { flex-direction: column; align-items: stretch; padding: 16px; gap: 16px; }
  .InstagramCreateAiImageAutomation_v2 .header-actions { flex-direction: column; width: 100%; }
  .InstagramCreateAiImageAutomation_v2 .header-actions > * { width: 100%; }
  .InstagramCreateAiImageAutomation_v2 .main-container { padding: 20px 16px; }
  .InstagramCreateAiImageAutomation_v2 .grid-cols-2, .InstagramCreateAiImageAutomation_v2 .grid-cols-3, .InstagramCreateAiImageAutomation_v2 .grid-cols-4 { grid-template-columns: 1fr !important; }
  .InstagramCreateAiImageAutomation_v2 .radio-group { flex-direction: column !important; align-items: flex-start !important; gap: 16px !important; }
  .InstagramCreateAiImageAutomation_v2 .alert-banner, .InstagramCreateAiImageAutomation_v2 .account-card { flex-direction: column; align-items: stretch; }
  
  .InstagramCreateAiImageAutomation_v2 .filter-range-grid { grid-template-columns: repeat(1, 1fr) !important; }
  .InstagramCreateAiImageAutomation_v2 .filter-range-label { width: 100% !important; }
  .InstagramCreateAiImageAutomation_v2 .filter-checkbox { justify-content: flex-start !important; }
  
  .InstagramCreateAiImageAutomation_v2 #scheduled_time_grid { grid-template-columns: repeat(1, 1fr) !important; }
  .InstagramCreateAiImageAutomation_v2 #hashtag_header { flex-direction: column; gap: 10px; }
  .InstagramCreateAiImageAutomation_v2 .hashtag-set-list-header { align-items: stretch; flex-direction: column; }
  .InstagramCreateAiImageAutomation_v2 .hashtag-set-list-header .btn { width: 100%; }
  .InstagramCreateAiImageAutomation_v2 .title-input-box { padding: 16px; }
  .InstagramCreateAiImageAutomation_v2 .slide-rules-container { grid-template-columns: 1fr !important; }

  .InstagramCreateAiImageAutomation_v2 .rule-applied-container {
    flex-direction: column;
  }

  .InstagramCreateAiImageAutomation_v2 .rule-type-box { width: 100%; min-height: 120px; }

  .InstagramCreateAiImageAutomation_v2 .rule-type-box.img-mode {
      width: 160px;
      min-height: 0;
      margin: 0 auto;
  }

  .InstagramCreateAiImageAutomation_v2 .rule-type-box.img-mode img {
      width: 160px;
      height: auto;
  }
}
`;

class InstagramCreateAiImageAutomation_v2 extends Component {
  constructor(props) {
    super(props);

    const settingSeq = window.location.search.includes("instagram_ai_image_auto_setting_seq=")
      ? window.location.search.split("instagram_ai_image_auto_setting_seq=")[1].split("&")[0]
      : null;

    const today = format(new Date(), "yyyy-MM-dd");

    this.state = {
      settingSeq,
      settingName: "", // 25 S/S 시즌 주력 상품 피드 자동화

      add_account_list: [],
      account_list: [],
      account_list_v2: [],
      selectedAccount: null,
      selected_ig_access_seq: "",
      default_ig_access_seq: "",
      loadedSettingWithoutIgAccount: false,
      accountConnected: false,
      token_id: null,
      show_account_connect_popup: false,
      selected_account_list: [],

      selectedRatio: "1/1",
      selectedFeedType: "SINGLE",
      selectedSlideCount: 5,
      selectedLogic: "ORDERS",
      selectedRoboMD: "",
      rp_event_setting_list: [],

      textMode: "AI",
      hashtagMode: "AI",
      timeMode: "SPECIFIC",
      publishCycle: "DAILY",
      activeDays: new Set(["월", "화", "수", "목", "금"]),
      startDate: today,
      scheduledTime: "10:00",

      customTexts: [""],
      hashtags: [""],
      textUserPrompt: "",
      aiHashtagInput: "",
      hashtagSetInputs: [""],
      favoriteTags: [],
      favoriteTagModalOpen: false,
      newFavoriteTagInput: "",
      feedCaptions: [],
      mediaList: [],
      selectedLearnedFeedCaptions: [],
      selectedLearnedFeedCaptionsCnt: 0,

      showAICopywriterModal: false,
      aiPresetSearchKeyword: "",

      copywriter_preset_seq: COPYWRITER_NOT_SELECTED,
      selectedAIPreset: null,
      tempSelectedAIPreset: null,
      copywriter_list: [],

      publish_type: "PUBLISH",
      is_enabled: "Y",

      select_category_1: "",
      select_category_2: "",
      select_category_3: "",
      select_category_4: "",

      event_item_created_period: "0",
      event_item_analysis_period: "0",

      is_any_item_price: true,
      is_any_item_hit: true,
      is_any_item_order_cnt: true,
      
      duplicate_check_type: "ALLOW_ALL",
      
      /** 로딩 다이얼로그 */
      save_dialog_show_state: false,
      save_dialog_content: "로딩중 입니다.",
      save_dialog_button_list: [
          {
              title: "확인",
              type: "1",
              display_yn:"N",
          }
      ],

      /** 확인,취소 다이얼로그 */
      confirm_dialog_show_state : false,
      confirm_dialog_content : "",
      confirm_dialog_button_list : [],

      /* BasicDialog 변수 */
      basic_dialog_show_state: false,
      basic_dialog_content: "",
      basic_dialog_button_list: [{title:"확인", event_method:()=>{this.setState({basic_dialog_show_state:false})}}],

      /* 경고 다이얼로그 */
      warning_dialog_show_state: false,
      warning_dialog_content: "",
      warning_dialog_button_list: [{title:"확인", event_method:()=>{this.setState({warning_dialog_show_state:false})}}],

      /* 제목,내용 다이얼로그 */
      title_content_dialog_title: "",
      title_content_dialog_content: "",
      title_content_dialog_button_list: [{title:"확인", event_method:()=>{this.setState({title_content_dialog_show_state:false})}}],
      title_content_dialog_show_state: false,

      event_item_created_period_list : [
          {value:0, title:"날짜 무관"},
          // {value:-1, title:"직접 지정"},
          // {value:"1/D", title:"최근 1일"},{value:"2/D", title:"최근 2일"},{value:"3/D", title:"최근 3일"},{value:"4/D", title:"최근 4일"},{value:"5/D", title:"최근 5일"},{value:"6/D", title:"최근 6일"},
          {value:"1/W", title:"최근 1주"},{value:"2/W", title:"최근 2주"},{value:"3/W", title:"최근 3주"},{value:"4/W", title:"최근 4주"},
          // {value:"5/W", title:"최근 5주"},{value:"6/W", title:"최근 6주"},
          {value:"8/W", title:"최근 8주"},
          // {value:"9/W", title:"최근 9주"},{value:"10/W", title:"최근 10주"},{value:"11/W", title:"최근 11주"},
          {value:"12/W", title:"최근 12주"},{value:"24/W", title:"최근 24주"},
          // {value:"1/M", title:"최근 1달"},{value:"2/M", title:"최근 2달"},{value:"3/M", title:"최근 3달"},{value:"4/M", title:"최근 4달"},{value:"5/M", title:"최근 5달"},{value:"6/M", title:"최근 6달"},
          // {value:"7/M", title:"최근 7달"},{value:"8/M", title:"최근 8달"},{value:"9/M", title:"최근 9달"},{value:"10/M", title:"최근 10달"},{value:"11/M", title:"최근 11달"},
          // {value:"1/Y", title:"최근 1년"},{value:"2/Y", title:"최근 2년"},{value:"3/Y", title:"최근 3년"},{value:"4/Y", title:"최근 4년"},            
      ],
      event_item_analysis_period_list : [
          {value:0, title:"날짜 무관"},
          // {value:"1/D", title:"최근 1일"},{value:"2/D", title:"최근 2일"},{value:"3/D", title:"최근 3일"},{value:"4/D", title:"최근 4일"},{value:"5/D", title:"최근 5일"},{value:"6/D", title:"최근 6일"},
          {value:1, title:"최근 1주"},{value:2, title:"최근 2주"},{value:3, title:"최근 3주"},{value:4, title:"최근 4주"},
          // {value:8, title:"최근 8주"},{value:12, title:"최근 12주"},{value:24, title:"최근 24주"},
          // {value:"3/M", title:"최근 3달"},
          // {value:"1/Y", title:"최근 1년"},{value:"2/Y", title:"최근 2년"},
      ],

      ai_image_rule_applied_yn: "N",
      ai_image_rule_prompt_list: [],
      ai_image_rule_thumb: "",

      show_ai_image_studio_popup: false,

      ai_studio_history_seq: "",
      ai_studio_history_title: "",
      ai_slide_rule_map: {},
      current_ai_rule_slide_no: 1,
      ai_studio_popup_edit_context: null,

      series_consistency_yn: "N",
      series_anchor_slide_no: 1,
      series_context_json: "",
      series_anchor_image_url: "",
      series_anchor_final_prompt_text: "",
      series_context_mode: "",
      series_context_history_seq: "",

      // show_account_connect_popup: true,
    };
  }

  handleFacebookOAuthReturn = () => {
    const searchParams = new URLSearchParams(window.location.search);
    const oauthStatus = searchParams.get("facebook_oauth_status");

    if (!oauthStatus) return false;

    const tokenId = searchParams.get("facebook_oauth_token_id") || "";

    try {
      const url = new URL(window.location.href);
      url.searchParams.delete("facebook_oauth_status");
      url.searchParams.delete("facebook_oauth_token_id");
      window.history.replaceState({}, "", `${url.pathname}${url.search}${url.hash}`);
    } catch (e) {
      // URL 정리 실패는 계정 연동 처리와 무관하므로 무시한다.
    }

    if (oauthStatus === "success" && tokenId) {
      this.setState(
        {
          token_id: tokenId,
          show_account_connect_popup: true,
        },
        () => this.loadInstagramAccountList(tokenId)
      );
    } else {
      this.setState({
        warning_dialog_show_state: true,
        warning_dialog_content: "페이스북 로그인 또는 권한 승인이 완료되지 않았습니다.",
      });
    }

    return true;
  };

  handleAiAutomationTutorialAction = (event) => {
    const action = event?.detail?.action;

    const ensureTutorialSnapshot = () => {
      if (this.aiAutomationTutorialSnapshot) return;

      this.aiAutomationTutorialSnapshot = {
        selectedFeedType: this.state.selectedFeedType,
        textMode: this.state.textMode,
        hashtagMode: this.state.hashtagMode,
        publishCycle: this.state.publishCycle,
      };
    };

    if (action === "SHOW_SLIDE_FEED") {
      ensureTutorialSnapshot();
      if (this.state.selectedFeedType !== "SLIDE") this.setState({ selectedFeedType: "SLIDE" });
      return;
    }

    if (action === "SHOW_SLIDE_CONSISTENCY") {
      ensureTutorialSnapshot();

      if (this.state.selectedFeedType !== "SLIDE") {
        this.setState({ selectedFeedType: "SLIDE" });
      }
      return;
    }

    if (action === "SHOW_COPY_AI") {
      ensureTutorialSnapshot();
      if (this.state.textMode !== "AI") this.setState({ textMode: "AI" });
      return;
    }

    if (action === "SHOW_COPY_LEARN") {
      ensureTutorialSnapshot();
      if (this.state.textMode !== "LEARN") this.setState({ textMode: "LEARN" });
      return;
    }

    if (action === "SHOW_COPY_DIRECT") {
      ensureTutorialSnapshot();
      if (this.state.textMode !== "DIRECT") this.setState({ textMode: "DIRECT" });
      return;
    }

    if (action === "SHOW_HASHTAG_AI") {
      ensureTutorialSnapshot();
      if (this.state.hashtagMode !== "AI") this.setState({ hashtagMode: "AI" });
      return;
    }

    if (action === "SHOW_HASHTAG_BASIC") {
      ensureTutorialSnapshot();
      if (this.state.hashtagMode !== "BASIC") this.setState({ hashtagMode: "BASIC" });
      return;
    }

    if (action === "SHOW_WEEKLY") {
      ensureTutorialSnapshot();
      if (this.state.publishCycle !== "WEEKLY") this.setState({ publishCycle: "WEEKLY" });
      return;
    }

    if (action === "RESTORE") {
      if (!this.aiAutomationTutorialSnapshot) return;

      const snapshot = this.aiAutomationTutorialSnapshot;
      this.aiAutomationTutorialSnapshot = null;

      this.setState({
        selectedFeedType: snapshot.selectedFeedType,
        textMode: snapshot.textMode,
        hashtagMode: snapshot.hashtagMode,
        publishCycle: snapshot.publishCycle,
      });
    }
  };

  componentDidMount() {
    this.instagramFreePreriod();
    const handledFacebookOAuthReturn = this.handleFacebookOAuthReturn();
    if (!handledFacebookOAuthReturn) {
      this.loadInstagramAccountList();
    }
    this.loadFavoriteHashtags();
    this.getRpStatus();
    // this.loadRpEventSettingList();
    this.loadProductCategory("", 1); // 최상위 카테고리 조회
    window.addEventListener("message", this.handleMessage);
    window.addEventListener(AI_AUTOMATION_TUTORIAL_EVENT, this.handleAiAutomationTutorialAction);

    let settingLoadPromise = Promise.resolve();

    if (this.state.settingSeq) {
      settingLoadPromise = this.loadInstagramAiImageAutoSetting();
    } else {
      this.loadRequestedCopywriterPreset();
    }

    settingLoadPromise.finally(() => {
      this.applyPendingAiStudioAutomationRule();
    });
  }

  componentWillUnmount() {
    window.removeEventListener("message", this.handleMessage);
    window.removeEventListener(AI_AUTOMATION_TUTORIAL_EVENT, this.handleAiAutomationTutorialAction);
    this.aiAutomationTutorialSnapshot = null;
  }

  isValidIgSeq = (value) => {
    if (value === null || value === undefined) return false;

    const v = String(value).trim();

    if (!v) return false;
    if (v === "none" || v === "add") return false;

    return true;
  };

  getActiveIgSeq = () => {
    const { selected_ig_access_seq, default_ig_access_seq, loadedSettingWithoutIgAccount } = this.state;

    if (this.isValidIgSeq(selected_ig_access_seq)) {
      return selected_ig_access_seq;
    }

    if (loadedSettingWithoutIgAccount) {
      return "";
    }

    if (this.isValidIgSeq(default_ig_access_seq)) {
      return default_ig_access_seq;
    }

    return "";
  };

  buildDisplayAccountList = (account_list = []) => {
    const hasRealAccount = Array.isArray(account_list) && account_list.length > 0;

    if (!hasRealAccount) {
      return [{ value: "add", title: "계정 연동하기" }];
    }

    return [
      { value: "none", title: "계정 미선택" },
      ...account_list,
      { value: "add", title: "계정 연동하기" },
    ];
  };

  buildDisplayAccountListV2 = (account_list_v2 = []) => {
    const hasRealAccount = Array.isArray(account_list_v2) && account_list_v2.length > 0;

    if (!hasRealAccount) {
      return [{ value: "add", title: "계정 연동하기", profile_picture_url: null }];
    }

    return [
      { value: "none", title: "계정 미선택", profile_picture_url: null },
      ...account_list_v2,
      { value: "add", title: "계정 연동하기", profile_picture_url: null },
    ];
  };

  loadProductCategory(parentCategory, depth) {
    const sendForm = {
        // user_seq: this.state.user_seq , 
        // shop_no: "1", 
        parent_category_no: parentCategory
    }

    var name = "product_category_list_"+depth;
    http.get("/rest/common/cafe24/category", {params:sendForm})
    .then(res => {
        this.setState({[name]: res.data.list});
    })
    .catch(error => {
      console.log ("===== error =====");
      console.log (error);
      console.log ("===== error request =====");
      console.log(error.request);
    });
  }

  handleMessage = (e) => {
    if (e.origin !== window.location.origin) return;

    if (e.data && e.data.type === "refreshInstagramAccount") {
      if (e.data.token_id) {
        this.setState(
          {
            token_id: e.data.token_id,
            show_account_connect_popup: true,
          },
          () => {
            setTimeout(() => {
              const result = this.loadInstagramAccountList(e.data.token_id);

              if (result && typeof result.finally === "function") {
                result.finally(() => {
                  if (e.source) {
                    e.source.postMessage(
                      { type: "refreshInstagramAccountDone" },
                      e.origin
                    );
                  }
                });
              } else {
                if (e.source) {
                  e.source.postMessage(
                    { type: "refreshInstagramAccountDone" },
                    e.origin
                  );
                }
              }
            }, 700);
          }
        );
      }
    }
  };

  openWarning = (message) => {
    this.setState({ warning_dialog_show_state: true, warning_dialog_content: message });
  };

  hasPendingAiStudioAutomationRule = () => {
    try {
      const searchParams = new URLSearchParams(window.location.search);
      return searchParams.get("apply_ai_studio_rule") === "Y";
    } catch (e) {
      return window.location.search.indexOf("apply_ai_studio_rule=Y") > -1;
    }
  };

  readPendingAiStudioAutomationRule = () => {
    if (!this.hasPendingAiStudioAutomationRule()) {
      return null;
    }

    const raw = sessionStorage.getItem(AI_STUDIO_APPLY_AUTOMATION_RULE_STORAGE_KEY);

    if (!raw) {
      return null;
    }

    try {
      return JSON.parse(raw);
    } catch (e) {
      console.error("AI 스튜디오 자동화 규칙 복원 실패:", e);
      return null;
    }
  };

  clearPendingAiStudioAutomationRule = () => {
    sessionStorage.removeItem(AI_STUDIO_APPLY_AUTOMATION_RULE_STORAGE_KEY);

    try {
      const url = new URL(window.location.href);
      if (url.searchParams.has("apply_ai_studio_rule")) {
        url.searchParams.delete("apply_ai_studio_rule");
        window.history.replaceState({}, "", url);
      }
    } catch (e) {
      // URL 정리에 실패해도 규칙 적용 자체에는 영향이 없도록 둔다.
    }
  };

  normalizeAiImageRulePromptList = (ruleData = {}) => {
    const rawPromptList = Array.isArray(ruleData.appliedPrompts)
      ? ruleData.appliedPrompts
      : Array.isArray(ruleData.ai_image_rule_prompt_list)
        ? ruleData.ai_image_rule_prompt_list
        : [];

    const sourceList = rawPromptList.length
      ? rawPromptList
      : Array.isArray(ruleData.steps)
        ? ruleData.steps
        : [];

    return sourceList
      .map((item, index) => {
        if (typeof item === "string") {
          return {
            stepNumber: index + 1,
            prompt: item,
            modeText: "(텍스트로만 생성)",
          };
        }

        if (!item) {
          return null;
        }

        const prompt = item.prompt || item.prompt_text || item.promptText || "";

        return {
          stepNumber: item.stepNumber || item.step_no || item.stepNo || index + 1,
          prompt,
          modeText:
            item.modeText ||
            item.mode_text ||
            (
              item.uploadedImage || item.source_image_url
                ? "(이미지 기반 변형)"
                : "(텍스트로만 생성)"
            ),
        };
      })
      .filter((item) => item && String(item.prompt || "").trim());
  };

  normalizeAiImageRuleThumb = (ruleData = {}) => {
    const thumb =
      ruleData.appliedThumb ||
      ruleData.ai_image_rule_thumb ||
      ruleData.thumb ||
      ruleData.source_image_url ||
      "";

    return typeof thumb === "string"
      ? thumb.replace(/^url\(['"]?/, "").replace(/['"]?\)$/, "")
      : "";
  };



  createEmptyAiImageRuleState = () => ({
    ai_image_rule_applied_yn: "N",
    ai_image_rule_prompt_list: [],
    ai_image_rule_thumb: "",
    ai_studio_history_seq: "",
    ai_studio_history_title: "",
  });

  parseAiStudioHistorySeqList = (value) => {
    if (Array.isArray(value)) {
      return value.map((item) => String(item || "").trim()).filter(Boolean);
    }

    if (value === null || value === undefined) return [];

    return String(value)
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean);
  };

  getAiImageRuleSlotCount = (feedType = this.state.selectedFeedType, slideCount = this.state.selectedSlideCount) => {
    if (feedType === "SLIDE") {
      const count = Number(slideCount) || 2;
      return Math.min(10, Math.max(2, count));
    }

    return 1;
  };

  buildLegacyRuleStateFromSlideRuleMap = (ruleMap = {}) => {
    const firstRule = Object.keys(ruleMap || {})
      .sort((a, b) => Number(a) - Number(b))
      .map((key) => ruleMap[key])
      .find((rule) => rule && rule.ai_image_rule_applied_yn === "Y");

    return firstRule ? { ...firstRule } : this.createEmptyAiImageRuleState();
  };

  normalizeAiSlideRuleMap = (ruleMap = {}, feedType = this.state.selectedFeedType, slideCount = this.state.selectedSlideCount, duplicateSingleRule = false) => {
    const count = this.getAiImageRuleSlotCount(feedType, slideCount);
    const sourceMap = ruleMap || {};
    const keys = Object.keys(sourceMap).filter((key) => sourceMap[key]);
    const nextMap = {};

    if (duplicateSingleRule && feedType === "SLIDE" && keys.length === 1 && sourceMap[keys[0]]) {
      for (let i = 1; i <= count; i += 1) {
        nextMap[i] = { ...sourceMap[keys[0]] };
      }
      return nextMap;
    }

    for (let i = 1; i <= count; i += 1) {
      if (sourceMap[i]) {
        nextMap[i] = { ...sourceMap[i] };
      }
    }

    return nextMap;
  };

  preserveAiSlideRuleMap = (ruleMap = {}) => {
    const sourceMap = ruleMap || {};
    const nextMap = {};

    Object.keys(sourceMap).forEach((key) => {
      const slideNo = Number(key);
      if (!Number.isInteger(slideNo) || slideNo < 1 || slideNo > 10 || !sourceMap[key]) return;
      nextMap[slideNo] = { ...sourceMap[key] };
    });

    return nextMap;
  };

  getVisibleAiSlideRuleMap = (ruleMap = {}, feedType = this.state.selectedFeedType, slideCount = this.state.selectedSlideCount) => {
    return this.normalizeAiSlideRuleMap(ruleMap, feedType, slideCount);
  };

  getAiImageRuleForSlide = (slideNo = 1) => {
    const key = String(slideNo || 1);
    const ruleMap = this.state.ai_slide_rule_map || {};
    return ruleMap[key] || null;
  };

  getAiStudioHistorySeqForSlide = (slideNo = 1) => {
    const rule = this.getAiImageRuleForSlide(slideNo);
    return rule && rule.ai_studio_history_seq ? rule.ai_studio_history_seq : "";
  };

  getConfiguredAiImageRuleCount = () => {
    const count = this.getAiImageRuleSlotCount();
    let configuredCount = 0;

    for (let i = 1; i <= count; i += 1) {
      const rule = this.getAiImageRuleForSlide(i);
      if (rule && rule.ai_studio_history_seq) configuredCount += 1;
    }

    return configuredCount;
  };

  buildAiStudioHistorySeqCsv = () => {
    const count = this.getAiImageRuleSlotCount();
    const seqList = [];

    for (let i = 1; i <= count; i += 1) {
      const rule = this.getAiImageRuleForSlide(i);
      if (rule && rule.ai_studio_history_seq) {
        seqList.push(String(rule.ai_studio_history_seq).trim());
      }
    }

    return seqList.filter(Boolean).join(",");
  };

  buildAiImageRuleMapFromHistorySeqValue = async (historySeqValue, feedType = "SINGLE", slideCount = 1) => {
    const count = this.getAiImageRuleSlotCount(feedType, slideCount);
    const seqList = this.parseAiStudioHistorySeqList(historySeqValue);

    if (!seqList.length) return {};

    const normalizedSeqList = feedType === "SLIDE" && seqList.length === 1
      ? Array(count).fill(seqList[0])
      : seqList.slice(0, count);

    const detailCache = {};
    const ruleMap = {};

    for (let i = 0; i < normalizedSeqList.length; i += 1) {
      const seq = normalizedSeqList[i];
      if (!seq) continue;

      try {
        if (!detailCache[seq]) {
          detailCache[seq] = await this.loadAiStudioHistoryDetail(seq);
        }

        const ruleState = this.buildAiImageRuleStateFromHistoryDetail(detailCache[seq]);
        ruleMap[i + 1] = {
          ...ruleState,
          ai_studio_history_seq: seq,
        };
      } catch (e) {
        console.error(`AI 스튜디오 상세 복원 실패(${seq}):`, e);
      }
    }

    return ruleMap;
  };

  buildAiImageRuleStateFromAppliedRule = (ruleData = {}) => {
    const promptList = this.normalizeAiImageRulePromptList(ruleData);
    const historySeq = ruleData.ai_studio_history_seq || ruleData.aiStudioHistorySeq || "";
    const historyTitle =
      ruleData.ai_studio_history_title ||
      ruleData.history_title ||
      ruleData.aiStudioHistoryTitle ||
      "";

    return {
      ai_image_rule_applied_yn: historySeq || promptList.length ? "Y" : "N",
      ai_image_rule_prompt_list: promptList,
      ai_image_rule_thumb: this.normalizeAiImageRuleThumb(ruleData),
      ai_studio_history_seq: historySeq,
      ai_studio_history_title: historyTitle,
      starter_sample_yn: String(ruleData.starter_sample_yn || ruleData.starterSampleYn || "N").toUpperCase() === "Y" ? "Y" : "N",
      starter_sample_title: ruleData.starter_sample_title || ruleData.starterSampleTitle || "",
    };
  };

  applyPendingAiStudioAutomationRule = async () => {
    const ruleData = this.readPendingAiStudioAutomationRule();

    if (!ruleData) {
      this.clearPendingAiStudioAutomationRule();
      return;
    }

    let nextRuleState = this.buildAiImageRuleStateFromAppliedRule(ruleData);

    if (nextRuleState.ai_studio_history_seq) {
      try {
        const historyDetail = await this.loadAiStudioHistoryDetail(nextRuleState.ai_studio_history_seq);
        const historyRuleState = this.buildAiImageRuleStateFromHistoryDetail(historyDetail);

        nextRuleState = {
          ...nextRuleState,
          starter_sample_yn: historyRuleState.starter_sample_yn || "N",
          starter_sample_title: historyRuleState.starter_sample_title || "",
          ...(nextRuleState.ai_image_rule_prompt_list.length === 0 ? {
            ai_image_rule_prompt_list: historyRuleState.ai_image_rule_prompt_list,
            ai_image_rule_thumb: historyRuleState.ai_image_rule_thumb || nextRuleState.ai_image_rule_thumb,
            ai_studio_history_title: historyRuleState.ai_studio_history_title || nextRuleState.ai_studio_history_title,
          } : {}),
        };
      } catch (e) {
        console.error("AI 스튜디오 상세 복원 실패:", e);
      }
    }

    const targetSlideNo = Number(ruleData.slide_no || ruleData.slideNo || this.state.current_ai_rule_slide_no || 1);
    const nextRuleMap = {
      ...(this.state.ai_slide_rule_map || {}),
      [targetSlideNo]: nextRuleState,
    };
    const preservedRuleMap = this.preserveAiSlideRuleMap(nextRuleMap);
    const visibleRuleMap = this.getVisibleAiSlideRuleMap(
      preservedRuleMap,
      this.state.selectedFeedType,
      this.state.selectedSlideCount
    );

    this.setState({
      ai_slide_rule_map: preservedRuleMap,
      ...this.buildLegacyRuleStateFromSlideRuleMap(visibleRuleMap),
    });
    this.clearPendingAiStudioAutomationRule();
  };

  buildIgAccountOptions = (apiPayload) => {
    const src = (apiPayload && (apiPayload.list || apiPayload)) || [];
    const options = [];

    src.forEach((fb) => {
      const fbName = fb.facebook_username || "(이름 없음)";
      const igList = fb.instagram_account_list || [];
      igList.forEach((ig) => {
        const value = String(ig.instagram_access_token_seq ?? "");
        if (!value) return;
        const username = ig.username ? `@${ig.username}` : "(계정명 없음)";
        options.push({ value, title: `${fbName} / ${username}` });
      });
    });

    return options;
  };

  buildIgAccountOptionsV2 = (apiPayload) => {
    const src = (apiPayload && (apiPayload.list || apiPayload)) || [];
    const options = [];

    src.forEach((fb) => {
      const igList = fb.instagram_account_list || [];
      igList.forEach((ig) => {
        const value = String(ig.instagram_access_token_seq ?? "");
        if (!value) return;
        options.push({
          value,
          title: ig.username ? `${ig.username}` : "(계정명 없음)",
          profile_picture_url: ig.profile_picture_url || null,
        });
      });
    });

    return options;
  };

  // buildIgDefaultAccount = (apiPayload) => {
  //   const src = (apiPayload && (apiPayload.list || apiPayload)) || [];

  //   for (const fb of src) {
  //     const igList = fb.instagram_account_list || [];
  //     const found = igList.find((ig) => String(ig.instagram_access_token_seq ?? "") && ig.default_yn === "Y");
  //     if (found) return String(found.instagram_access_token_seq);
  //     if (igList.length > 0) return String(igList[0].instagram_access_token_seq ?? "");
  //   }

  //   return "";
  // };

  buildIgDefaultAccount(apiPayload) {
    const src = (apiPayload && (apiPayload.list || apiPayload)) || [];
    
    // 1. 모든 facebook 객체에서 instagram_account_list를 추출하여 하나의 배열로 만듭니다.
    const allIgAccounts = src.reduce((acc, fb) => {
        return acc.concat(fb.instagram_account_list || []);
    }, []);

    if (allIgAccounts.length === 0) return "";

    // 2. 전체 배열에서 default_yn === 'Y'인 것을 찾습니다.
    const defaultAcc = allIgAccounts.find(ig => ig.default_yn === 'Y');

    // 3. 찾았다면 그 시퀀스를 반환, 없다면 배열의 첫 번째 계정을 반환합니다.
    if (defaultAcc) {
        return String(defaultAcc.instagram_access_token_seq ?? "");
    } else {
        return String(allIgAccounts[0].instagram_access_token_seq ?? "");
    }
  }

  loadInstagramAccountList = (token_id) => {
    const url = token_id
      ? `/rest/shop/instagram/profile/list?token_id=${token_id}`
      : "/rest/shop/instagram/profile/list";

    http
      .get(url)
      .then((res) => {
        const list = Array.isArray(res?.data?.list) ? res.data.list : [];
        // const list = []; // 빈 계정 테스트
        const account_list = this.buildIgAccountOptions(list);
        const account_list_v2 = this.buildIgAccountOptionsV2(list);
        const defaultSeq = this.buildIgDefaultAccount(list) || "";

        const currentSelected = this.state.selected_ig_access_seq || "";
        const activeSeq =
          currentSelected && currentSelected !== "add"
            ? currentSelected
            : defaultSeq;

        const targetAcc =
          account_list_v2.find((acc) => String(acc.value) === String(activeSeq)) || null;

        this.setState(
          {
            add_account_list: list,
            account_list,
            account_list_v2,
            default_ig_access_seq: defaultSeq,
            accountConnected: list.length > 0,
            selectedAccount: targetAcc,
            selected_ig_access_seq: currentSelected,
          },
          () => {
            // 방금 페이스북 연동 후 계정 선택 팝업을 띄우는 흐름에서는
            // 아직 updateAccountsUseYn 전이므로 피드 조회하지 않음
            if (token_id) return;

            const accessSeq = this.getActiveIgSeq();
            if (accessSeq) {
              this.fetchAllInstagramMedia(false, accessSeq);
            }
          }
        );
      })
      .catch((error) => {
        console.error(error);
      });
  };

  needConnectAccount() {
    this.setState({
        title_content_dialog_show_state: true,
        title_content_dialog_title: "<b>계정 연결 필요</b>",
        title_content_dialog_content: "계정 연결 후 이용하실 수 있습니다.<br/>지금 연결 하시겠습니까?",
        title_content_dialog_button_list: [
          {
            title: "네",
            event_method: () => {
              this.setState({ title_content_dialog_show_state: false });
              this.handleConnectAccount(); // 기존에 구현되어 있는 페이스북/인스타 연동 함수 실행
            }
          },
          {
            title: "아니요",
            event_method: () => {
              this.setState({ title_content_dialog_show_state: false });
            }
          }
        ]
    });
  }

  onChangeAccount = (text, value) => {
    if (value === "add") {
      this.needConnectAccount();
      // this.handleConnectAccount();
      return;
    }

    const targetAcc = (this.state.account_list_v2 || []).find(
      (acc) => String(acc.value) === String(value)
    );

    this.setState(
      {
        selected_ig_access_seq: value,
        selectedAccount: targetAcc || null,
        loadedSettingWithoutIgAccount: false,
      },
      () => {
        const accessSeq = this.getActiveIgSeq();
        if (this.isValidIgSeq(accessSeq)) {
          this.fetchAllInstagramMedia(true, accessSeq);
        }
      }
    );
  };

  handleConnectAccount = () => {
    submitFacebookOAuthForm({
      redirectUri: REDIRECT_URI,
    });
  };

  loadRpEventSettingList = () => {
    http.get("/rest/shop/instagram/rp/event/setting/list").then((res) => {
      const list = Array.isArray(res?.data?.list) ? res.data.list : [];
      this.setState({ rp_event_setting_list: list });
    });
  };

  instagramFreePreriod = () => {
    http
      .get("/rest/shop/instagram/FreePreriod")
      .then((res) => {
        if (res.data.is_finish != null) {
          this.setState({ instagram_is_finish: res.data.is_finish });
          if (res.data.is_finish === 1) {
            alert("이용 기간이 종료되었습니다.");
            window.location.href = "/shop/instagram/InstagramAiImageAutomationList";
          }
        }
      })
      .catch((error) => {
        console.error(error);
      });
  };

  sanitizeLearnCaption = (text) => {
    if (!text) return "";

    let caption = text;
    caption = caption.replace(/^\s*(#[A-Za-z0-9가-힣_]+\s*)+/gm, "");
    caption = caption.replace(/(^|\s)@[A-Za-z0-9._]+/g, "$1");
    caption = caption.replace(/([^\n#]+?)(\s*#[A-Za-z0-9가-힣_]+)+\s*$/gm, "$1");
    caption = caption.replace(/^\s*(#[A-Za-z0-9가-힣_]+\s*)+$/gm, "");
    caption = caption.replace(/\n{2,}/g, "\n").trim();
    return caption;
  };

  fetchAllInstagramMedia = (
    is_init = false,
    instagram_access_token_seq,
    pageLimit = 100,
    maxItems = 5000
  ) => {
    if (!this.isValidIgSeq(instagram_access_token_seq)) return;

    const accessSeq = String(instagram_access_token_seq).trim();

    http
      .get(`/rest/shop/instagram/${accessSeq}/media/all`, {
        params: { pageLimit, maxItems },
      })
      .then((response) => {
        const res = response.data;
        if (res && res.status === "success") {
          const mediaList = res.data?.data || [];

          const feedCaptions = mediaList
            .filter((item) => this.sanitizeLearnCaption(item.caption))
            .map((item) => ({
              instagram_media_id: item.id,
              caption: this.sanitizeLearnCaption(item.caption),
            }));

          this.setState((prev) => ({
            mediaList,
            feedCaptions,
            selectedLearnedFeedCaptions: is_init ? [] : prev.selectedLearnedFeedCaptions,
            selectedLearnedFeedCaptionsCnt: is_init ? 0 : prev.selectedLearnedFeedCaptionsCnt,
          }));
        }
      })
      .catch((error) => {
        console.error("Instagram 미디어 조회 오류:", error);
      });
  };

  loadInstagramAiImageAutoSetting = async () => {
    const { settingSeq } = this.state;
    if (!settingSeq) return;

    try {
      this.setState({
        save_dialog_show_state: true,
        save_dialog_content: "불러오는 중입니다..."
      });

      const response = await http.get(`/rest/shop/instagram/auto/ai-image/setting?settingSeq=${settingSeq}`);

      if (response?.data?.result !== "success") {
        alert("존재하지 않는 설정입니다.");
        window.location.href = "/shop/instagram/InstagramAiImageAutomationList";
        return;
      }

      const data = response.data.data || {};
      const startDate = parseStartDateString(data.startDate);
      const scheduledTime = parseTimeStringHHmm(data.scheduledTime);
      const loadedCopywriterPresetSeq = normalizeCopywriterPresetSeq(data.copywriter_preset_seq);
      const loadedSelectedAIPreset = loadedCopywriterPresetSeq
        ? (data.copywriter_preset_title
          ? {
              id: loadedCopywriterPresetSeq,
              title: data.copywriter_preset_title,
              description: "저장된 브랜드 톤과 작성 규칙을 적용합니다.",
              voice: "",
              learning: "",
              weight: "",
            }
          : null)
        : createBasicAIPreset();

      const activeDays = Array.isArray(data.activeWeekdays)
        ? new Set(data.activeWeekdays.map((idx) => INDEX_TO_DAY[idx]).filter(Boolean))
        : new Set(["월", "화", "수", "목", "금"]);

      const loadedFeedType = data.feedType || data.feed_type || "SINGLE";
      const loadedSlideCount = data.slideCount || data.slide_count || 5;
      const loadedHistorySeq = data.ai_studio_history_seq || data.aiStudioHistorySeq || "";
      const loadedRuleMap = await this.buildAiImageRuleMapFromHistorySeqValue(
        loadedHistorySeq,
        loadedFeedType,
        loadedSlideCount
      );
      const aiRuleState = this.buildLegacyRuleStateFromSlideRuleMap(loadedRuleMap);

      this.setState(
        {
          settingSeq: data.settingSeq,
          selected_ig_access_seq: String(data.instagramAccessTokenSeq || ""),
          loadedSettingWithoutIgAccount: !this.isValidIgSeq(data.instagramAccessTokenSeq),
          settingName: data.settingName || this.state.settingName,

          selectedFeedType: loadedFeedType,
          selectedSlideCount: loadedSlideCount,
          selectedRatio: data.image_ratio || "1/1",
          series_consistency_yn:
            String(data.seriesConsistencyYn || data.series_consistency_yn || "N").toUpperCase() === "Y"
              ? "Y"
              : "N",
          series_anchor_slide_no: Number(data.seriesAnchorSlideNo || data.series_anchor_slide_no || 1),

          textMode: data.textMode || "AI",
          hashtagMode: data.hashtagMode || "AI",
          publishCycle: data.publishCycle || "DAILY",
          timeMode: data.timeMode || "SPECIFIC",
          textUserPrompt: data.textUserPrompt || "",

          startDate,
          scheduledTime,
          regDate: parseBackendISO(data.regDate),
          modDate: parseBackendISO(data.modDate),
          activeDays,

          customTexts: data.captions && data.captions.length ? data.captions : [""],
          hashtags: data.hashtags && data.hashtags.length ? data.hashtags : [""],

          selectedLearnedFeedCaptions: data.learnedCaption || [],
          selectedLearnedFeedCaptionsCnt: (data.learnedCaption || []).length,

          copywriter_preset_seq: loadedCopywriterPresetSeq,
          selectedAIPreset: loadedSelectedAIPreset,
          publish_type: data.publish_type || "PUBLISH",
          is_enabled: String(data.isEnabled || data.is_enabled || "N").toUpperCase() === "Y" ? "Y" : "N",

          ai_slide_rule_map: loadedRuleMap,
          ...aiRuleState,
        },
        () => {
          const accessSeq = this.getActiveIgSeq();
          if (accessSeq) this.fetchAllInstagramMedia(false, accessSeq);
          this.asyncSelectedAIPreset();
        }
      );
    } catch (error) {
      const errMsg = error.response?.data?.message || error.message || "알 수 없는 오류";
      alert(`조회 실패: ${errMsg}`);
      console.error(error);
    } finally {
      this.setState({ save_dialog_show_state: false });
    }
  };

  saveInstagramAiImageAutoSetting = async (options = {}) => {
    const shouldOpenSuccessDialog = !(options && options.silent === true);
    const {
      settingSeq,
      settingName,
      selectedFeedType,
      selectedSlideCount,
      textMode,
      hashtagMode,
      publishCycle,
      timeMode,
      scheduledTime,
      startDate,
      activeDays,
      customTexts,
      hashtags,
      textUserPrompt,
      selectedLearnedFeedCaptions,
      selectedRatio,
      series_consistency_yn,
      series_anchor_slide_no,
      copywriter_preset_seq,
      selectedAIPreset,
      publish_type,
      is_enabled,
      accountConnected,
    } = this.state;

    const isActivePublishType = is_enabled === "Y" && publish_type === "PUBLISH";
    const activeIgSeq = this.getActiveIgSeq();

    if (isActivePublishType && !accountConnected) {
      this.needConnectAccount();
      return;
    }

    if (isActivePublishType && !activeIgSeq) {
      return this.openWarning("인스타그램 계정을 선택해주세요.");
    }

    if (!settingName || !settingName.trim()) {
      return this.openWarning("자동화 제목을 입력해주세요.");
    }

    const ai_studio_history_seq = this.buildAiStudioHistorySeqCsv();
    const requiredAiRuleCount = this.getAiImageRuleSlotCount(selectedFeedType, selectedSlideCount);
    const configuredAiRuleCount = this.getConfiguredAiImageRuleCount();

    if (!ai_studio_history_seq) {
      return this.openWarning("AI 스튜디오 규칙을 선택해주세요.");
    }

    if (configuredAiRuleCount < requiredAiRuleCount) {
      return this.openWarning(`AI 이미지 생성 규칙을 ${requiredAiRuleCount}개 모두 설정해주세요. 현재 ${configuredAiRuleCount}개 설정되었습니다.`);
    }

    if (textMode === "DIRECT" && (customTexts.length === 0 || customTexts.every((text) => !text.trim()))) {
      return this.openWarning("최소 1개의 문구를 입력해주세요.");
    }

    const isCopywriterNotSelected = copywriter_preset_seq === COPYWRITER_NOT_SELECTED;

    if (textMode === "AI" && isCopywriterNotSelected) {
      return this.openWarning("AI 카피라이터를 선택해주세요.");
    }

    const normalizedCopywriterPresetSeq = isCopywriterNotSelected
      ? null
      : normalizeCopywriterPresetSeq(copywriter_preset_seq);
    const selectedCopywriterPresetSeq = normalizeCopywriterPresetSeq(selectedAIPreset?.id);

    if (
      textMode === "AI" &&
      normalizedCopywriterPresetSeq &&
      (!selectedAIPreset || selectedCopywriterPresetSeq !== normalizedCopywriterPresetSeq)
    ) {
      return this.openWarning("저장된 AI 카피라이터를 불러올 수 없습니다. 다시 선택해주세요.");
    }

    if (textMode === "LEARN" && (!selectedLearnedFeedCaptions || selectedLearnedFeedCaptions.length === 0)) {
      return this.openWarning("학습할 피드를 최소 1개 이상 선택해주세요.");
    }

    if (hashtagMode === "BASIC" && (hashtags.length === 0 || hashtags.every((tag) => !tag.trim()))) {
      return this.openWarning("최소 1개의 해시태그를 입력해주세요.");
    }

    if (!startDate) return this.openWarning("날짜를 선택해주세요.");
    if (!scheduledTime) return this.openWarning("시간을 선택해주세요.");
    if (!activeDays || activeDays.size === 0) return this.openWarning("요일을 선택해주세요.");

    const activeWeekdays = Array.from(activeDays)
      .map((day) => DAY_TO_INDEX[day])
      .filter((value) => value !== undefined)
      .sort((a, b) => a - b);

    const cleanedCaptions = customTexts.map(v => v.trim()).filter(Boolean);
    const cleanedHashtags = hashtags.filter((v) => String(v || "").trim()).map((v) => String(v));

    const body = {
      settingSeq,
      instagramAccessTokenSeq: activeIgSeq || null,
      settingName,
      feedType: selectedFeedType,
      slideCount: selectedSlideCount,
      image_ratio: selectedRatio,
      series_consistency_yn:
        selectedFeedType === "SLIDE" && series_consistency_yn === "Y" ? "Y" : "N",
      series_anchor_slide_no: Number(series_anchor_slide_no) || 1,
      textMode,
      hashtagMode,
      publishCycle,
      timeMode,
      scheduledTime,
      startDate,
      activeWeekdays,
      captions: cleanedCaptions,
      hashtags: cleanedHashtags,
      textUserPrompt,
      selectedLearnedFeedCaptions,
      copywriter_preset_seq: normalizedCopywriterPresetSeq,
      ai_studio_history_seq,   // ⭐ 핵심
      publish_type,
      isEnabled: is_enabled
    };

    try {
      this.setState({ save_dialog_show_state: true, save_dialog_content: "저장 중입니다." });

      const response = await http.post("/rest/shop/instagram/auto/ai-image/setting/save", body, {
        headers: { "Content-Type": "application/json" },
      });

      const nextSeq = response?.data?.settingSeq;
      const savedSeq = nextSeq || settingSeq;
      this.setState({ settingSeq: savedSeq });

      if (nextSeq) {
        const url = new URL(window.location.href);
        url.searchParams.set("instagram_ai_image_auto_setting_seq", nextSeq); // ⭐ 수정
        window.history.replaceState({}, "", url);
      }

      if (shouldOpenSuccessDialog) {
        this.openBasic("저장이 완료되었습니다.");
      }
      return savedSeq;
    } catch (error) {
      const errMsg = error.response?.data?.message || error.message || "알 수 없는 오류";
      alert(`저장 실패: ${errMsg}`);
      return null;
    } finally {
      this.setState({ save_dialog_show_state: false });
    }
  };

  getDefaultBasicDialogButtons = () => ([
    { title: "확인", event_method: () => { this.setState({ basic_dialog_show_state: false }); } }
  ]);

  openBasic = (message, buttonList) => {
    this.setState({
      basic_dialog_show_state: true,
      basic_dialog_content: message,
      basic_dialog_button_list: buttonList || this.getDefaultBasicDialogButtons(),
    });
  };

  getDefaultRunNowResultDialogButtons = () => ([
    { title: "확인", event_method: () => { this.setState({ title_content_dialog_show_state: false }); } }
  ]);

  openRunNowResultDialog = (dialog) => {
    const safeDialog = dialog || {};
    const buttonList = Array.isArray(safeDialog.buttonList) ? safeDialog.buttonList.filter(Boolean) : [];

    // 즉시 실행 실패는 오류 성격에 맞게 WarningDialog로 표시한다.
    if (safeDialog.dialogType === "warning") {
      this.openWarning(safeDialog.message || "즉시 실행 중 오류가 발생했습니다.");
      return;
    }

    // TitleContentDialogV2는 2개 버튼을 전제로 사용하므로,
    // 단일 확인 메시지는 BasicDialogV2로 표시한다.
    if (buttonList.length < 2) {
      this.openBasic(safeDialog.message || "즉시 실행이 완료되었습니다.");
      return;
    }

    this.setState({
      title_content_dialog_show_state: true,
      title_content_dialog_title: safeDialog.title || "즉시 실행 결과",
      title_content_dialog_content: safeDialog.message || "",
      title_content_dialog_button_list: buttonList,
    });
  };

  openInstagramCreatePost = (mediaId) => {
    if (!mediaId) return;
    window.location.href = `/shop/instagram/InstagramCreatePost?media_id=${encodeURIComponent(mediaId)}`;
  };

  openPermalink = (permalink) => {
    if (!permalink) return;
    window.open(permalink, "_blank", "noopener,noreferrer");
  };

  buildRunNowResultDialog = (response, successTitle) => {
    const data = response?.data || {};
    const defaultButtons = this.getDefaultRunNowResultDialogButtons();

    if (String(data.code) !== "200") {
      return {
        dialogType: "warning",
        title: "즉시 실행 실패",
        message: `즉시 실행에 실패했습니다.<br/>${data.error || data.msg || "알 수 없는 오류"}`,
        buttonList: defaultButtons,
      };
    }

    const resultList = data.result?.resultList || [];
    if (!Array.isArray(resultList) || resultList.length === 0) {
      return {
        dialogType: "warning",
        title: "즉시 실행 결과",
        message: "즉시 실행 요청은 완료되었지만 처리된 설정이 없습니다.<br/>AI 호출 남은량이 부족하지 않은지 확인해주세요.",
        buttonList: defaultButtons,
      };
    }

    const successList = resultList.filter((item) => String(item.code) === "200");
    const firstResult = resultList[0] || {};
    const successResult = successList[0] || firstResult;

    if (successList.length > 0) {
      const mediaId = successResult.media_id || successResult.instagram_media_seq;
      const permalink = successResult.permalink;
      const status = String(successResult.status || "").toUpperCase();
      const isDraft = String(successResult.is_draft || "").toUpperCase() === "Y" || status === "READY";
      const isPublished = status === "PUBLISHED" || (!!permalink && !isDraft);
      const resultMessage = successResult.msg || successResult.message || "즉시 실행이 완료되었습니다.";

      if (isPublished) {
        const buttonList = permalink
          ? [
              { title: "인스타 게시물 보기", event_method: () => { this.openPermalink(permalink); } },
              { title: "닫기", event_method: () => { this.setState({ title_content_dialog_show_state: false }); } },
            ]
          : defaultButtons;

        return {
          title: "즉시 실행 완료",
          message: `${successTitle}<br/>인스타그램에 발행된 콘텐츠를 확인 해보시겠습니까?${permalink ? "" : "<br/>게시물 URL이 아직 조회되지 않았습니다."}`,
          buttonList,
        };
      }

      if (mediaId) {
        return {
          title: "즉시 실행 완료",
          message: `${successTitle}<br/>콘텐츠 등록 화면에서 내용을 확인할 수 있습니다.`,
          buttonList: [
            { title: "콘텐츠 확인", event_method: () => { this.openInstagramCreatePost(mediaId); } },
            { title: "닫기", event_method: () => { this.setState({ title_content_dialog_show_state: false }); } },
          ],
        };
      }

      return {
        title: "즉시 실행 완료",
        message: `${successTitle}<br/>${resultMessage}`,
        buttonList: defaultButtons,
      };
    }

    return {
      dialogType: "warning",
      title: "즉시 실행 실패",
      message: `${firstResult.msg || firstResult.message || firstResult.error || "상세 오류는 서버 로그를 확인해주세요."}`,
      buttonList: defaultButtons,
    };
  };

  runInstagramAiImageAutoSettingNow = async () => {
    const savedSeq = await this.saveInstagramAiImageAutoSetting({ silent: true });
    if (!savedSeq) return;

    try {
      this.setState({
        save_dialog_show_state: true,
        save_dialog_content: "저장된 설정으로 즉시 실행 중입니다.",
      });

      const response = await http.post(
        `/rest/shop/instagram/auto/ai-image/setting/runNow?instagram_ai_image_auto_setting_seq=${encodeURIComponent(savedSeq)}`,
        null,
        { headers: { Accept: "application/json" } }
      );

      const dialog = this.buildRunNowResultDialog(response, "AI 이미지 자동화 즉시 실행이 완료되었습니다.");
      this.openRunNowResultDialog(dialog);
    } catch (error) {
      const errorData = error.response?.data || {};
      const resultList = errorData.result?.resultList;
      const firstResult = Array.isArray(resultList) && resultList.length > 0 ? resultList[0] : {};
      const errMsg = firstResult.msg || firstResult.message || firstResult.error
        || errorData.msg || errorData.message || errorData.error
        || error.message || "알 수 없는 오류";
      this.openWarning(`즉시 실행에 실패했습니다.<br/>${errMsg}`);
    } finally {
      this.setState({ save_dialog_show_state: false });
    }
  };

  setRatio = (ratio) => this.setState({ selectedRatio: ratio });
  setFeedType = (feedType) => {
    this.setState((prev) => {
      const preservedRuleMap = this.preserveAiSlideRuleMap(prev.ai_slide_rule_map);
      const visibleRuleMap = this.getVisibleAiSlideRuleMap(preservedRuleMap, feedType, prev.selectedSlideCount);
      return {
        selectedFeedType: feedType,
        ai_slide_rule_map: preservedRuleMap,
        ...this.buildLegacyRuleStateFromSlideRuleMap(visibleRuleMap),
      };
    });
  };
  selectSlideCount = (count) => {
    this.setState((prev) => {
      const preservedRuleMap = this.preserveAiSlideRuleMap(prev.ai_slide_rule_map);
      const visibleRuleMap = this.getVisibleAiSlideRuleMap(preservedRuleMap, prev.selectedFeedType, count);
      return {
        selectedSlideCount: count,
        ai_slide_rule_map: preservedRuleMap,
        ...this.buildLegacyRuleStateFromSlideRuleMap(visibleRuleMap),
      };
    });
  };
  selectLogic = (logic) => this.setState({ selectedLogic: logic, selectedRoboMD: logic === "ROBOMD" ? this.state.selectedRoboMD : "" });
  selectRoboMD = (value) => this.setState({ selectedRoboMD: value });
  setTextMode = (mode) => this.setState({ textMode: mode });
  setHashtagMode = (mode) => this.setState({ hashtagMode: mode });
  setTimeMode = (mode) => this.setState({ timeMode: mode });

  addCustomText = () => this.setState((prev) => ({ customTexts: [...prev.customTexts, ""] }));
  removeCustomText = (index) => {
    this.setState((prev) => {
      if (prev.customTexts.length <= 1) {
        this.openWarning("최소 1개의 문구는 필요합니다.");
        return null;
      }
      return { customTexts: prev.customTexts.filter((_, idx) => idx !== index) };
    });
  };
  changeCustomText = (index, value) => {
    this.setState((prev) => {
      const customTexts = [...prev.customTexts];
      customTexts[index] = value;
      return { customTexts };
    });
  };

  addHashtag = () => this.setState((prev) => ({
    hashtags: [...prev.hashtags, ""],
    hashtagSetInputs: [...(prev.hashtagSetInputs || []), ""],
  }));

  removeHashtag = (index) => {
    this.setState((prev) => {
      if (prev.hashtags.length <= 1) {
        this.openWarning("최소 1개의 해시태그 세트는 필요합니다.");
        return null;
      }

      return {
        hashtags: prev.hashtags.filter((_, idx) => idx !== index),
        hashtagSetInputs: (prev.hashtagSetInputs || []).filter((_, idx) => idx !== index),
      };
    });
  };

  changeHashtag = (index, value) => {
    this.setState((prev) => {
      const hashtags = [...prev.hashtags];
      hashtags[index] = value;
      return { hashtags };
    });
  };

  changeTextUserPrompt = (value) => this.setState({ textUserPrompt: value });

  getHashtagValueInfo = (mode, setIndex = 0) => {
    const value = mode === "AI"
      ? this.state.textUserPrompt
      : this.state.hashtags[setIndex];

    return parseStoredHashtagText(value);
  };

  getHashtagTags = (mode, setIndex = 0) => {
    return this.getHashtagValueInfo(mode, setIndex).tags;
  };

  updateHashtagTags = (mode, setIndex, tags) => {
    const hashtagText = (tags || []).join(" ");

    if (mode === "AI") {
      this.changeTextUserPrompt(hashtagText);
      return;
    }

    this.changeHashtag(setIndex, hashtagText);
  };

  getHashtagInputValue = (mode, setIndex = 0) => {
    if (mode === "AI") return this.state.aiHashtagInput || "";
    return (this.state.hashtagSetInputs || [])[setIndex] || "";
  };

  changeHashtagInputValue = (mode, setIndex, value) => {
    if (mode === "AI") {
      this.setState({ aiHashtagInput: value });
      return;
    }

    this.setState((prev) => {
      const hashtagSetInputs = [...(prev.hashtagSetInputs || [])];
      hashtagSetInputs[setIndex] = value;
      return { hashtagSetInputs };
    });
  };

  addHashtagTags = (mode, setIndex, rawValue) => {
    const currentValueInfo = this.getHashtagValueInfo(mode, setIndex);

    if (currentValueInfo.type === "LEGACY_RAW") {
      return this.openWarning("태그별로 수정하려면 현재 값을 삭제한 후 해시태그를 다시 등록해주세요.");
    }

    const newTags = parseNewHashtagInput(rawValue);
    if (newTags.length === 0) return;

    const currentTags = currentValueInfo.tags;
    const combinedTags = [...new Set([...currentTags, ...newTags])];

    if (combinedTags.length > 30) {
      this.openWarning("해시태그는 최대 30개까지 등록하는 것을 권장합니다.");
    }

    this.updateHashtagTags(mode, setIndex, combinedTags);
    this.changeHashtagInputValue(mode, setIndex, "");
  };

  removeHashtagTag = (mode, setIndex, tagIndex) => {
    const nextTags = this.getHashtagTags(mode, setIndex)
      .filter((_, index) => index !== tagIndex);
    this.updateHashtagTags(mode, setIndex, nextTags);
  };

  reorderHashtagTags = (mode, setIndex, nextTags) => {
    this.updateHashtagTags(mode, setIndex, nextTags);
  };

  clearLegacyHashtagValue = (mode, setIndex = 0) => {
    if (mode === "AI") {
      this.setState({ textUserPrompt: "", aiHashtagInput: "" });
      return;
    }

    this.setState((prev) => {
      const hashtags = [...prev.hashtags];
      const hashtagSetInputs = [...(prev.hashtagSetInputs || [])];
      hashtags[setIndex] = "";
      hashtagSetInputs[setIndex] = "";
      return { hashtags, hashtagSetInputs };
    });
  };

  loadFavoriteHashtags = () => {
    http.get("/rest/shop/favorite-hashtags")
      .then((res) => {
        const favoriteTags = Array.isArray(res?.data?.list) ? res.data.list : [];
        this.setState({ favoriteTags });
      })
      .catch((error) => {
        console.error("즐겨찾는 태그 조회 실패:", error);
      });
  };

  openFavoriteTagModal = () => {
    this.setState({
      favoriteTagModalOpen: true,
      newFavoriteTagInput: "",
    });
  };

  closeFavoriteTagModal = () => {
    this.setState({
      favoriteTagModalOpen: false,
      newFavoriteTagInput: "",
    });
  };

  addNewFavoriteTag = async () => {
    const tagName = normalizeHashtagToken(this.state.newFavoriteTagInput);

    if (!tagName) {
      return this.openWarning("즐겨찾는 태그를 입력해주세요.");
    }

    if ((this.state.favoriteTags || []).some((tag) => tag.tag_name === tagName)) {
      return this.openWarning("이미 등록된 즐겨찾는 태그입니다.");
    }

    try {
      const response = await http.post("/rest/shop/favorite-hashtags", {
        tag_name: tagName,
      });

      const favoriteTagSeq = response?.data?.favorite_tag_seq || `temp_${Date.now()}`;

      this.setState((prev) => ({
        favoriteTags: [
          ...(prev.favoriteTags || []),
          { favorite_tag_seq: favoriteTagSeq, tag_name: tagName },
        ],
        favoriteTagModalOpen: false,
        newFavoriteTagInput: "",
      }));
    } catch (error) {
      console.error("즐겨찾는 태그 추가 실패:", error);
      this.openWarning("즐겨찾는 태그를 추가하지 못했습니다.");
    }
  };

  requestDeleteFavoriteTag = (index) => {
    const target = (this.state.favoriteTags || [])[index];
    if (!target) return;

    this.setState({
      confirm_dialog_show_state: true,
      confirm_dialog_content: `<b>${target.tag_name}</b> 태그를 삭제하시겠습니까?`,
      confirm_dialog_button_list: [
        {
          title: "삭제",
          type: "1",
          event_method: () => this.deleteFavoriteHashtag(index),
        },
        {
          title: "취소",
          type: "2",
          event_method: () => this.setState({ confirm_dialog_show_state: false }),
        },
      ],
    });
  };

  deleteFavoriteHashtag = async (index) => {
    const target = (this.state.favoriteTags || [])[index];
    if (!target) return;

    this.setState((prev) => ({
      confirm_dialog_show_state: false,
      favoriteTags: (prev.favoriteTags || []).filter((_, idx) => idx !== index),
    }));

    try {
      const response = await http.delete(`/rest/shop/favorite-hashtags/${target.favorite_tag_seq}`);
      if (response?.data?.status && response.data.status !== "success") {
        throw new Error(response.data.message || "삭제 처리 실패");
      }
    } catch (error) {
      console.error("즐겨찾는 태그 삭제 실패:", error);
      this.setState((prev) => {
        const favoriteTags = [...(prev.favoriteTags || [])];
        favoriteTags.splice(index, 0, target);
        return { favoriteTags };
      });
      this.openWarning("즐겨찾는 태그를 삭제하지 못했습니다.");
    }
  };

  toggleDay = (day) => {
    this.setState((prev) => {
      const next = new Set(prev.activeDays);
      if (next.has(day)) {
        if (next.size === 1) {
          this.openWarning("최소 1개의 요일을 선택해주세요.");
          return null;
        }
        next.delete(day);
      } else {
        if (prev.publishCycle === "THREETIMES" && next.size >= 3) {
          this.openWarning("주 3회 발행 시 최대 3개의 요일만 선택할 수 있습니다.");
          return null;
        }
        next.add(day);
      }
      return { activeDays: next };
    });
  };

  handlePublishCycleChange = (text, value) => {
    this.setState((prev) => {
      let nextDays = new Set(prev.activeDays);
      if (value === "DAILY" || value === "BIDAY") {
        nextDays = new Set(DAY_LABELS);
      } else if (value === "THREETIMES" && nextDays.size > 3) {
        nextDays = new Set(["월", "수", "금"]);
      }
      return { publishCycle: value, activeDays: nextDays };
    });
  };

  selectLearnedFeed = (instagram_media_id, caption) => {
    this.setState((prev) => {
      const copied = [...prev.selectedLearnedFeedCaptions];
      const index = copied.findIndex((item) => String(item.instagram_media_id) === String(instagram_media_id));
      if (index !== -1) {
        copied.splice(index, 1);
      } else {
        copied.push({ instagram_media_id, caption });
      }
      return {
        selectedLearnedFeedCaptions: copied,
        selectedLearnedFeedCaptionsCnt: copied.length,
      };
    });
  };

  getDateObjForDate = () => {
    const { startDate, scheduledTime } = this.state;
    if (!startDate) return null;
    const time = scheduledTime || "00:00";
    return new Date(`${startDate}T${time}:00`);
  };

  getDateObjForTime = () => {
    const { startDate, scheduledTime } = this.state;
    const baseDate = startDate || format(new Date(), "yyyy-MM-dd");
    const time = scheduledTime || "00:00";
    return new Date(`${baseDate}T${time}:00`);
  };

  onChangeDatePicker = (date) => {
    if (!date) {
      this.setState({ startDate: "" });
      return;
    }
    this.setState({ startDate: format(date, "yyyy-MM-dd") });
  };

  onChangeTimePicker = (date) => {
    if (!date) {
      this.setState({ scheduledTime: "" });
      return;
    }
    this.setState({ scheduledTime: format(date, "HH:mm") });
  };

  openAICopywriterModal = () => {
    this.setState({
      showAICopywriterModal: true,
      tempSelectedAIPreset: this.state.selectedAIPreset
        ? { ...this.state.selectedAIPreset }
        : null,
    });
  };

  closeAICopywriterModal = () => {
    this.setState({ showAICopywriterModal: false });
  };

  selectAIPreset = (preset) => {
    this.setState({ tempSelectedAIPreset: preset });
  };

  applyAICopywriter = () => {
    this.setState({
      selectedAIPreset: this.state.tempSelectedAIPreset
        ? { ...this.state.tempSelectedAIPreset }
        : null,
      showAICopywriterModal: false,
    });
  };

  selectCardGroup = (type) => {
    this.setState({
      duplicate_check_type: type
    });
  };

  onChangeCategory = (text, value, depth) => {
    const selectedText = text;
    const selectedValue = value;
    
    // 1. 상태를 한 번에 업데이트하기 위해 빈 객체를 생성합니다.
    const nextState = {};

    if (depth > 1 && depth <= 5) {
      if (value != "") {
        this.loadProductCategory(selectedValue, depth);
      }
      const name_for_text = `select_category_${depth - 1}_text`;
      const name = `select_category_${depth - 1}`;
      nextState[name_for_text] = selectedText;
      nextState[name] = selectedValue;
    }

    // 2. 하위 뎁스의 리스트와 선택된 값을 모두 초기화합니다.
    for (let idx = depth; idx <= 4; idx++) {
      // 하위 카테고리 리스트 빈 배열로 초기화
      nextState[`product_category_list_${idx}`] = [];
      
      // jQuery $("#...").val("") 대체: 
      // 하위 카테고리의 선택된 값을 state에서 빈 문자열로 초기화합니다.
      const name_for_text = `select_category_${idx}_text`;
      const name = `select_category_${idx}`;
      nextState[name_for_text] = "";
      nextState[name] = "";
      
      // (참고: 기존 코드에 name2와 name3가 동일한 요소를 두 번 비우는 중복 코드가 있어 하나로 합쳤습니다)
    }

    // 3. 모아둔 변경 사항을 단 한 번의 setState로 처리합니다.
    this.setState(nextState);
  };

  onClickAddCategory = () => {
    const {
      select_category_1,
      select_category_2,
      select_category_3,
      select_category_4,
      select_category_1_text,
      select_category_2_text,
      select_category_3_text,
      select_category_4_text,
      select_category_no_list = [],
      select_category_name_list = []
    } = this.state;

    const category_no =
      select_category_4 ||
      select_category_3 ||
      select_category_2 ||
      select_category_1;

    if (!category_no) {
      alert("카테고리를 선택해 주세요.");
      return;
    }

    if (select_category_no_list.includes(category_no)) {
      alert("이미 추가된 카테고리입니다.");
      return;
    }

    const categoryPathList = [
      select_category_1_text,
      select_category_2_text,
      select_category_3_text,
      select_category_4_text
    ].filter(Boolean);

    const newCategory = {
      category_no,
      category_1: select_category_1_text || "",
      category_2: select_category_2_text || "",
      category_3: select_category_3_text || "",
      category_4: select_category_4_text || "",
      path: categoryPathList.join(" ❯ ")
    };

    this.setState({
      select_category_no_list: [...select_category_no_list, category_no],
      select_category_name_list: [...select_category_name_list, newCategory]
    });
  };

  handleRemoveCategory = (index) => {
    this.setState((prevState) => ({
      select_category_no_list: prevState.select_category_no_list.filter((_, i) => i !== index),
      select_category_name_list: prevState.select_category_name_list.filter((_, i) => i !== index)
    }));
  };

  getPreviewData = () => {
    const { textMode, customTexts, feedCaptions, selectedLearnedFeedCaptions, hashtagMode, hashtags, selectedLogic } = this.state;

    let caption = "문구를 입력해주세요.";
    if (textMode === "AI") {
      caption = "입력한 프롬프트에 따른 맞춤 문구를 추천해 드립니다.";
    } else if (textMode === "LEARN") {
      caption = "선택한 피드 문구에 따른 맞춤 문구를 추천해 드립니다.";
    }

    if (textMode === "DIRECT" && customTexts[0] && customTexts[0].trim()) {
      caption = customTexts[0];
    }

    if (textMode === "LEARN" && selectedLearnedFeedCaptions.length > 0) {
      if (feedCaptions.length > 0) {
        const target = feedCaptions.find((fc) =>
          selectedLearnedFeedCaptions.some((sel) => String(sel.instagram_media_id) === String(fc.instagram_media_id))
        );
        caption = target?.caption?.trim() || selectedLearnedFeedCaptions[0]?.caption?.trim() || caption;
      } else {
        caption = selectedLearnedFeedCaptions[0]?.caption?.trim() || caption;
      }
    }

    const hashtagsText =
      hashtagMode === "AI"
        ? "#AI자동생성 #트렌드분석 #맞춤해시태그"
        : (hashtags[0] && hashtags[0].trim()) || "#해시태그를 입력해주세요.";

    const logicMap = {
      ORDERS: "주문 많은 상품 기준",
      NEW: "신상품 기준",
      VIEWS: "조회 높은 상품 기준",
      ROBOMD: "로보MD 진열 기준",
    };

    return {
      caption,
      hashtagsText,
      logicText: logicMap[selectedLogic] || "상품 기준",
    };
  };

  renderRoboMDBox = () => {
    const { selectedLogic, rp_event_setting_list, selectedRoboMD } = this.state;
    /* 
      { key: "ORDERS", title: "판매량 베스트", desc: "가장 많이 팔린 순서대로 가져옵니다." },
      { key: "NEW", title: "신상품 우선", desc: "최근 업데이트된 상품 순서대로 가져옵니다." },
      { key: "VIEWS", title: "조회 높은 순", desc: "클릭 및 뷰가 높은 순서대로 가져옵니다." },
      { key: "ROBOMD", title: "쇼핑몰 진열 연동", desc: "쇼핑몰 진열 규칙을 그대로 적용합니다." },
    */
    if (selectedLogic == "ORDERS" || selectedLogic == "NEW" || selectedLogic == "VIEWS") {
      return (
        <div id="advancedFilterContainer" className="advanced-filter-box">
          <div
            style={{
              fontSize: "16px",
              fontWeight: 800,
              color: "var(--deep-navy)",
              marginBottom: "20px",
              borderBottom: "2px solid #e5e7eb",
              paddingBottom: "12px",
            }}
          >
            <span style={{ marginRight: "4px" }}>
              <svg width="20" height="19" viewBox="0 0 20 19" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path opacity="0.5" d="M19.7724 7.39596C19.7502 7.29675 19.7069 7.20343 19.6453 7.12231C19.5837 7.04119 19.5053 6.97417 19.4154 6.92577L17.2914 5.75473C17.1775 5.52521 17.0493 5.30299 16.9076 5.08936L16.9522 2.6763C16.9531 2.57508 16.9329 2.47479 16.8929 2.38168C16.853 2.28857 16.7942 2.20464 16.7202 2.13513C15.6626 1.18194 14.4119 0.465009 13.0522 0.0325761C12.9546 0.00231062 12.8516 -0.00701284 12.75 0.00522155C12.6485 0.017456 12.5507 0.0509698 12.4632 0.103549L10.3838 1.34557C10.1249 1.33669 9.87506 1.33669 9.61624 1.34557L7.53682 0.103549C7.44928 0.0509698 7.35151 0.017456 7.24997 0.00522155C7.14843 -0.00701284 7.04544 0.00231062 6.9478 0.0325761C5.58632 0.465338 4.33507 1.1856 3.2798 2.144C3.20514 2.21112 3.1458 2.29335 3.10578 2.38517C3.06575 2.47698 3.04597 2.57625 3.04776 2.6763L3.09238 5.08936C2.94959 5.30228 2.82465 5.52407 2.6997 5.75473L0.575656 6.92577C0.487054 6.97467 0.410099 7.04199 0.350035 7.12313C0.289971 7.20427 0.24821 7.29733 0.227598 7.39596C-0.075866 8.78144 -0.075866 10.2156 0.227598 11.6011C0.249766 11.7003 0.29311 11.7936 0.354698 11.8747C0.416286 11.9558 0.494681 12.0229 0.584581 12.0713L2.70863 13.2423C2.82249 13.4718 2.95066 13.694 3.09238 13.9077L3.04776 16.3207C3.04695 16.422 3.06713 16.5222 3.10705 16.6154C3.14698 16.7085 3.20578 16.7924 3.2798 16.8619C4.3374 17.8151 5.58812 18.532 6.9478 18.9645C7.04544 18.9947 7.14843 19.0041 7.24997 18.9918C7.35151 18.9796 7.44928 18.9461 7.53682 18.8935L9.61624 17.6515H10.3838L12.4721 18.8935C12.5807 18.9645 12.7081 19.0015 12.838 19C12.9106 18.9968 12.9825 18.9849 13.0522 18.9645C14.4137 18.5317 15.6649 17.8114 16.7202 16.853C16.7949 16.7859 16.8542 16.7037 16.8942 16.6119C16.9342 16.5201 16.954 16.4208 16.9522 16.3207L16.9076 13.9077C17.0504 13.6948 17.1754 13.473 17.3003 13.2423L19.4243 12.0713C19.5129 12.0224 19.5899 11.9551 19.65 11.8739C19.71 11.7928 19.7518 11.6997 19.7724 11.6011C20.0759 10.2156 20.0759 8.78144 19.7724 7.39596ZM18.4337 10.9889L16.4078 12.1068C16.2793 12.181 16.1766 12.2924 16.1133 12.4261C15.9665 12.7285 15.7964 13.0192 15.6046 13.2955C15.5232 13.4195 15.4797 13.5644 15.4797 13.7125L15.5154 16.0102C14.747 16.6606 13.8674 17.1684 12.9183 17.5095L10.9282 16.3207C10.8171 16.2565 10.6908 16.2228 10.5622 16.2232H10.5087C10.1701 16.2498 9.82991 16.2498 9.4913 16.2232C9.34498 16.2152 9.19942 16.2491 9.07185 16.3207L7.08167 17.5095C6.13377 17.1701 5.25434 16.6654 4.48462 16.0191L4.52032 13.7125C4.52026 13.5644 4.47685 13.4195 4.39537 13.2955C4.20684 13.0171 4.03691 12.7267 3.88667 12.4261C3.82078 12.2942 3.71868 12.1835 3.59216 12.1068L1.56628 10.9889C1.38773 10.0033 1.38773 8.99379 1.56628 8.0081L3.59216 6.89028C3.72068 6.81603 3.82341 6.70463 3.88667 6.57091C4.0335 6.26853 4.20357 5.97787 4.39537 5.7015C4.47685 5.57751 4.52026 5.43263 4.52032 5.28453L4.48462 2.9868C5.25298 2.33645 6.13264 1.82862 7.08167 1.48751L9.07185 2.6763C9.19905 2.74892 9.34489 2.78285 9.4913 2.77389C9.82991 2.74727 10.1701 2.74727 10.5087 2.77389C10.655 2.78183 10.8006 2.74797 10.9282 2.6763L12.9183 1.48751C13.8662 1.82698 14.7457 2.33168 15.5154 2.97793L15.4797 5.28453C15.4797 5.43263 15.5232 5.57751 15.6046 5.7015C15.7932 5.97992 15.9631 6.27035 16.1133 6.57091C16.1792 6.70286 16.2813 6.81358 16.4078 6.89028L18.4337 8.0081C18.6123 8.99379 18.6123 10.0033 18.4337 10.9889ZM10 4.53045C9.01154 4.53045 8.04527 4.82182 7.22339 5.36772C6.40152 5.91362 5.76094 6.68953 5.38267 7.59732C5.00441 8.50512 4.90543 9.50403 5.09827 10.4677C5.29111 11.4315 5.7671 12.3167 6.46605 13.0115C7.165 13.7063 8.05552 14.1794 9.02499 14.3711C9.99446 14.5628 10.9993 14.4644 11.9126 14.0884C12.8258 13.7124 13.6063 13.0756 14.1555 12.2586C14.7046 11.4416 14.9978 10.4811 14.9978 9.49852C14.9978 8.18091 14.4712 6.91726 13.5339 5.98557C12.5967 5.05387 11.3255 4.53045 10 4.53045ZM10 13.0471C9.29395 13.0471 8.60377 12.839 8.01671 12.4491C7.42966 12.0592 6.9721 11.5049 6.70191 10.8565C6.43172 10.2081 6.36102 9.49458 6.49877 8.80622C6.63651 8.11785 6.9765 7.48555 7.47575 6.98927C7.975 6.49298 8.61108 6.15501 9.30356 6.01809C9.99604 5.88116 10.7138 5.95144 11.3661 6.22002C12.0184 6.48861 12.5759 6.94345 12.9682 7.52701C13.3605 8.11058 13.5698 8.79667 13.5698 9.49852C13.5698 10.4397 13.1937 11.3423 12.5243 12.0078C11.8548 12.6733 10.9468 13.0471 10 13.0471Z" fill="#0a1a2f"></path>
              </svg>
            </span>
            상세 추출 필터
            <span
              style={{
                fontSize: "13px",
                fontWeight: 500,
                color: "var(--text-sub)",
                marginLeft: "8px",
              }}
            >
              (입력하지 않거나 무관 체크 시 전체 대상으로 작동합니다)
            </span>
          </div>

          {/* ====== 카테고리 한정 영역 (4뎁스 연동 및 Chip UI 적용) ====== */}
          <div style={{ marginBottom: "20px" }}>
            <label className="form-label" style={{ display: "block", marginBottom: "8px" }}>카테고리 한정</label>
            
            <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", alignItems: "center" }}>
              <div id="category_filter" style={{ display: "grid", gap: "8px", flex: 1, gridTemplateColumns: "repeat(4, 1fr)" }}>
                <div>
                  {/* 1뎁스 (항상 노출) */}
                  <SelectBoxV2
                    id="select_category_1"
                    className="background-white"
                    selectedValue={this.state.select_category_1 || ""}
                    onClickOption={(text, value) => this.onChangeCategory(text, value, 2)}
                    optionTexttName = "label"
                    optionValuetName = "value"
                    list={[
                      { value: "", label: "카테고리 선택" },
                      // product_category_list_1 데이터를 { value, label } 형태로 변환하여 배열에 추가
                      ...(this.state.product_category_list_1 || []).map((obj) => ({
                        value: obj.category_no,
                        label: obj.category_name_no_format,
                      })),
                    ]}
                    width="100%"
                    height="44px"
                  />
                </div>

                {/* 2뎁스 (데이터가 있을 때만 렌더링) */}
                {this.state.product_category_list_2 && this.state.product_category_list_2.length > 0 && (
                  <div>
                    <SelectBoxV2
                      id="select_category_2"
                      className="background-white"
                      selectedValue={this.state.select_category_2 || ""}
                      onClickOption={(text, value) => this.onChangeCategory(text, value, 3)}
                      optionTexttName = "label"
                      optionValuetName = "value"
                      list={[
                        { value: "", label: "하위 카테고리 선택" },
                        ...this.state.product_category_list_2.map((obj) => ({
                          value: obj.category_no,
                          label: obj.category_name_no_format,
                        })),
                      ]}
                      width="100%"
                      height="44px"
                    />
                  </div>
                )}

                {/* 3뎁스 (데이터가 있을 때만 렌더링) */}
                {this.state.product_category_list_3 && this.state.product_category_list_3.length > 0 && (
                  <div>
                    <SelectBoxV2
                      id="select_category_3"
                      className="background-white"
                      selectedValue={this.state.select_category_3 || ""}
                      onClickOption={(text, value) => this.onChangeCategory(text, value, 4)}
                      optionTexttName = "label"
                      optionValuetName = "value"
                      list={[
                        { value: "", label: "하위 카테고리 선택" },
                        ...this.state.product_category_list_3.map((obj) => ({
                          value: obj.category_no,
                          label: obj.category_name_no_format,
                        })),
                      ]}
                      width="100%"
                      height="44px"
                      />
                  </div>
                )}

                {/* 4뎁스 (데이터가 있을 때만 렌더링) */}
                {this.state.product_category_list_4 && this.state.product_category_list_4.length > 0 && (
                  <div>
                    <SelectBoxV2
                      id="select_category_4"
                      className="background-white"
                      selectedValue={this.state.select_category_4 || ""}
                      onClickOption={(text, value) => this.onChangeCategory(text, value, 5)}
                      optionTexttName = "label"
                      optionValuetName = "value"
                      list={[
                        { value: "", label: "하위 카테고리 선택" },
                        ...this.state.product_category_list_4.map((obj) => ({
                          value: obj.category_no,
                          label: obj.category_name_no_format,
                        })),
                      ]}
                      width="100%"
                      height="44px"
                    />
                  </div>
                )}

              </div>
              <button
                id="category_filter_button"
                type="button"
                className="btn btn-outline btn-sm"
                style={{ height: "44px", minWidth: "120px", width: "96px" }}
                onClick={this.onClickAddCategory}
              >
                선택 카테고리 추가
              </button>
            </div>

            {/* 추가된 카테고리 태그(Chip) 렌더링 영역 */}
            {this.state.select_category_name_list && this.state.select_category_name_list.length > 0 && (
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "8px",
                  marginTop: "12px",
                  padding: "12px",
                  backgroundColor: "#f8f9fa",
                  border: "1px dashed #ced4da",
                  borderRadius: "6px"
                }}
              >
                {this.state.select_category_name_list.map((item, index) => (
                  <div
                    key={index}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                      padding: "6px 12px",
                      backgroundColor: "#ffffff",
                      border: "1px solid #4dabf7",
                      borderRadius: "20px",
                      fontSize: "13px",
                      color: "#1864ab",
                      fontWeight: 500,
                      boxShadow: "0 1px 2px rgba(0,0,0,0.05)"
                    }}
                  >
                    {/* item.path 형식 예시: '패션의류 ❯ 남성의류 ❯ 상의' */}
                    {item.path}
                    <button
                      type="button"
                      onClick={() => this.handleRemoveCategory(index)}
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: "18px",
                        height: "18px",
                        borderRadius: "50%",
                        backgroundColor: "#e7f5ff",
                        color: "#1864ab",
                        border: "none",
                        fontSize: "10px",
                        cursor: "pointer",
                        padding: 0,
                        marginLeft: "4px"
                      }}
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
          {/* ========================================================== */}

          <div style={{ borderTop: "1px dashed #e5e7eb", margin: "24px 0" }}></div>

          {/* 기간 설정 영역 (기존 카테고리와 같은 줄에 있던 것을 분리) */}
          <div className="filter-row" style={{ display: "flex", gap: "24px" }}>
            <div className="filter-col" style={{ flex: 1 }}>
              <label className="form-label">
                상품 등록날짜{" "}
                {/* <span
                  className="help-icon"
                  title="쇼핑몰에 상품이 등록된 날짜를 기준으로 합니다."
                >
                  ?
                </span> */}
                <div className="tooltip-wrap">
                  <span className="tooltip-icon">!</span>
                  <div className="tooltip-content">
                    쇼핑몰에 상품이 등록된 날짜를 기준으로 합니다.
                  </div>
                </div>
              </label>
              <SelectBoxV2
                id="event_item_created_period"
                className="background-white"
                selectedValue={this.state.event_item_created_period}
                onClickOption={(text, value) => this.setState({ event_item_created_period: value })}
                optionTexttName = "title"
                optionValuetName = "value"
                list={this.state.event_item_created_period_list}
                width="100%"
                height="44px"
              />
            </div>

            <div className="filter-col" style={{ flex: 1 }}>
              <label className="form-label">
                상품분석 기간{" "}
                {/* <span
                  className="help-icon"
                  title="조회수 및 구매수 데이터를 집계할 기준 기간입니다."
                >
                  ?
                </span> */}
                <div className="tooltip-wrap">
                  <span className="tooltip-icon">!</span>
                  <div className="tooltip-content">
                    조회수 및 구매수 데이터를 집계할 기준 기간입니다.
                  </div>
                </div>
              </label>
              <SelectBoxV2
                id="event_item_analysis_period"
                className="background-white"
                selectedValue={this.state.event_item_analysis_period}
                onClickOption={(text, value) => this.setState({ event_item_analysis_period: value })}
                optionTexttName = "title"
                optionValuetName = "value"
                list={this.state.event_item_analysis_period_list}
                width="100%"
                height="44px"
              />
            </div>
            
            {/* 빈 공간을 채우기 위한 더미 flex div (옵션) */}
            {/* <div style={{ flex: 1 }}></div> */}
          </div>

          <div style={{ borderTop: "1px dashed #e5e7eb", margin: "24px 0" }}></div>

          <div>
            {/* 상품 가격 */}
            <div className="range-grid filter-range-grid">
              <div className="range-label filter-range-label">상품 가격</div>

              <div className="range-inputs">
                <TextInput
                  type="number"
                  className="background-white"
                  disabled={this.state.is_any_item_price}
                  value={this.state.event_item_min_price ?? ""}
                  onChange={(e) => this.setState({ event_item_min_price: e.target.value })}
                  height={42}
                />
                <span className="unit">원 ~</span>

                <TextInput
                  type="number"
                  className="background-white"
                  disabled={this.state.is_any_item_price}
                  value={this.state.event_item_max_price ?? ""}
                  onChange={(e) => this.setState({ event_item_max_price: e.target.value })}
                  height={42}
                />
                <span className="unit">원</span>
              </div>

              <CheckBox
                className="filter-checkbox"
                checked={this.state.is_any_item_price}
                onChangeCheck={(e) => {
                  if (this.state.is_any_item_price) {
                    this.setState({ is_any_item_price: false })
                  } else {
                    this.setState({ is_any_item_price: true, event_item_min_price: null, event_item_max_price: null })
                  }
                }}
              >
                가격 무관
              </CheckBox>
            </div>

            {/* 조회수 */}
            <div className="range-grid filter-range-grid">
              <div className="range-label filter-range-label">조회수</div>

              <div className="range-inputs">
                <TextInput
                  type="number"
                  className="background-white"
                  disabled={this.state.is_any_item_hit}
                  value={this.state.event_item_min_hit ?? ""}
                  onChange={(e) => this.setState({ event_item_min_hit: e.target.value })}
                  height={42}
                />
                <span className="unit">개 ~</span>

                <TextInput
                  type="number"
                  className="background-white"
                  disabled={this.state.is_any_item_hit}
                  value={this.state.event_item_max_hit ?? ""}
                  onChange={(e) => this.setState({ event_item_max_hit: e.target.value })}
                  height={42}
                />
                <span className="unit">개</span>
              </div>

              <CheckBox
                className="filter-checkbox"
                checked={this.state.is_any_item_hit}
                onChangeCheck={(e) => {
                  if (this.state.is_any_item_hit) {
                    this.setState({ is_any_item_hit: false })
                  } else {
                    this.setState({ is_any_item_hit: true, event_item_min_hit: null, event_item_max_hit: null })
                  }
                }}
              >
                조회수 무관
              </CheckBox>
            </div>

            {/* 구매수 */}
            <div className="range-grid filter-range-grid">
              <div className="range-labe filter-range-label">구매수</div>

              <div className="range-inputs">
                <TextInput
                  type="number"
                  className="background-white"
                  disabled={this.state.is_any_item_order_cnt}
                  value={this.state.event_item_min_order_cnt ?? ""}
                  onChange={(e) => this.setState({ event_item_min_order_cnt: e.target.value })}
                  height={42}
                />
                <span className="unit">개 ~</span>

                <TextInput
                  type="number"
                  className="background-white"
                  disabled={this.state.is_any_item_order_cnt}
                  value={this.state.event_item_max_order_cnt ?? ""}
                  onChange={(e) => this.setState({ event_item_max_order_cnt: e.target.value })}
                  height={42}
                />
                <span className="unit">개</span>
              </div>

              <CheckBox
                className="filter-checkbox"
                checked={this.state.is_any_item_order_cnt}
                onChangeCheck={(e) => {
                  if (this.state.is_any_item_order_cnt) {
                    this.setState({ is_any_item_order_cnt: false })
                  } else {
                    this.setState({ is_any_item_order_cnt: true, event_item_min_order_cnt: null, event_item_max_order_cnt: null })
                  }
                }}
              >
                구매수 무관
              </CheckBox>
            </div>
          </div>
        </div>
      )
    } else if (selectedLogic == "ROBOMD") {
      return (
        <div className="card advanced-filter-box">
          <div style={{ maxHeight: 320, overflow: "auto", display: "flex", flexDirection: "column", gap: 8 }}>
            {this.state.rp_added_type != "Y" && (
              <div id="state-disconnected">
                <div className="state-desc">
                  쇼핑몰에 설정해둔 복합 상품 진열 규칙을 피드 순서로 연동할 수 있습니다.
                  <br />
                  현재 연동된 진열 설정이 없습니다.
                </div>

                <button
                  className="btn btn-outline btn-sm"
                  style={{
                    borderColor: "var(--primary-mint)",
                    color: "var(--primary-mint-hover)"
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    this.goRpService();
                  }}
                >
                  진열 서비스 이용하러 가기
                </button>
              </div>
            )}

            {this.state.rp_is_finish == 1 && (
              <div id="state-locked">
                <div
                  className="state-desc"
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: "8px"
                  }}
                >
                  <span style={{ fontSize: "16px" }}>🔒</span>

                  <div>
                    매출과 재고 데이터를 복합 분석한 쇼핑몰 자체 진열 자동화 규칙을
                    인스타그램에도 연동하려면 프리미엄 요금제 구독이 필요합니다.
                  </div>
                </div>

                <div style={{ marginTop: "12px" }}>
                  <button
                    className="btn btn-outline btn-sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      this.props.history.push(
                          {
                              pathname: `/shop/qna/form`,
                              state: {qna_type_cd: "2104"}
                          }	
                      )
                    }}
                  >
                    기간연장 문의하기
                  </button>
                </div>
              </div>
            )}
            {
              this.state.rp_added_type == "Y" && this.state.rp_is_finish != 1
              && (
                <>
                 <span style={{ fontSize: 15, fontWeight: 700, color: "#0A1A2F", marginBottom: "12px" }}>
                  쇼핑몰에 설정해둔 로보MD 진열 목록 중 하나를 선택해주세요.
                </span>
                {
                  rp_event_setting_list.length > 0 ? (
                    rp_event_setting_list.map((obj) => {
                      const value = `${obj.iframe_page_id}|${obj.iframe_html_id}`;
                      return (
                        <button
                          key={value}
                          type="button"
                          className={`feed-item-button ${selectedRoboMD === value ? "active" : ""}`}
                          onClick={() => this.selectRoboMD(value)}
                        >
                          {obj.display_name}
                        </button>
                      );
                    })
                  ) : (
                    <div style={{ fontSize: 14, color: "#6b7280" }}>
                      로보MD 진열 목록이 없습니다. 먼저 로보MD 진열 설정을 생성해주세요.
                    </div>
                  )
                }
                </>
              )
            }
          </div>
        </div>
        // <div className="robomd-state-container" id="robomdContainer">
        //   <div id="state-connected">
        //     <div className="state-desc">
        //       쇼핑몰에 설정해둔 정교한 자체 상품 진열 규칙(판매량, 재고, 계절 등 복합 조합)을 인스타그램 피드에도 그대로 연동합니다.
        //     </div>

        //     <div className="robomd-state-box">
        //       <div>
        //         <span style={{ fontSize: "14px", marginRight: "4px" }}>현재 연동 중인 쇼핑몰 진열 기준</span>
        //         <strong style={{ color: "var(--primary-mint-hover)", fontSize: "15px" }}>
        //           25 S/S 시즌 주력 상품 믹스 진열
        //         </strong>
        //       </div>

        //       <button
        //         className="btn btn-outline btn-sm"
        //         onClick={(e) => {
        //           e.stopPropagation();
        //           // alert("진열 설정 변경 팝업이 뜹니다.");
        //         }}
        //       >
        //         다른 진열 불러오기
        //       </button>
        //     </div>
        //   </div>
        // </div>
      );
    }

    return null;
  };

  renderDuplicateProductSetting = () => {
    const { duplicate_check_type } = this.state;
    return (
      <div
        style={{
          marginTop: "12px",
          paddingTop: "24px",
          borderTop: "1px dashed var(--border-color)"
        }}
      >
        <label
          className="form-label"
          style={{ fontSize: "15px", marginBottom: "16px" }}
        >
          상품을 중복체크하여 피드를 생성하겠습니까?
        </label>

        <div
          className="selection-grid grid-cols-3"
          style={{ marginBottom: "16px" }}
        >
          <div
            className={`selection-card ${
              duplicate_check_type === "MONTH_1_EXCLUDE" ? "active" : ""
            }`}
            onClick={() => this.selectCardGroup("MONTH_1_EXCLUDE")}
            style={{ padding: "16px", minHeight: "auto" }}
          >
            <div
              className="selection-title"
              style={{ fontSize: "14px", marginBottom: 0 }}
            >
              한 달 이내 발행된 상품 제외
            </div>
          </div>

          <div
            className={`selection-card ${
              duplicate_check_type === "CURRENT_FEED_EXCLUDE" ? "active" : ""
            }`}
            onClick={() => this.selectCardGroup("CURRENT_FEED_EXCLUDE")}
            style={{ padding: "16px", minHeight: "auto" }}
          >
            <div
              className="selection-title"
              style={{ fontSize: "14px", marginBottom: 0 }}
            >
              이전에 발행된 상품 모두 제외
            </div>
          </div>

          <div
            className={`selection-card ${
              duplicate_check_type === "ALLOW_ALL" ? "active" : ""
            }`}
            onClick={() => this.selectCardGroup("ALLOW_ALL")}
            style={{ padding: "16px", minHeight: "auto" }}
          >
            <div
              className="selection-title"
              style={{ fontSize: "14px", marginBottom: 0 }}
            >
              중복 체크 없이 모두 허용
            </div>
          </div>
        </div>

        <div className="filter-warning">
          <span>⚠️</span>{" "}
          설정하신 추출 조건 및 중복 체크 기준에 맞는 상품이 없을 경우,
          피드가 발행되지 않을 수 있습니다.
        </div>
      </div>
    );
  }

  renderCaptionArea = () => {
    const {
      textMode,
      selectedAIPreset,
      customTexts,
      textUserPrompt,
      feedCaptions,
      selectedLearnedFeedCaptions,
    } = this.state;

    if (textMode === "AI") {
      return (
        <div>
          <div className="applied-ai-box" style={{ marginBottom: 16 }}>
            <div className="applied-ai-header" style={{ alignItems: "center" }}>
              {
                selectedAIPreset && selectedAIPreset.title
                ? <div className="applied-ai-title">{selectedAIPreset.title}</div>
                : <div style={{ fontSize: 14, lineHeight: 1.2 }}>저장된 AI 카피라이터를 불러올 수 없습니다. 다시 선택해 주세요.</div>
              }
              <button className="btn btn-outline btn-sm" type="button" onClick={this.openAICopywriterModal}>
                설정 변경
              </button>
            </div>
            {
                selectedAIPreset && selectedAIPreset.voice
                ?
                  <div className="applied-ai-tags">
                    <span className="applied-tag">보이스: {selectedAIPreset.voice}</span>
                    <span className="applied-tag">학습: {selectedAIPreset.weight || selectedAIPreset.learning || "-"}</span>
                  </div>
                : null
            }
          </div>
          {/* <textarea
            className="form-control"
            style={{ minHeight: 120, resize: "vertical" }}
            placeholder="AI 문구 생성 프롬프트를 입력하세요. 예: 친근한 톤, 이모티콘 포함"
            value={textUserPrompt}
            onChange={(e) => this.changeTextUserPrompt(e.target.value)}
          /> */}
        </div>
      );
    }

    if (textMode === "LEARN") {
      return (
        // <div className="inline-box">
        //   <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12, gap: 12 }}>
        //     <div style={{ fontSize: 14, fontWeight: 700 }}>
        //       최근 피드 문구
        //       <span style={{ fontSize: 12, color: "#6b7280", marginLeft: 8 }}>
        //         (선택한 개수: {selectedLearnedFeedCaptions.length}개)
        //       </span>
        //     </div>
        //     <button className="btn btn-outline btn-sm" type="button" onClick={() => {
        //       const accessSeq = this.state.selected_ig_access_seq || this.state.default_ig_access_seq;
        //       if (accessSeq) this.fetchAllInstagramMedia(true, accessSeq);
        //     }}>
        //       최근 피드 새로고침
        //     </button>
        //   </div>

        //   <div style={{ maxHeight: 280, overflowY: "auto", display: "flex", flexDirection: "column", gap: 8 }}>
        //     {feedCaptions.length > 0 ? (
        //       feedCaptions.map((obj) => {
        //         const isSelected = selectedLearnedFeedCaptions.some(
        //           (item) => String(item.instagram_media_id) === String(obj.instagram_media_id)
        //         );
        //         return (
        //           <button
        //             key={obj.instagram_media_id}
        //             type="button"
        //             className={`feed-item-button ${isSelected ? "active" : ""}`}
        //             onClick={() => this.selectLearnedFeed(obj.instagram_media_id, obj.caption)}
        //           >
        //             {obj.caption}
        //           </button>
        //         );
        //       })
        //     ) : (
        //       <div style={{ fontSize: 14, color: "#6b7280" }}>불러온 피드 문구가 없습니다.</div>
        //     )}
        //   </div>

        //   <p className="helper-text">선택한 문구를 AI가 학습하여, 계정 톤에 맞는 새 문장을 추천합니다.</p>
        // </div>
        <div className="inline-box">
          <div className="ref-inner">
            {this.state.accountConnected ? (
              <div>
                {/* <div style={{ marginBottom: 16 }}>
                  <label className="form-label" style={{ marginBottom: 8, display: "block" }}>
                    인스타그램 계정 선택
                  </label>
                  <SelectBoxV2
                    id="ig_account_select"
                    tabIndex={0}
                    width="100%"
                    height="40px"
                    padding="8px 36px 8px 12px"
                    backgroundColor="#fff"
                    pageWrapperName="common-page-wrapper"
                    list={this.state.account_list || []}
                    optionTexttName="title"
                    optionValuetName="value"
                    selectedValue={this.state.selected_ig_access_seq || this.state.default_ig_access_seq || ""}
                    onClickOption={this.onChangeAccount}
                  />
                </div> */}

                <div style={{ marginBottom: 8 }}>
                  <span style={{ fontSize: 15, fontWeight: 700, color: "#0A1A2F" }}>
                    학습 기준이 될 최근 게시물을 하나 선택해주세요.
                  </span>
                  {/* <span style={{ fontSize: 12, color: "#6b7280", marginLeft: 8 }}>
                    (선택한 개수: {this.state.selectedLearnedFeedCaptions?.length || 0}개)
                  </span> */}
                </div>

                <div
                  className="robomd-list"
                  style={{
                    marginTop: 0,
                    maxHeight: 240,
                    overflowY: "auto",
                    // border: "1px solid #e5e7eb",
                    // borderRadius: 6,
                    // padding: 12,
                    padding: "12px 0px 0px 0px",
                    marginBottom: "16px",
                    backgroundColor: "#f9fafb",
                  }}
                >
                  {this.state.feedCaptions && this.state.feedCaptions.length > 0 ? (
                    this.state.feedCaptions.map((obj) => (
                      <button
                        key={obj.instagram_media_id}
                        type="button"
                        className={`robomd-item ${
                          this.state.selectedLearnedFeedCaptions.some(
                            (item) => item.instagram_media_id == obj.instagram_media_id
                          )
                            ? "active"
                            : ""
                        }`}
                        // onClick={() => this.selectLearnedFeed(obj.instagram_media_id, obj.caption)}
                        onClick={() => this.selectLearnedFeedOnlyOne(obj.instagram_media_id, obj.caption)}
                      >
                        <div style={{fontSize: "14px",
                          fontWeight: 600,
                          // marginBottom: '8px'
                        }}>{obj.caption}</div>
                        {/* <span style={{
                          fontSize: '12px',          
                          fontWeight: 600,          
                          background: '#f1f5f9',
                          color: '#475569',
                          padding: '4px 8px',
                          borderRadius: '6px',       
                        }}>
                          {this.formatDate(obj.timestamp)}
                        </span> */}
                      </button>
                    ))
                  ) : (
                    <div style={{ fontSize: 14, color: "#6b7280" }}>
                      불러온 피드 문구가 없습니다.
                    </div>
                  )}
                </div>

                <p style={{ fontSize: 12, color: "#6b7280", marginTop: 8, lineHeight: "1.6", marginBottom: 0 }}>
                  {/* 선택한 피드 문구를 기반으로 AI가 말투와 감성을 참고합니다. */}
                  선택한 피드 문구를 분석하여 내 피드와 어울리는 문구를 작성합니다.
                </p>
              </div>
            ) : (
              <div
                style={{
                  textAlign: 'center',
                  padding: '40px 20px',
                  background: 'white',
                  borderRadius: '8px',
                  border: '1px dashed #d1d5db'
                }}
              >
                <div style={{ fontSize: '32px', marginBottom: '12px' }}><i className="fa fa-brands fa-instagram"></i></div>
                <p style={{ fontSize: '16px', fontWeight: '600', color: '#0A1A2F', marginBottom: '8px' }}>
                  인스타그램 계정이 연동되지 않았습니다.
                </p>
                <p style={{ fontSize: '14px', color: '#6b7280', marginBottom: '20px', lineHeight: '1.6' }}>
                  계정을 연동하면 반응이 좋았던 내 게시글을 AI가 학습합니다.<br />
                  <strong>자주 쓰는 단어, 이모지 패턴, 특유의 감성</strong>까지<br />
                  분석하여 내 피드와 어울리는 문구를 작성합니다.
                </p>
                <button
                  className="btn btn-primary"
                  style={{ padding: '12px 24px', fontSize: '14px' }}
                  onClick={this.handleConnectAccount}
                >
                  인스타그램 계정 연동하기
                </button>
              </div>
            )}
          </div>
        </div>
      );
    }

    return (
      <div className="inline-box">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <span style={{ fontSize: 14, fontWeight: 700 }}>고정 문구 리스트</span>
          <button className="btn btn-outline btn-sm" type="button" onClick={this.addCustomText}>+ 추가</button>
        </div>

        {customTexts.map((item, index) => (
          <div className="text-input-group" key={index}>
            <textarea
              className="form-control"
              style={{ minHeight: 100, resize: "vertical" }}
              placeholder="매번 동일하게 발행될 텍스트를 입력하세요."
              value={item}
              onChange={(e) => this.changeCustomText(index, e.target.value)}
            />
            <button className="btn btn-outline" style={{ width: 42, padding: 0, color: "#ef4444", borderColor: "#fecaca" }} type="button" onClick={() => this.removeCustomText(index)}>
              ✕
            </button>
          </div>
        ))}

        <div className="rotation-hint"><span><i className="fa fa-lightbulb-o"></i></span> 여러 개 등록 시 발행 순서에 맞춰 교대로 반복 적용됩니다.</div>
      </div>
    );
  };

  renderHashtagEditor = (mode, setIndex = 0) => {
    const valueInfo = this.getHashtagValueInfo(mode, setIndex);
    const tags = valueInfo.tags;

    return (
      <InstagramHashtagEditor
        tags={tags}
        inputValue={this.getHashtagInputValue(mode, setIndex)}
        onInputChange={(value) => this.changeHashtagInputValue(mode, setIndex, value)}
        onAddInput={(value) => this.addHashtagTags(mode, setIndex, value)}
        onRemoveTag={(tagIndex) => this.removeHashtagTag(mode, setIndex, tagIndex)}
        onReorderTags={(nextTags) => this.reorderHashtagTags(mode, setIndex, nextTags)}
        favoriteTags={this.state.favoriteTags}
        onAddFavoriteTag={(tagName) => this.addHashtagTags(mode, setIndex, tagName)}
        onDeleteFavoriteTag={this.requestDeleteFavoriteTag}
        onOpenFavoriteTagModal={this.openFavoriteTagModal}
        legacyRawValue={valueInfo.type === "LEGACY_RAW" ? valueInfo.rawValue : ""}
        onClearLegacyRawValue={() => this.clearLegacyHashtagValue(mode, setIndex)}
      />
    );
  };

  renderHashtagArea = () => {
    const { hashtagMode, hashtags } = this.state;

    if (hashtagMode === "AI") {
      return (
        <div className="inline-box">
          <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 12 }}>
            필수 포함 고정 해시태그 (선택)
          </div>

          <div className="hashtag-set-card-body">
            {this.renderHashtagEditor("AI", 0)}
          </div>

          <div className="hashtag-ai-guide">
            <span><i className="fa fa-lightbulb-o"></i></span>
            <div>
              고정 태그 외 빈 자리는 자동화 실행 시 <strong>완성된 피드 문구의 문맥을 분석</strong>하여 AI가 생성합니다.
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="inline-box">
        <div className="hashtag-set-list-header">
          <span style={{ fontSize: 14, fontWeight: 700 }}>고정 해시태그 세트</span>
          <button className="btn btn-outline btn-sm" type="button" onClick={this.addHashtag}>
            + 추가
          </button>
        </div>

        <div className="hashtag-set-list">
          {hashtags.map((item, index) => (
            <div className="hashtag-set-card" key={`hashtag-set-${index}`}>
              <div className="hashtag-set-card-header">
                <strong>해시태그 세트 {index + 1}</strong>
                <button
                  type="button"
                  className="hashtag-set-delete-button"
                  onClick={() => this.removeHashtag(index)}
                  title="해시태그 세트 삭제"
                >
                  ×
                </button>
              </div>
              {this.renderHashtagEditor("BASIC", index)}
            </div>
          ))}
        </div>

        <div className="rotation-hint">
          <span><i className="fa fa-lightbulb-o"></i></span>
          여러 개 등록 시 상품 발행 순서에 맞춰 교대로 반복 적용됩니다.
        </div>
      </div>
    );
  };

  onChangepublishType(value) {
    if (value === "STOP") {
      this.setState({ is_enabled: "N" });
      return;
    }

    this.setState({
      publish_type: value,
      is_enabled: "Y"
    });
  }


  getRequestedCopywriterPresetSeq = () => {
    if (this.state.settingSeq) {
      return null;
    }

    const requestedPresetSeq = normalizeCopywriterPresetSeq(
      new URLSearchParams(window.location.search).get("copywriter_preset_seq")
    );

    if (!requestedPresetSeq || requestedPresetSeq === COPYWRITER_NOT_SELECTED) {
      return null;
    }

    return requestedPresetSeq;
  };

  buildCopywriterPresetList = (rawList = []) => {
    const savedPresetList = rawList.map((item) => {
      const voiceParts = [];

      if (
        item.use_basic_voice == 1 ||
        item.use_basic_voice === true ||
        item.use_basic_voice === "Y"
      ) {
        voiceParts.push("AI 브랜드 보이스");
      }
      if (
        item.use_myfeed_voice == 1 ||
        item.use_myfeed_voice === true ||
        item.use_myfeed_voice === "Y"
      ) {
        voiceParts.push("내 기존 피드");
      }
      if (
        item.use_custom_voice == 1 ||
        item.use_custom_voice === true ||
        item.use_custom_voice === "Y"
      ) {
        voiceParts.push("커스텀 뱅크");
      }

      let learning = "-";
      if (item.learning_data_type === "multi") {
        learning = "멀티모달 매핑 (사진 + 텍스트)";
      } else if (item.learning_data_type === "text") {
        learning = "텍스트 학습";
      } else if (item.learning_data_type === "image") {
        learning = "이미지 학습";
      }

      return {
        id: String(item.preset_seq),
        title: item.preset_title || "제목 없는 AI 카피라이터",
        description:
          item.preset_description ||
          item.description ||
          item.memo ||
          item.reference_summary ||
          "저장된 브랜드 톤과 작성 규칙을 적용합니다.",
        voice: voiceParts.length > 0 ? voiceParts.join(" + ") : "선택 없음",
        learning,
        weight: learning,
        defaultYn: item.default_yn || "N",
      };
    });

    const hasSavedDefault = savedPresetList.some(
      (preset) => preset.defaultYn === "Y"
    );

    return [
      {
        ...createBasicAIPreset(),
        defaultYn: hasSavedDefault ? "N" : "Y",
      },
      ...savedPresetList,
    ];
  };

  loadRequestedCopywriterPreset = async () => {
    const requestedPresetSeq = this.getRequestedCopywriterPresetSeq();

    if (!requestedPresetSeq) {
      return;
    }

    try {
      const response = await http.get(
        "/rest/shop/instagram/copywriter/preset/modal/list"
      );

      if (response?.data?.status !== "success") {
        throw new Error("AI 카피라이터 목록을 불러오지 못했습니다.");
      }

      const copywriterList = this.buildCopywriterPresetList(
        response.data.list || []
      );

      const selectedPreset = copywriterList.find(
        (preset) => String(preset.id) === String(requestedPresetSeq)
      );

      if (!selectedPreset) {
        this.setState({
          copywriter_list: copywriterList,
          copywriter_preset_seq: COPYWRITER_NOT_SELECTED,
          selectedAIPreset: null,
        });

        this.openWarning(
          "저장한 AI 카피라이터를 불러올 수 없습니다. 다시 선택해주세요."
        );
        return;
      }

      this.setState({
        copywriter_list: copywriterList,
        copywriter_preset_seq: requestedPresetSeq,
        selectedAIPreset: selectedPreset,
        textMode: "AI",
      });
    } catch (error) {
      console.error("AI 카피라이터 자동 선택 실패:", error);

      this.openWarning(
        error?.message ||
          "AI 카피라이터를 불러오는 중 오류가 발생했습니다."
      );
    }
  };

  asyncSelectedAIPreset = () => {
    const { copywriter_preset_seq, copywriter_list = [], selectedAIPreset } = this.state;

    if (copywriter_list.length === 0) {
      return;
    }

    const normalizedPresetSeq = normalizeCopywriterPresetSeq(copywriter_preset_seq) || "";
    const found = copywriter_list.find(
      (item) => String(item.id || "") === normalizedPresetSeq
    );

    if (!found) {
      const nextSelectedPreset = normalizedPresetSeq ? null : createBasicAIPreset();
      if (selectedAIPreset !== nextSelectedPreset) {
        this.setState({ selectedAIPreset: nextSelectedPreset });
      }
      return;
    }

    const normalizedFound = {
      ...found,
      learning: found.learning || found.weight || "",
      weight: found.weight || found.learning || "",
    };

    const currentLearning = selectedAIPreset?.weight || selectedAIPreset?.learning || "";
    const nextLearning = normalizedFound.weight || normalizedFound.learning || "";

    if (
      String(selectedAIPreset?.id || "") === String(normalizedFound.id || "") &&
      selectedAIPreset?.title === normalizedFound.title &&
      selectedAIPreset?.voice === normalizedFound.voice &&
      currentLearning === nextLearning
    ) {
      return;
    }

    this.setState({
      selectedAIPreset: normalizedFound
    });
  };

  getRpStatus() {
    http.get("/rest/shop/instagram/rp/status")
      .then(res => {
        const data = res.data;
        this.setState({
          rp_added_type: data.added_type,
          rp_is_finish: data.is_finish,
          rp_payment_yn: data.payment_yn,
        });

        /*
        연동이 되어 있고 (added_type == "Y")
        기간이 만료되지 않은 경우 (is_finish != 1)
        → 이때만 진열 목록(loadRpEventSettingList) 호출
        */
        if (data.added_type == "Y" && data.is_finish != 1) {
          this.loadRpEventSettingList();
        }

      }).catch(error => {
        console.log(error);
      })
  }

  goRpService() {
    http.get("/rest/common/changeServiceType?service_type=01002")
      .then(res => {
        const data = res.data;
        window.location.href = data.redirectUrl;
      }).catch(error => {
        console.log(error);
      })
  }

  selectLearnedFeedOnlyOne = (id, caption) => {
    const current = this.state.selectedLearnedFeedCaptions || [];
    const alreadySelected = current.some(obj => obj.instagram_media_id == id);

    let next = [];

    if (!alreadySelected) {
      next = [{
        instagram_media_id: id,
        caption
      }];
    }

    this.setState({
      selectedLearnedFeedCaptions: next,
      selectedLearnedFeedCaptionsCnt: next.length,
    });
  };

  loadSeriesContextForAnchorHistory = async (anchorHistorySeq) => {
    const normalizedHistorySeq = String(anchorHistorySeq || "").trim();

    if (!normalizedHistorySeq) {
      this.setState({
        series_context_json: "",
        series_anchor_image_url: "",
        series_anchor_final_prompt_text: "",
        series_context_mode: "",
        series_context_history_seq: "",
      });
      return null;
    }

    try {
      const res = await http.get('/rest/shop/instagram/auto/ai-image/series-context', {
        params: {
          ai_studio_history_seq: normalizedHistorySeq,
        },
      });

      const data = res && res.data ? res.data : {};
      if (String(data.code) !== "200") {
        this.setState({
          series_context_json: "",
          series_anchor_image_url: "",
          series_anchor_final_prompt_text: "",
          series_context_mode: "",
          series_context_history_seq: "",
        });
        return null;
      }

      const nextContext = {
        series_context_json: data.series_context_json || "",
        series_anchor_image_url: data.series_anchor_image_url || "",
        series_anchor_final_prompt_text: data.series_anchor_final_prompt_text || "",
        series_context_mode: data.series_context_mode || "",
        series_context_history_seq: normalizedHistorySeq,
      };

      this.setState(nextContext);
      return nextContext;
    } catch (e) {
      console.error("슬라이드 분석 결과 조회 실패:", e);
      this.setState({
        series_context_json: "",
        series_anchor_image_url: "",
        series_anchor_final_prompt_text: "",
        series_context_mode: "",
        series_context_history_seq: "",
      });
      return null;
    }
  };

  getAiStudioAutomationLocalUsageList = () => {
    const count = this.getAiImageRuleSlotCount(
      this.state.selectedFeedType,
      this.state.selectedSlideCount
    );

    return Array.from({ length: count }, (_, index) => {
      const slideNo = index + 1;
      return {
        slide_no: slideNo,
        ai_studio_history_seq: this.getAiStudioHistorySeqForSlide(slideNo) || null,
      };
    });
  };

  openAiImageStudioForSlide = (slideNo = 1) => {
    const normalizedSlideNo = Number(slideNo || 1);
    const currentHistorySeq = this.getAiStudioHistorySeqForSlide(normalizedSlideNo);

    this.setState({
      current_ai_rule_slide_no: normalizedSlideNo,
      ai_studio_popup_edit_context: {
        settingSeq: this.state.settingSeq || null,
        slideNo: normalizedSlideNo,
        historySeq: currentHistorySeq || null,
        localUsageList: this.getAiStudioAutomationLocalUsageList(),
      },
      show_ai_image_studio_popup: true,
    });
  };

  ensureSeriesContextReadyForGenerate = async (slideNo = 1) => {
    const currentSlideNo = Number(slideNo || 1);
    const anchorSlideNo = Number(this.state.series_anchor_slide_no || 1);
    const shouldUseSeriesContext =
      this.state.selectedFeedType === "SLIDE" &&
      this.state.series_consistency_yn === "Y" &&
      currentSlideNo !== anchorSlideNo;

    if (!shouldUseSeriesContext) {
      return {
        series_context_json: String(this.state.series_context_json || "").trim(),
        series_anchor_image_url: String(this.state.series_anchor_image_url || "").trim(),
        series_anchor_final_prompt_text: String(this.state.series_anchor_final_prompt_text || "").trim(),
        series_context_mode: String(this.state.series_context_mode || "").trim(),
        series_context_history_seq: String(this.state.series_context_history_seq || "").trim(),
      };
    }

    const anchorHistorySeq = String(this.getAiStudioHistorySeqForSlide(anchorSlideNo) || "").trim();
    if (!anchorHistorySeq) {
      throw new Error("슬라이드 일관성을 반영하려면 먼저 1번 슬라이드의 자동화 규칙을 적용해주세요.");
    }

    const hasCurrentContext =
      !!String(this.state.series_context_json || "").trim() &&
      String(this.state.series_context_history_seq || "") === anchorHistorySeq;

    if (hasCurrentContext) {
      return {
        series_context_json: String(this.state.series_context_json || "").trim(),
        series_anchor_image_url: String(this.state.series_anchor_image_url || "").trim(),
        series_anchor_final_prompt_text: String(this.state.series_anchor_final_prompt_text || "").trim(),
        series_context_mode: String(this.state.series_context_mode || "").trim(),
        series_context_history_seq: String(this.state.series_context_history_seq || "").trim(),
      };
    }

    // 설정 화면의 state만 비어 있는 경우에는 DB에 저장된 기존 분석 결과를 먼저 재사용한다.
    const loadedContext = await this.loadSeriesContextForAnchorHistory(anchorHistorySeq);
    if (loadedContext && String(loadedContext.series_context_json || "").trim()) {
      return loadedContext;
    }

    // DB에도 분석 결과가 없을 때만 2번 이후 슬라이드의 실제 생성 직전에 분석한다.
    try {
      const res = await http.post(
        '/rest/shop/instagram/auto/ai-image/series-context/analyze',
        {
          ai_studio_history_seq: anchorHistorySeq,
        },
        {
          headers: {
            'content-type': 'application/json;',
          },
        }
      );

      const data = res && res.data ? res.data : {};
      if (String(data.code) !== "200") {
        throw new Error(data.error || data.message || data.msg || "슬라이드 기준 분석에 실패했습니다.");
      }

      const analyzedContext = {
        series_context_json: data.series_context_json || "",
        series_anchor_image_url: data.series_anchor_image_url || "",
        series_anchor_final_prompt_text: data.series_anchor_final_prompt_text || "",
        series_context_mode: data.series_context_mode || "",
        series_context_history_seq: String(anchorHistorySeq),
      };

      await new Promise((resolve) => {
        this.setState(analyzedContext, resolve);
      });

      return analyzedContext;
    } catch (e) {
      console.error("슬라이드 기준 지연 분석 실패:", e);
      throw new Error(
        e && e.response && e.response.data && (e.response.data.message || e.response.data.error)
          ? (e.response.data.message || e.response.data.error)
          : (e && e.message ? e.message : "슬라이드 기준 분석에 실패했습니다.")
      );
    }
  };

  handleApplyAiImageRule = async (ruleData) => {
    const slideNo = Number(this.state.current_ai_rule_slide_no || 1);
    const anchorSlideNo = Number(this.state.series_anchor_slide_no || 1);
    let nextRuleState = this.buildAiImageRuleStateFromAppliedRule(ruleData);

    // 팝업에서 처음 적용하는 경우 applyPayload에는 Starter 샘플 메타가 없을 수 있다.
    // 저장 직후의 historyDetail을 다시 조회해서 샘플 여부/샘플명을 현재 규칙 상태에 보완한다.
    const appliedHistorySeq =
      ruleData.ai_studio_history_seq ||
      ruleData.aiStudioHistorySeq ||
      nextRuleState.ai_studio_history_seq ||
      "";

    if (appliedHistorySeq) {
      try {
        const historyDetail = await this.loadAiStudioHistoryDetail(appliedHistorySeq);
        const historyRuleState = this.buildAiImageRuleStateFromHistoryDetail(historyDetail);

        nextRuleState = {
          ...nextRuleState,
          starter_sample_yn: historyRuleState.starter_sample_yn || "N",
          starter_sample_title: historyRuleState.starter_sample_title || "",
          ...(nextRuleState.ai_image_rule_prompt_list.length === 0 ? {
            ai_image_rule_prompt_list: historyRuleState.ai_image_rule_prompt_list,
            ai_image_rule_thumb: historyRuleState.ai_image_rule_thumb || nextRuleState.ai_image_rule_thumb,
            ai_studio_history_title: historyRuleState.ai_studio_history_title || nextRuleState.ai_studio_history_title,
          } : {}),
        };
      } catch (e) {
        console.error("AI 스튜디오 적용 직후 Starter 샘플 정보 복원 실패:", e);
      }
    }

    const shouldAnalyzeSeriesContext =
      this.state.selectedFeedType === "SLIDE" &&
      this.state.series_consistency_yn === "Y" &&
      slideNo === anchorSlideNo;

    let analyzedContext = null;

    if (shouldAnalyzeSeriesContext) {
      const historySeq =
        ruleData.ai_studio_history_seq ||
        ruleData.aiStudioHistorySeq ||
        nextRuleState.ai_studio_history_seq ||
        "";

      if (!historySeq) {
        throw new Error("1번 워크플로우 히스토리 정보를 찾을 수 없습니다.");
      }

      try {
        const res = await http.post(
          '/rest/shop/instagram/auto/ai-image/series-context/analyze',
          {
            // 적용 직전 saveAllSteps가 STEP/RESULT를 새로 저장하므로,
            // 저장 전에 잡힌 step/result 식별값은 전달하지 않는다.
            // 백엔드가 history 기준으로 최신 선택 단계와 결과를 다시 조회한다.
            ai_studio_history_seq: historySeq,
          },
          {
            headers: {
              'content-type': 'application/json;',
            },
          }
        );

        const data = res && res.data ? res.data : {};
        if (String(data.code) !== "200") {
          throw new Error(data.error || data.message || data.msg || "슬라이드 기준 분석에 실패했습니다.");
        }

        analyzedContext = {
          series_context_json: data.series_context_json || "",
          series_anchor_image_url: data.series_anchor_image_url || "",
          series_anchor_final_prompt_text: data.series_anchor_final_prompt_text || "",
          series_context_mode: data.series_context_mode || "",
          series_context_history_seq: String(historySeq),
        };
      } catch (e) {
        console.error("슬라이드 기준 분석 실패:", e);
        throw new Error(
          e && e.response && e.response.data && (e.response.data.message || e.response.data.error)
            ? (e.response.data.message || e.response.data.error)
            : (e && e.message ? e.message : "슬라이드 기준 분석에 실패했습니다.")
        );
      }
    }

    await new Promise((resolve) => {
      this.setState((prev) => {
        const nextMap = {
          ...(prev.ai_slide_rule_map || {}),
          [slideNo]: nextRuleState,
        };

        const preservedRuleMap = this.preserveAiSlideRuleMap(nextMap);
        const visibleRuleMap = this.getVisibleAiSlideRuleMap(
          preservedRuleMap,
          prev.selectedFeedType,
          prev.selectedSlideCount
        );

        return {
          ai_slide_rule_map: preservedRuleMap,
          ...this.buildLegacyRuleStateFromSlideRuleMap(visibleRuleMap),
          ...(analyzedContext || {}),
        };
      }, resolve);
    });
  };

  handleResetAiImageRule = (slideNo = 1) => {
    const isAnchorSlide = Number(slideNo) === Number(this.state.series_anchor_slide_no || 1);

    this.setState((prev) => {
      const nextMap = { ...(prev.ai_slide_rule_map || {}) };
      delete nextMap[slideNo];

      const preservedRuleMap = this.preserveAiSlideRuleMap(nextMap);
      const visibleRuleMap = this.getVisibleAiSlideRuleMap(
        preservedRuleMap,
        prev.selectedFeedType,
        prev.selectedSlideCount
      );

      return {
        ai_slide_rule_map: preservedRuleMap,
        ...this.buildLegacyRuleStateFromSlideRuleMap(visibleRuleMap),
        ...(isAnchorSlide ? {
          series_context_json: "",
          series_anchor_image_url: "",
          series_anchor_final_prompt_text: "",
          series_context_mode: "",
          series_context_history_seq: "",
        } : {}),
      };
    });
  };

  handleEditAiImageRule = (slideNo = 1) => {
    if (!this.getAiStudioHistorySeqForSlide(slideNo)) {
      alert("수정할 자동화 규칙 히스토리 정보가 없습니다.");
      return;
    }

    this.openAiImageStudioForSlide(slideNo);
  };

  renderAiImageRuleCard = (slideNo = 1, title = "싱글 피드 자동화 설정", isSingle = false) => {
    const rule = this.getAiImageRuleForSlide(slideNo);
    const isConfigured = !!(rule && rule.ai_image_rule_applied_yn === "Y" && rule.ai_studio_history_seq);
    const promptList = isConfigured && Array.isArray(rule.ai_image_rule_prompt_list)
      ? rule.ai_image_rule_prompt_list
      : [];
    const thumb = isConfigured && rule.ai_image_rule_thumb
      ? String(rule.ai_image_rule_thumb).replace(/^url\(['"]?/, "").replace(/['"]?\)$/, "")
      : "";
    const isStarterSample = isConfigured && String(rule.starter_sample_yn || "N").toUpperCase() === "Y";
    const starterSampleTitle = isStarterSample ? String(rule.starter_sample_title || "").trim() : "";

    return (
      <div
        key={`slide_rule_${slideNo}`}
        className={`slide-rule-card ${isConfigured ? "configured" : ""}`}
      >
        <div className="slide-header">
          <span>
            {title}
            {this.state.selectedFeedType === "SLIDE" &&
              this.state.series_consistency_yn === "Y" &&
              Number(this.state.series_anchor_slide_no || 1) === Number(slideNo) && (
                <span className="slide-anchor-label">슬라이드 기준</span>
              )}
          </span>
          <span className={`slide-badge ${isConfigured ? "success" : ""}`}>
            {isConfigured ? "적용됨" : "미설정"}
          </span>
        </div>

        {isConfigured ? (
          <div className="slide-filled">
            <div className="slide-configured-body">
              <div
                className="slide-configured-thumb"
                style={thumb ? { backgroundImage: `url(${thumb})` } : undefined}
              >
                {!thumb && "텍스트 기반"}
                {/* <div className="rule-count-badge">{promptList.length || 1}단계</div> */}
              </div>

              <div className="slide-configured-prompt-list">
                <span class="rule-prompt-title">단계별 실행 프롬프트</span>
                {promptList.length ? (
                  promptList.map((item, index) => (
                    <div className="slide-configured-desc" key={`slide_${slideNo}_prompt_${index}`}>
                      <strong>
                        {isStarterSample && Number(item.stepNumber) === 1 && starterSampleTitle
                          ? `추천: ${starterSampleTitle}`
                          : `${item.stepNumber || index + 1}단계`}
                      </strong>
                      <div>{item.prompt || "-"}</div>
                    </div>
                  ))
                ) : isStarterSample ? (
                  <div className="slide-configured-desc">
                    <strong>{starterSampleTitle ? `추천: ${starterSampleTitle}` : "Starter 추천"}</strong>
                    <div>저장된 추천 이미지 생성 규칙이 적용되었습니다.</div>
                  </div>
                ) : (
                  <div className="slide-configured-desc">
                    저장된 AI 이미지 생성 규칙이 적용되었습니다.
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className="slide-empty">
            <svg style={{ width: isSingle ? "40px" : "32px", color: "#cbd5e1" }} viewBox="0 0 24 24" fill="none">
              <rect x="3" y="3" width="18" height="18" rx="4" stroke="currentColor" strokeWidth="2" />
              <circle cx="8.5" cy="8.5" r="1.5" fill="currentColor" />
              <path d="M21 15L16 10L5 21" stroke="currentColor" strokeWidth="2" />
            </svg>
            <p>{isSingle ? "이 피드에 적용할 AI 이미지 생성 규칙이 없습니다." : "규칙 없음"}</p>
          </div>
        )}

        <div className="slide-action-area">
          <button
            type="button"
            className={`btn ${isConfigured ? "btn-outline" : "btn-primary"}`}
            style={isConfigured ? { borderColor: "var(--primary-mint)", color: "var(--deep-navy)" } : undefined}
            onClick={() => this.openAiImageStudioForSlide(slideNo)}
          >
            {isConfigured ? "설정 수정" : "AI 스튜디오 열기"}
          </button>
          {isConfigured && (
            <button
              type="button"
              className="btn btn-outline"
              onClick={() => this.handleResetAiImageRule(slideNo)}
            >
              삭제
            </button>
          )}
        </div>
      </div>
    );
  };

  renderSeriesConsistencySetting = () => {
    const {
      selectedFeedType,
      series_consistency_yn,
      series_anchor_slide_no,
    } = this.state;

    if (selectedFeedType !== "SLIDE") return null;

    const isEnabled = series_consistency_yn === "Y";
    const anchorSlideNo = Number(series_anchor_slide_no) || 1;

    return (
      <div className="series-consistency-box" data-instagram-tutorial="ai-setting-series-consistency">
        <div className="series-consistency-row">
          <div>
            <div className="series-consistency-title">
              <i className="fa fa-link" aria-hidden="true"></i>
              <span>슬라이드 일관성 유지</span>
            </div>
            <p className="series-consistency-desc">
              1번 슬라이드에서 생성된 주제·그림체·캐릭터·색감을 나머지 슬라이드에 일관되게 반영합니다.
            </p>
          </div>
          <button
            type="button"
            className={`series-consistency-switch ${isEnabled ? "active" : ""}`}
            onClick={() => this.setState({ series_consistency_yn: isEnabled ? "N" : "Y" })}
            aria-pressed={isEnabled}
          >
            {isEnabled ? "ON" : "OFF"}
          </button>
        </div>
        {isEnabled && (
          <div className="series-consistency-note">
            슬라이드 일관성을 활성화하면 1번 슬라이드를 기준으로 주제·그림체·캐릭터·색감을 일관되게 반영합니다.<br/>
            각 슬라이드의 ‘단계별 실행 프롬프트’는 유지되므로, 슬라이드별 구성과 내용은 개별 설정에 따라 생성됩니다.
          </div>
        )}
      </div>
    );
  };

  renderAiImageRuleCards = () => {
    const { selectedFeedType, selectedSlideCount } = this.state;
    const count = this.getAiImageRuleSlotCount(selectedFeedType, selectedSlideCount);

    if (selectedFeedType !== "SLIDE") {
      return (
        <div className="slide-rules-container" style={{ gridTemplateColumns: "1fr" }}>
          {this.renderAiImageRuleCard(1, "싱글 피드 자동화 설정", true)}
        </div>
      );
    }

    return (
      <div className="slide-rules-container">
        {Array.from({ length: count }, (_, index) => {
          const slideNo = index + 1;
          return this.renderAiImageRuleCard(slideNo, `슬라이드 ${slideNo}`);
        })}
      </div>
    );
  };

  renderAiImageRulePromptList = () => {
    const { ai_image_rule_prompt_list = [] } = this.state;

    if (!ai_image_rule_prompt_list.length) return null;

    return (
      <ul className="rule-prompt-list">
        {ai_image_rule_prompt_list.map((item, index) => (
          <li key={`ai_rule_${index}`}>
            <div style={{ marginBottom: "6px" }}>
              {/* <span className={`rule-badge ${item.modeText && item.modeText.indexOf("이미지") > -1 ? "img" : "txt"}`}>
                {item.modeText && item.modeText.indexOf("이미지") > -1 ? "이미지 기반" : "텍스트 기반"}
              </span> */}
              <strong style={{ color: "var(--primary-mint-hover)" }}>
                {item.stepNumber}단계
              </strong>
            </div>
            <div>{item.prompt || "-"}</div>
          </li>
        ))}
      </ul>
    );
  };

  buildAiImageRuleStateFromHistoryDetail = (historyDetail) => {
    const historyInfo = historyDetail?.history_info || {};
    const stepList = historyDetail?.step_list || [];
    const isStarterSample = String(historyInfo.starter_sample_yn || "N").toUpperCase() === "Y";
    let starterSampleTitle = "";

    if (isStarterSample && historyInfo.starter_meta_snapshot_json) {
      try {
        const starterMeta = typeof historyInfo.starter_meta_snapshot_json === "string"
          ? JSON.parse(historyInfo.starter_meta_snapshot_json)
          : historyInfo.starter_meta_snapshot_json;
        starterSampleTitle = starterMeta && starterMeta.title ? String(starterMeta.title).trim() : "";
      } catch (e) {
        console.warn("Starter 샘플 메타데이터 파싱 실패:", e);
      }
    }

    if (isStarterSample && !starterSampleTitle) {
      starterSampleTitle = String(historyInfo.history_title || "").trim();
    }

    if (!Array.isArray(stepList) || stepList.length === 0) {
      return {
        ai_image_rule_applied_yn: "N",
        ai_image_rule_prompt_list: [],
        ai_image_rule_thumb: "",
        ai_studio_history_seq: historyInfo.ai_studio_history_seq || "",
        ai_studio_history_title: historyInfo.history_title || "",
        starter_sample_yn: isStarterSample ? "Y" : "N",
        starter_sample_title: starterSampleTitle,
      };
    }

    const selectedStep =
      stepList.find((step) => step.is_final_selected === "Y") ||
      stepList.find((step) => step.ai_studio_history_step_seq === historyInfo.selected_final_step_seq) ||
      stepList[stepList.length - 1];

    const thumbStep =
      stepList.find((step) => step.source_image_url && String(step.source_image_url).trim()) ||
      selectedStep;

    const promptList = stepList.map((step) => ({
      stepNumber: step.step_no,
      prompt: step.prompt_text || "",
      modeText:
        step.source_image_url && String(step.source_image_url).trim()
          ? "(이미지 기반 변형)"
          : "(텍스트로만 생성)",
    }));

    // const thumb =
    //   (thumbStep && (
    //     thumbStep.source_image_url 
    //     || thumbStep.generated_thumbnail_url 
    //     || thumbStep.generated_image_url
    //   )) || "";
    
      const thumb =
      (stepList[0] && (
        stepList[0].source_image_url 
      )) || "";

    return {
      ai_image_rule_applied_yn: "Y",
      ai_image_rule_prompt_list: promptList,
      ai_image_rule_thumb: thumb,
      ai_studio_history_seq: historyInfo.ai_studio_history_seq || "",
      ai_studio_history_title: historyInfo.history_title || "",
      starter_sample_yn: isStarterSample ? "Y" : "N",
      starter_sample_title: starterSampleTitle,
    };
  };

  loadAiStudioHistoryDetail = async (aiStudioHistorySeq) => {
    if (!aiStudioHistorySeq) {
      return null;
    }

    const response = await http.get('/rest/shop/aiStudio/historyDetail', {
      params: { ai_studio_history_seq: aiStudioHistorySeq }
    });

    if (response?.data?.result !== "success") {
      throw new Error(response?.data?.message || "AI 스튜디오 히스토리 상세 조회 실패");
    }

    return response.data;
  };

  updateSelectedAccountsUseYn = () => {
    const { token_id, selected_ig_access_seq } = this.state;

    if (!token_id) {
      this.openWarning("토큰 정보가 없습니다. 다시 연동해주세요.");
      return;
    }

    if (!this.isValidIgSeq(selected_ig_access_seq)) {
      this.openWarning("계정을 하나 선택해주세요.");
      return;
    }

    const selected_account_list = [String(selected_ig_access_seq)];

    this.setState({
      save_dialog_show_state: true,
      save_dialog_content: "계정 연결 중입니다.",
    });

    http.post(
      "/rest/shop/instagram/updateAccountsUseYn",
      {
        token_id,
        selected_account_list,
        use_yn: "Y",
      },
      {
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
        },
      }
    )
    .then((res) => {
      const data = res && res.data ? res.data : {};

      if (String(data.code) === "200") {
        this.setState(
          {
            show_account_connect_popup: false,
            selected_account_list,
            token_id: null,
          },
          () => {
            this.loadInstagramAccountList();
          }
        );
      } else {
        this.openWarning("계정 업데이트에 실패했습니다.");
      }
    })
    .catch((err) => {
      console.error("updateSelectedAccountsUseYn ERROR:", err);
      this.openWarning("서버 요청 중 오류가 발생했습니다.");
    })
    .finally(() => {
      this.setState({
        save_dialog_show_state: false,
      });
    });
  };

  render() {
    const {
      settingName,
      accountConnected,
      account_list,
      selected_ig_access_seq,
      default_ig_access_seq,
      loadedSettingWithoutIgAccount,
      selectedRatio,
      selectedFeedType,
      selectedSlideCount,
      selectedLogic,
      textMode,
      hashtagMode,
      publishCycle,
      activeDays,
      scheduledTime,
      startDate,
      showAICopywriterModal,
      tempSelectedAIPreset,
      account_list_v2,
      selectedAccount,
      ai_image_rule_thumb,
    } = this.state;

    const aiPresetOptions = [
      { title: "A 스타일 (여름 이벤트 피드용)", voice: "AI 자동 맞춤", weight: "멀티모달 매핑 (사진+텍스트)" },
      { title: "시크/모던 매거진 톤 (기본)", voice: "커스텀 뱅크 보이스", weight: "텍스트 단일 매핑" },
      { title: "게릴라 세일 안내용 (강조)", voice: "AI 자동 맞춤 (세일 강조)", weight: "멀티모달 매핑 (사진+텍스트)" },
    ];

    const { caption, hashtagsText, logicText } = this.getPreviewData();

    const activeIgSeq = this.getActiveIgSeq();
    const accountSelectValue = activeIgSeq || (accountConnected ? "" : "add");

    let displayAccountList = [];
    let selectedAccountText = null;

    if (!accountConnected || !account_list_v2 || account_list_v2.length === 0) {
      displayAccountList = [
        { title: "+ 인스타그램 연동하기", value: "add" }
      ];

      selectedAccountText = "계정 연동하기";
    } else {
      displayAccountList = (account_list_v2 || []).map((acc) => {
        return {
          ...acc,
          title: acc.title.startsWith("@") ? acc.title : "@" + acc.title,
        };
      });

      if (loadedSettingWithoutIgAccount && !activeIgSeq) {
        displayAccountList = [
          { title: "계정 선택", value: "none" },
          ...displayAccountList,
        ];
      }
    }

    return (
      <>
      <div
          id="page-wrapper"
          className="container InstagramCreateAiImageAutomation_v2"
          style={{ backgroundColor: "#fafaf" }}
      >
        <style>{styles}</style>

        <style>
          {`
            .common_popup_frame_v2_content:has(.instagram-no-accounts) {
              max-width: 720px !important;
              width: min(720px, calc(100vw - 32px)) !important;
              word-break: keep-all;
            }
          `}
        </style>

        <div className="header-row">
          <div className="page-title">AI 콘텐츠 자동화 등록</div>
          <div className="header-actions">
            {/* {accountConnected ? ( */}
              <div style={{ minWidth: 260 }}>
                  <div
                      onClickCapture={(e) => {
                          if (!accountConnected && this.state.publish_type !== "DRAFT") {
                            e.preventDefault();
                            e.stopPropagation();
                            this.needConnectAccount();
                          }
                      }}
                  >
                    <SelectBoxV2
                      id="ig_account_select"
                      width="100%"
                      height="42px"
                      padding="8px 36px 8px 12px"
                      pageWrapperName="InstagramFeedAutomationMerged"
                      list={displayAccountList || []}
                      optionTexttName="title"
                      optionValuetName="value"
                      selectedValue={accountSelectValue}
                      selectedText={
                        selectedAccountText ||
                        displayAccountList.find((acc) => String(acc.value) === String(activeIgSeq))?.title ||
                        "계정 선택"
                      }
                      placeHolder="계정 선택"
                      onClickOption={this.onChangeAccount}
                    />
                </div>
              </div>
            {/* ) : null} */}
            <button className="btn btn-secondary" data-instagram-tutorial="ai-setting-run-now" type="button" onClick={this.runInstagramAiImageAutoSettingNow}>저장 후 즉시 실행</button>
            <button className="btn btn-primary" data-instagram-tutorial="ai-setting-save" type="button" onClick={this.saveInstagramAiImageAutoSetting}>저장</button>
          </div>
        </div>
        
        <div className="main-container-wrapper">
          <div id="InstagramFeedAutomationMerged" className="main-container">
            {!accountConnected && this.state.publish_type !== "DRAFT" ? (
              <div className="alert-banner">
                <div className="alert-banner-text">
                  <span style={{marginBottom:"0.1rem"}}>
                    {/* ℹ️ */}
                    <svg width="16" height="16" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M9.75 0C7.82164 0 5.93657 0.571828 4.33319 1.64317C2.72982 2.71451 1.48013 4.23726 0.742179 6.01884C0.00422452 7.80042 -0.188858 9.76082 0.187348 11.6521C0.563554 13.5434 1.49215 15.2807 2.85571 16.6443C4.21928 18.0079 5.95656 18.9365 7.84787 19.3127C9.73919 19.6889 11.6996 19.4958 13.4812 18.7578C15.2627 18.0199 16.7855 16.7702 17.8568 15.1668C18.9282 13.5634 19.5 11.6784 19.5 9.75C19.4975 7.1649 18.4695 4.68639 16.6416 2.85845C14.8136 1.03051 12.3351 0.00248209 9.75 0ZM9.5625 4.5C9.78501 4.5 10.0025 4.56598 10.1875 4.6896C10.3725 4.81321 10.5167 4.98891 10.6019 5.19448C10.687 5.40005 10.7093 5.62625 10.6659 5.84448C10.6225 6.06271 10.5153 6.26316 10.358 6.4205C10.2007 6.57783 10.0002 6.68498 9.78198 6.72838C9.56375 6.77179 9.33755 6.74951 9.13199 6.66436C8.92642 6.57922 8.75072 6.43502 8.6271 6.25002C8.50348 6.06501 8.4375 5.8475 8.4375 5.625C8.4375 5.32663 8.55603 5.04048 8.76701 4.8295C8.97799 4.61853 9.26414 4.5 9.5625 4.5ZM10.5 15H9.75C9.55109 15 9.36033 14.921 9.21967 14.7803C9.07902 14.6397 9 14.4489 9 14.25V9.75C8.80109 9.75 8.61033 9.67098 8.46967 9.53033C8.32902 9.38968 8.25 9.19891 8.25 9C8.25 8.80109 8.32902 8.61032 8.46967 8.46967C8.61033 8.32902 8.80109 8.25 9 8.25H9.75C9.94892 8.25 10.1397 8.32902 10.2803 8.46967C10.421 8.61032 10.5 8.80109 10.5 9V13.5C10.6989 13.5 10.8897 13.579 11.0303 13.7197C11.171 13.8603 11.25 14.0511 11.25 14.25C11.25 14.4489 11.171 14.6397 11.0303 14.7803C10.8897 14.921 10.6989 15 10.5 15Z" fill="#708eac"/>
                    </svg>
                  </span>
                  <span>
                    {/* <strong>쇼핑몰 연동 안내</strong>  */}
                  자동 발행을 하려면 계정연동이 필요하고, 임시 저장은 계정연동 없이도 가능합니다.
                  </span>
                </div>
                <button className="btn-banner" type="button" onClick={this.handleConnectAccount}>계정 연동하기</button>
              </div>
            ) : null}

            <div className="title-input-box" data-instagram-tutorial="ai-setting-title">
              <label className="title-label">피드 자동화 식별 제목</label>
              <input
                type="text"
                className="title-input"
                placeholder="예: 봄 신상품 매일 오전 자동 발행"
                value={settingName || ""}
                onChange={(e) => this.setState({ settingName: e.target.value })}
              />
            </div>

            <div className="card" data-instagram-tutorial="ai-setting-media">
              <div className="card-header">
                <h3 className="card-title">1. 피드 이미지 포맷</h3>
                <p className="card-subtitle">발행될 이미지의 비율과 장수를 설정합니다.</p>
              </div>

              <div className="ratio-container">
                  {/* <div className="ratio-title">📐 이미지 비율</div> */}
                  <div className="ratio-title">
                    {/* <i className="fa fa-solid fa-crop-simple"></i> */}
                    <i className="fa fa-solid fa-expand"></i>
                    <span>이미지 비율</span>
                  </div>
                  <div className="ratio-btn-group" data-instagram-tutorial="ai-setting-ratio">
                    {[{value:"1/1", text:"1:1 (정방형)"}, {value:"4/5", text:"4:5 (세로형)"}, {value:"16/9", text:"16:9 (가로형)"}].map((item) => (
                      <button class={"ratio-btn" + (item.value === selectedRatio ? " active" : "")} key={item.value} onClick={() => this.setRatio(item.value)}>{item.text}</button>
                      ))}
                  </div>
              </div>

              <div className="selection-grid grid-cols-2" data-instagram-tutorial="ai-setting-feed-type">
                <div data-instagram-tutorial="ai-setting-feed-single" className={`selection-card ${selectedFeedType === "SINGLE" ? "active" : ""}`} onClick={() => this.setFeedType("SINGLE")}>
                  <div className="selection-title">싱글 피드</div>
                  <div className="selection-desc">상품 이미지 1장만 추출하여 발행합니다.</div>
                </div>
                <div data-instagram-tutorial="ai-setting-feed-slide" className={`selection-card ${selectedFeedType === "SLIDE" ? "active" : ""}`} onClick={() => this.setFeedType("SLIDE")}>
                  <div className="selection-title">슬라이드 (캐러셀) 피드</div>
                  <div className="selection-desc">여러 장의 상품 이미지를 슬라이드로 묶어 발행합니다.</div>
                  <div data-instagram-tutorial="ai-setting-slide-count" className={`slide-options ${selectedFeedType === "SLIDE" ? "show" : ""}`}>
                    <label className="form-label">최대 추출 장수 설정</label>
                    
                    {/* <select className="form-select" value={selectedSlideCount} onChange={(e) => this.selectSlideCount(Number(e.target.value))}>
                      {[2,3,4,5,6,7,8,9,10].map((num) => <option key={num} value={num}>최대 {num}장</option>)}
                    </select> */}
                    <SelectBoxV2
                      id="slide-count-select"
                      className="background-white"
                      width="100%"
                      height="40px"
                      selectedValue={selectedSlideCount}
                      list={[
                        {key: 2, title: "2"},
                        {key: 3, title: "3"},
                        {key: 4, title: "4"},
                        {key: 5, title: "5"},
                        {key: 6, title: "6"},
                        {key: 7, title: "7"},
                        {key: 8, title: "8"},
                        {key: 9, title: "9"},
                        {key: 10, title: "10"},
                      ]}
                      optionTexttName="title"
                      optionValuetName="key"
                      onClickOption={(text, value) => this.selectSlideCount(value)}
                    />
                      
                  </div>
                </div>
              </div>
            </div>

            {/* <div className="card">
              <div className="card-header">
                <h3 className="card-title">2. 진열 로직 선택</h3>
                <p className="card-subtitle">쇼핑몰 데이터를 기반으로 피드에 노출될 상품의 추출 기준과 상세 필터를 설정합니다.</p>
              </div>

              <div className="selection-grid grid-cols-4">
                {[
                  { key: "ORDERS", title: "판매량 베스트", desc: "결제 데이터 기반 가장 많이 팔린 순서대로 가져옵니다." },
                  { key: "NEW", title: "신상품 우선", desc: "쇼핑몰에 최근 업데이트된 상품 순서대로 가져옵니다." },
                  { key: "VIEWS", title: "조회 높은 순", desc: "고객의 클릭 및 뷰가 가장 높은 순서대로 가져옵니다." },
                  { key: "ROBOMD", title: "쇼핑몰 진열 연동", desc: "내 쇼핑몰의 복합 진열 규칙을 그대로 적용합니다." },
                ].map((item) => (
                  <div key={item.key} className={`selection-card ${selectedLogic === item.key ? "active" : ""}`} onClick={() => this.selectLogic(item.key)}>
                    <div className="selection-title">{item.title}</div>
                    <div className="selection-desc">{item.desc}</div>
                  </div>
                ))}
              </div>

              {this.renderRoboMDBox()}
              {this.renderDuplicateProductSetting()}
            </div> */}

            <div className="card" data-instagram-tutorial="ai-setting-rule">
              <div
                className="card-header"
                style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}
              >
                <div>
                  <h3 className="card-title">2. AI 이미지 생성 규칙 설정</h3>
                  <p className="card-subtitle">
                    AI 스튜디오를 열어 스케줄러가 자동으로 실행할 프롬프트(요청사항)를 각 장마다 저장하세요.
                  </p>
                </div>
              </div>

              <div>{this.renderSeriesConsistencySetting()}</div>
              <div data-instagram-tutorial="ai-setting-rule-cards">{this.renderAiImageRuleCards()}</div>
            </div>

            <div className="card" data-instagram-tutorial="ai-setting-copy">
              <div className="card-header">
                <h3 className="card-title">3. 피드 문구(본문) 설정</h3>
                <p className="card-subtitle">선택된 방식에 따라 매번 피드에 들어갈 본문을 구성합니다.</p>
              </div>

              <div className="selection-grid grid-cols-3" data-instagram-tutorial="ai-setting-copy-mode">
                <div data-instagram-tutorial="ai-setting-copy-ai" className={`selection-card ${textMode === "AI" ? "active" : ""}`} onClick={() => this.setTextMode("AI")} style={{ minHeight: 90 }}>
                  {/* <div className="selection-title">✨ AI 자동 생성</div> */}
                  <div className="selection-title">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M18.5232 12.0994L13.6847 10.3181L11.9035 5.47594C11.798 5.18937 11.6072 4.94206 11.3567 4.76736C11.1063 4.59267 10.8082 4.499 10.5029 4.499C10.1975 4.499 9.89949 4.59267 9.64904 4.76736C9.39858 4.94206 9.20773 5.18937 9.10225 5.47594L7.31912 10.3125L2.47694 12.0938C2.19037 12.1992 1.94305 12.3901 1.76836 12.6405C1.59367 12.891 1.5 13.189 1.5 13.4944C1.5 13.7997 1.59367 14.0978 1.76836 14.3482C1.94305 14.5987 2.19037 14.7895 2.47694 14.895L7.31256 16.6875L9.09381 21.5269C9.1993 21.8134 9.39014 22.0608 9.6406 22.2355C9.89106 22.4101 10.1891 22.5038 10.4944 22.5038C10.7998 22.5038 11.0978 22.4101 11.3483 22.2355C11.5987 22.0608 11.7896 21.8134 11.8951 21.5269L13.6763 16.6884L18.5185 14.9072C18.8051 14.8017 19.0524 14.6109 19.2271 14.3604C19.4018 14.1099 19.4954 13.8119 19.4954 13.5066C19.4954 13.2012 19.4018 12.9032 19.2271 12.6527C19.0524 12.4023 18.8051 12.2114 18.5185 12.1059L18.5232 12.0994ZM13.1616 15.2812C12.9589 15.3556 12.7749 15.4732 12.6222 15.6259C12.4696 15.7786 12.352 15.9626 12.2776 16.1653L10.4963 20.9897L8.71881 16.1616C8.64436 15.96 8.52712 15.7769 8.37516 15.6249C8.22319 15.4729 8.04011 15.3557 7.8385 15.2812L3.01412 13.5L7.8385 11.7188C8.04011 11.6443 8.22319 11.5271 8.37516 11.3751C8.52712 11.2231 8.64436 11.04 8.71881 10.8384L10.5001 6.01406L12.2813 10.8384C12.3557 11.0411 12.4733 11.2252 12.626 11.3778C12.7786 11.5305 12.9627 11.6481 13.1654 11.7225L17.9897 13.5037L13.1616 15.2812ZM13.5001 3.75C13.5001 3.55109 13.5791 3.36032 13.7197 3.21967C13.8604 3.07902 14.0511 3 14.2501 3H15.7501V1.5C15.7501 1.30109 15.8291 1.11032 15.9697 0.96967C16.1104 0.829018 16.3011 0.75 16.5001 0.75C16.699 0.75 16.8897 0.829018 17.0304 0.96967C17.171 1.11032 17.2501 1.30109 17.2501 1.5V3H18.7501C18.949 3 19.1397 3.07902 19.2804 3.21967C19.421 3.36032 19.5001 3.55109 19.5001 3.75C19.5001 3.94891 19.421 4.13968 19.2804 4.28033C19.1397 4.42098 18.949 4.5 18.7501 4.5H17.2501V6C17.2501 6.19891 17.171 6.38968 17.0304 6.53033C16.8897 6.67098 16.699 6.75 16.5001 6.75C16.3011 6.75 16.1104 6.67098 15.9697 6.53033C15.8291 6.38968 15.7501 6.19891 15.7501 6V4.5H14.2501C14.0511 4.5 13.8604 4.42098 13.7197 4.28033C13.5791 4.13968 13.5001 3.94891 13.5001 3.75ZM23.2501 8.25C23.2501 8.44891 23.171 8.63968 23.0304 8.78033C22.8897 8.92098 22.699 9 22.5001 9H21.7501V9.75C21.7501 9.94891 21.671 10.1397 21.5304 10.2803C21.3897 10.421 21.199 10.5 21.0001 10.5C20.8011 10.5 20.6104 10.421 20.4697 10.2803C20.3291 10.1397 20.2501 9.94891 20.2501 9.75V9H19.5001C19.3011 9 19.1104 8.92098 18.9697 8.78033C18.8291 8.63968 18.7501 8.44891 18.7501 8.25C18.7501 8.05109 18.8291 7.86032 18.9697 7.71967C19.1104 7.57902 19.3011 7.5 19.5001 7.5H20.2501V6.75C20.2501 6.55109 20.3291 6.36032 20.4697 6.21967C20.6104 6.07902 20.8011 6 21.0001 6C21.199 6 21.3897 6.07902 21.5304 6.21967C21.671 6.36032 21.7501 6.55109 21.7501 6.75V7.5H22.5001C22.699 7.5 22.8897 7.57902 23.0304 7.71967C23.171 7.86032 23.2501 8.05109 23.2501 8.25Z" fill="#333333"/>
                    </svg>
                    <span style={{ marginLeft: 8 }}>AI 카피라이터</span>
                  </div>
                  <div className="selection-desc">AI 카피라이터가 자동으로 작성합니다.</div>
                </div>
                <div data-instagram-tutorial="ai-setting-copy-learn" className={`selection-card ${textMode === "LEARN" ? "active" : ""}`} onClick={() => this.setTextMode("LEARN")} style={{ minHeight: 90 }}>
                  {/* <div className="selection-title">📱 내 피드 학습</div> */}
                  <div className="selection-title">
                    {/* <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M21.7837 20.7027L19.3017 18.1917C20.1874 17.0408 20.6096 15.6074 20.517 14.1426C20.414 12.5942 19.7446 11.1713 18.622 10.125C17.4994 9.07876 16.037 8.51379 14.5128 8.55564C12.9886 8.58702 11.5571 9.21478 10.4758 10.3029C9.3944 11.391 8.78678 12.8557 8.75588 14.4042C8.72499 15.9527 9.27082 17.4383 10.3007 18.5788C11.3305 19.7192 12.7415 20.4097 14.2554 20.5039C15.6972 20.6085 17.1081 20.1586 18.2409 19.2693L20.7229 21.7908C20.8671 21.9268 21.0525 22.0105 21.2482 22.0105C21.4438 22.0105 21.6292 21.9372 21.7734 21.7908C21.9176 21.6443 21.9897 21.456 21.9897 21.2572C21.9897 21.0584 21.9073 20.8596 21.7734 20.7236L21.7837 20.7027ZM18.7044 16.2456C18.3645 17.0617 17.8084 17.7522 17.0875 18.244C16.3666 18.7357 15.5221 18.9973 14.6467 18.9973C13.4727 18.9973 12.381 18.5265 11.5468 17.6894C10.7229 16.8524 10.2595 15.7329 10.2595 14.5402C10.2595 13.6614 10.517 12.8034 11.001 12.0606C11.485 11.3282 12.1647 10.7632 12.968 10.418C13.7713 10.0832 14.6467 9.98901 15.5015 10.1669C16.3563 10.3447 17.1287 10.7632 17.7466 11.391C18.3645 12.0187 18.7765 12.8034 18.9516 13.6718C19.1266 14.5402 19.0339 15.4295 18.7044 16.2456Z" fill="#333333"/>
                      <path d="M7.802 19H3.65292C2.74131 19 2 18.2591 2 17.3479V4.65207C2 3.74093 2.74131 3 3.65292 3H17.3471C18.2587 3 19 3.74093 19 4.65207V7.14643C19 7.55695 18.6594 7.89737 18.2487 7.89737C17.8379 7.89737 17.4973 7.55695 17.4973 7.14643V4.65207C17.4973 4.57196 17.4272 4.50188 17.3471 4.50188H3.65292C3.57278 4.50188 3.50265 4.57196 3.50265 4.65207V17.3479C3.50265 17.428 3.57278 17.4981 3.65292 17.4981H7.802C8.21273 17.4981 8.55333 17.8385 8.55333 18.2491C8.55333 18.6596 8.21273 19 7.802 19Z" fill="#333333"/>
                    </svg> */}
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <g clip-path="url(#clip0_2434_307)">
                      <path d="M4.20687 7.60684L9.30101 2.2188C9.43677 2.07521 9.62424 2 9.81818 2H18.5455C18.9333 2 19.3018 2.1641 19.5733 2.45128C19.8448 2.73846 20 3.12821 20 3.53846V20.4615C20 20.8718 19.8448 21.2615 19.5733 21.5487C19.3018 21.8359 18.9333 22 18.5455 22H5.45455C5.06667 22 4.69818 21.8359 4.42667 21.5487C4.15515 21.2615 4 20.8718 4 20.4615V8.15385C4 7.94872 4.07111 7.75043 4.20687 7.60684ZM9.09414 4.62564L6.48242 7.3812H9.09414V4.62564ZM5.45455 20.4615H18.5455V3.53846H10.5422V8.15385C10.5422 8.35897 10.4646 8.55043 10.3289 8.70085C10.1931 8.84444 10.0057 8.9265 9.81172 8.9265H5.45455V20.4684V20.4615Z" fill="#333333"/>
                      <path d="M16.2053 10.9368H7.79476C7.42702 10.9368 7.12891 11.2521 7.12891 11.641V17.8017C7.12891 18.1907 7.42702 18.506 7.79476 18.506H16.2053C16.573 18.506 16.8711 18.1907 16.8711 17.8017V11.641C16.8711 11.2521 16.573 10.9368 16.2053 10.9368Z" fill="white"/>
                      <path d="M16.2052 11.6205L16.2246 11.641L16.2052 17.8222L7.77535 17.8017L7.79475 11.6205H16.2117M16.2117 10.253H7.79475C7.0707 10.253 6.48242 10.8752 6.48242 11.641V17.8017C6.48242 18.5675 7.0707 19.1897 7.79475 19.1897H16.2117C16.9358 19.1897 17.524 18.5675 17.524 17.8017V11.641C17.524 10.8752 16.9358 10.253 16.2117 10.253Z" fill="#1D1D1B"/>
                      <path fill-rule="evenodd" clip-rule="evenodd" d="M11.4033 5.78801C11.4033 5.4392 11.6719 5.15643 12.0033 5.15643H16.9617C17.2931 5.15643 17.5617 5.4392 17.5617 5.78801C17.5617 6.13682 17.2931 6.41959 16.9617 6.41959H12.0033C11.6719 6.41959 11.4033 6.13682 11.4033 5.78801Z" fill="#1D1D1B"/>
                      <path fill-rule="evenodd" clip-rule="evenodd" d="M11.4033 8.22905C11.4033 7.88024 11.6719 7.59747 12.0033 7.59747H15.0546C15.386 7.59747 15.6546 7.88024 15.6546 8.22905C15.6546 8.57786 15.386 8.86063 15.0546 8.86063H12.0033C11.6719 8.86063 11.4033 8.57786 11.4033 8.22905Z" fill="#1D1D1B"/>
                      </g>
                      <defs>
                      <clipPath id="clip0_2434_307">
                      <rect width="16" height="20" fill="white" transform="translate(4 2)"/>
                      </clipPath>
                      </defs>
                    </svg>
                    <span style={{ marginLeft: 8 }}>내 피드 학습</span>
                  </div>
                  <div className="selection-desc">내 기존 게시물의 톤앤매너를 학습합니다.</div>
                </div>
                <div data-instagram-tutorial="ai-setting-copy-direct" className={`selection-card ${textMode === "DIRECT" ? "active" : ""}`} onClick={() => this.setTextMode("DIRECT")} style={{ minHeight: 90 }}>
                  {/* <div className="selection-title">📝 고정 문구 등록</div> */}
                  <div className="selection-title">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M22.0614 7.62846L16.3717 1.93971C16.2324 1.80038 16.067 1.68986 15.885 1.61446C15.703 1.53905 15.5079 1.50024 15.3109 1.50024C15.1139 1.50024 14.9188 1.53905 14.7368 1.61446C14.5548 1.68986 14.3894 1.80038 14.2501 1.93971L9.22229 6.98346C8.22292 6.67033 5.94104 6.29252 3.55979 8.21533C3.39566 8.34731 3.26116 8.51238 3.16505 8.69978C3.06894 8.88719 3.01337 9.09273 3.00196 9.30304C2.99055 9.51334 3.02356 9.72369 3.09883 9.9204C3.17411 10.1171 3.28996 10.2957 3.43885 10.4447L7.96885 14.9728L3.96948 18.9694C3.82875 19.1101 3.74969 19.301 3.74969 19.5C3.74969 19.699 3.82875 19.8899 3.96948 20.0306C4.11021 20.1714 4.30108 20.2504 4.5001 20.2504C4.69913 20.2504 4.89 20.1714 5.03073 20.0306L9.02729 16.0313L13.5545 20.5585C13.6937 20.6982 13.859 20.8092 14.0411 20.8849C14.2232 20.9607 14.4185 20.9998 14.6157 21H14.7217C14.9355 20.9852 15.1436 20.9245 15.3319 20.8221C15.5202 20.7197 15.6843 20.578 15.8129 20.4066C17.6542 17.9597 17.477 15.9703 17.0495 14.7816L22.0623 9.75002C22.2016 9.61066 22.312 9.44524 22.3873 9.2632C22.4626 9.08115 22.5014 8.88605 22.5013 8.68904C22.5012 8.49203 22.4623 8.29697 22.3868 8.11499C22.3113 7.93301 22.2007 7.76769 22.0614 7.62846ZM21.0001 8.68971L15.631 14.0766C15.5202 14.1879 15.447 14.3312 15.422 14.4863C15.3969 14.6414 15.4212 14.8004 15.4914 14.941C16.3782 16.7156 15.3226 18.5588 14.6157 19.4991L4.5001 9.38252C5.6326 8.46939 6.71635 8.22846 7.5451 8.22846C8.07097 8.22128 8.5925 8.32443 9.07604 8.53127C9.21711 8.60187 9.37685 8.62619 9.53254 8.60078C9.68822 8.57536 9.83193 8.5015 9.94323 8.38971L15.3114 3.00002L21.0001 8.68877V8.68971Z" fill="#333333"/>
                    </svg>
                    <span style={{ marginLeft: 8 }}>고정 문구 등록</span>
                  </div>
                  <div className="selection-desc">매번 동일한 고정 텍스트를 사용합니다.</div>
                </div>
              </div>

              <div data-instagram-tutorial="ai-setting-copy-detail">{this.renderCaptionArea()}</div>
            </div>

            <div className="card" data-instagram-tutorial="ai-setting-hashtag">
              <div id="hashtag_header" className="card-header" style={{display: "flex", justifyContent: "space-between", alignItems: "flex-start",}}>
                <div>
                  <h3 className="card-title">4. 해시태그 설정</h3>
                  <p className="card-subtitle">본문 문맥을 분석하여 해시태그를 자동 생성하거나 고정할 수 있습니다.</p>
                </div>
                <a href="/shop/instagram/InstagramCreateCntSetting" className="setting-link" target='_blank'>
                  <svg width="20" height="19" viewBox="0 0 20 19" fill="none" xmlns="http://www.w3.org/2000/svg"><path opacity="0.5" d="M19.7724 7.39596C19.7502 7.29675 19.7069 7.20343 19.6453 7.12231C19.5837 7.04119 19.5053 6.97417 19.4154 6.92577L17.2914 5.75473C17.1775 5.52521 17.0493 5.30299 16.9076 5.08936L16.9522 2.6763C16.9531 2.57508 16.9329 2.47479 16.8929 2.38168C16.853 2.28857 16.7942 2.20464 16.7202 2.13513C15.6626 1.18194 14.4119 0.465009 13.0522 0.0325761C12.9546 0.00231062 12.8516 -0.00701284 12.75 0.00522155C12.6485 0.017456 12.5507 0.0509698 12.4632 0.103549L10.3838 1.34557C10.1249 1.33669 9.87506 1.33669 9.61624 1.34557L7.53682 0.103549C7.44928 0.0509698 7.35151 0.017456 7.24997 0.00522155C7.14843 -0.00701284 7.04544 0.00231062 6.9478 0.0325761C5.58632 0.465338 4.33507 1.1856 3.2798 2.144C3.20514 2.21112 3.1458 2.29335 3.10578 2.38517C3.06575 2.47698 3.04597 2.57625 3.04776 2.6763L3.09238 5.08936C2.94959 5.30228 2.82465 5.52407 2.6997 5.75473L0.575656 6.92577C0.487054 6.97467 0.410099 7.04199 0.350035 7.12313C0.289971 7.20427 0.24821 7.29733 0.227598 7.39596C-0.075866 8.78144 -0.075866 10.2156 0.227598 11.6011C0.249766 11.7003 0.29311 11.7936 0.354698 11.8747C0.416286 11.9558 0.494681 12.0229 0.584581 12.0713L2.70863 13.2423C2.82249 13.4718 2.95066 13.694 3.09238 13.9077L3.04776 16.3207C3.04695 16.422 3.06713 16.5222 3.10705 16.6154C3.14698 16.7085 3.20578 16.7924 3.2798 16.8619C4.3374 17.8151 5.58812 18.532 6.9478 18.9645C7.04544 18.9947 7.14843 19.0041 7.24997 18.9918C7.35151 18.9796 7.44928 18.9461 7.53682 18.8935L9.61624 17.6515H10.3838L12.4721 18.8935C12.5807 18.9645 12.7081 19.0015 12.838 19C12.9106 18.9968 12.9825 18.9849 13.0522 18.9645C14.4137 18.5317 15.6649 17.8114 16.7202 16.853C16.7949 16.7859 16.8542 16.7037 16.8942 16.6119C16.9342 16.5201 16.954 16.4208 16.9522 16.3207L16.9076 13.9077C17.0504 13.6948 17.1754 13.473 17.3003 13.2423L19.4243 12.0713C19.5129 12.0224 19.5899 11.9551 19.65 11.8739C19.71 11.7928 19.7518 11.6997 19.7724 11.6011C20.0759 10.2156 20.0759 8.78144 19.7724 7.39596ZM18.4337 10.9889L16.4078 12.1068C16.2793 12.181 16.1766 12.2924 16.1133 12.4261C15.9665 12.7285 15.7964 13.0192 15.6046 13.2955C15.5232 13.4195 15.4797 13.5644 15.4797 13.7125L15.5154 16.0102C14.747 16.6606 13.8674 17.1684 12.9183 17.5095L10.9282 16.3207C10.8171 16.2565 10.6908 16.2228 10.5622 16.2232H10.5087C10.1701 16.2498 9.82991 16.2498 9.4913 16.2232C9.34498 16.2152 9.19942 16.2491 9.07185 16.3207L7.08167 17.5095C6.13377 17.1701 5.25434 16.6654 4.48462 16.0191L4.52032 13.7125C4.52026 13.5644 4.47685 13.4195 4.39537 13.2955C4.20684 13.0171 4.03691 12.7267 3.88667 12.4261C3.82078 12.2942 3.71868 12.1835 3.59216 12.1068L1.56628 10.9889C1.38773 10.0033 1.38773 8.99379 1.56628 8.0081L3.59216 6.89028C3.72068 6.81603 3.82341 6.70463 3.88667 6.57091C4.0335 6.26853 4.20357 5.97787 4.39537 5.7015C4.47685 5.57751 4.52026 5.43263 4.52032 5.28453L4.48462 2.9868C5.25298 2.33645 6.13264 1.82862 7.08167 1.48751L9.07185 2.6763C9.19905 2.74892 9.34489 2.78285 9.4913 2.77389C9.82991 2.74727 10.1701 2.74727 10.5087 2.77389C10.655 2.78183 10.8006 2.74797 10.9282 2.6763L12.9183 1.48751C13.8662 1.82698 14.7457 2.33168 15.5154 2.97793L15.4797 5.28453C15.4797 5.43263 15.5232 5.57751 15.6046 5.7015C15.7932 5.97992 15.9631 6.27035 16.1133 6.57091C16.1792 6.70286 16.2813 6.81358 16.4078 6.89028L18.4337 8.0081C18.6123 8.99379 18.6123 10.0033 18.4337 10.9889ZM10 4.53045C9.01154 4.53045 8.04527 4.82182 7.22339 5.36772C6.40152 5.91362 5.76094 6.68953 5.38267 7.59732C5.00441 8.50512 4.90543 9.50403 5.09827 10.4677C5.29111 11.4315 5.7671 12.3167 6.46605 13.0115C7.165 13.7063 8.05552 14.1794 9.02499 14.3711C9.99446 14.5628 10.9993 14.4644 11.9126 14.0884C12.8258 13.7124 13.6063 13.0756 14.1555 12.2586C14.7046 11.4416 14.9978 10.4811 14.9978 9.49852C14.9978 8.18091 14.4712 6.91726 13.5339 5.98557C12.5967 5.05387 11.3255 4.53045 10 4.53045ZM10 13.0471C9.29395 13.0471 8.60377 12.839 8.01671 12.4491C7.42966 12.0592 6.9721 11.5049 6.70191 10.8565C6.43172 10.2081 6.36102 9.49458 6.49877 8.80622C6.63651 8.11785 6.9765 7.48555 7.47575 6.98927C7.975 6.49298 8.61108 6.15501 9.30356 6.01809C9.99604 5.88116 10.7138 5.95144 11.3661 6.22002C12.0184 6.48861 12.5759 6.94345 12.9682 7.52701C13.3605 8.11058 13.5698 8.79667 13.5698 9.49852C13.5698 10.4397 13.1937 11.3423 12.5243 12.0078C11.8548 12.6733 10.9468 13.0471 10 13.0471Z" fill="#6b7280"></path></svg> 
                  개수 설정 (현재 5개)
                </a>
              </div>

              <div className="selection-grid grid-cols-2" data-instagram-tutorial="ai-setting-hashtag-mode">
                <div data-instagram-tutorial="ai-setting-hashtag-ai" className={`selection-card ${hashtagMode === "AI" ? "active" : ""}`} onClick={() => this.setHashtagMode("AI")} style={{ minHeight: 90 }}>
                  {/* <div className="selection-title">🪄 AI 매번 생성</div> */}
                  <div className="selection-title">
                    {/* <i className="fa fa-solid fa-wand-magic-sparkles" style={{ fontSize: "18px", color: "#333333" }}></i> */}
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M18.5232 12.0994L13.6847 10.3181L11.9035 5.47594C11.798 5.18937 11.6072 4.94206 11.3567 4.76736C11.1063 4.59267 10.8082 4.499 10.5029 4.499C10.1975 4.499 9.89949 4.59267 9.64904 4.76736C9.39858 4.94206 9.20773 5.18937 9.10225 5.47594L7.31912 10.3125L2.47694 12.0938C2.19037 12.1992 1.94305 12.3901 1.76836 12.6405C1.59367 12.891 1.5 13.189 1.5 13.4944C1.5 13.7997 1.59367 14.0978 1.76836 14.3482C1.94305 14.5987 2.19037 14.7895 2.47694 14.895L7.31256 16.6875L9.09381 21.5269C9.1993 21.8134 9.39014 22.0608 9.6406 22.2355C9.89106 22.4101 10.1891 22.5038 10.4944 22.5038C10.7998 22.5038 11.0978 22.4101 11.3483 22.2355C11.5987 22.0608 11.7896 21.8134 11.8951 21.5269L13.6763 16.6884L18.5185 14.9072C18.8051 14.8017 19.0524 14.6109 19.2271 14.3604C19.4018 14.1099 19.4954 13.8119 19.4954 13.5066C19.4954 13.2012 19.4018 12.9032 19.2271 12.6527C19.0524 12.4023 18.8051 12.2114 18.5185 12.1059L18.5232 12.0994ZM13.1616 15.2812C12.9589 15.3556 12.7749 15.4732 12.6222 15.6259C12.4696 15.7786 12.352 15.9626 12.2776 16.1653L10.4963 20.9897L8.71881 16.1616C8.64436 15.96 8.52712 15.7769 8.37516 15.6249C8.22319 15.4729 8.04011 15.3557 7.8385 15.2812L3.01412 13.5L7.8385 11.7188C8.04011 11.6443 8.22319 11.5271 8.37516 11.3751C8.52712 11.2231 8.64436 11.04 8.71881 10.8384L10.5001 6.01406L12.2813 10.8384C12.3557 11.0411 12.4733 11.2252 12.626 11.3778C12.7786 11.5305 12.9627 11.6481 13.1654 11.7225L17.9897 13.5037L13.1616 15.2812ZM13.5001 3.75C13.5001 3.55109 13.5791 3.36032 13.7197 3.21967C13.8604 3.07902 14.0511 3 14.2501 3H15.7501V1.5C15.7501 1.30109 15.8291 1.11032 15.9697 0.96967C16.1104 0.829018 16.3011 0.75 16.5001 0.75C16.699 0.75 16.8897 0.829018 17.0304 0.96967C17.171 1.11032 17.2501 1.30109 17.2501 1.5V3H18.7501C18.949 3 19.1397 3.07902 19.2804 3.21967C19.421 3.36032 19.5001 3.55109 19.5001 3.75C19.5001 3.94891 19.421 4.13968 19.2804 4.28033C19.1397 4.42098 18.949 4.5 18.7501 4.5H17.2501V6C17.2501 6.19891 17.171 6.38968 17.0304 6.53033C16.8897 6.67098 16.699 6.75 16.5001 6.75C16.3011 6.75 16.1104 6.67098 15.9697 6.53033C15.8291 6.38968 15.7501 6.19891 15.7501 6V4.5H14.2501C14.0511 4.5 13.8604 4.42098 13.7197 4.28033C13.5791 4.13968 13.5001 3.94891 13.5001 3.75ZM23.2501 8.25C23.2501 8.44891 23.171 8.63968 23.0304 8.78033C22.8897 8.92098 22.699 9 22.5001 9H21.7501V9.75C21.7501 9.94891 21.671 10.1397 21.5304 10.2803C21.3897 10.421 21.199 10.5 21.0001 10.5C20.8011 10.5 20.6104 10.421 20.4697 10.2803C20.3291 10.1397 20.2501 9.94891 20.2501 9.75V9H19.5001C19.3011 9 19.1104 8.92098 18.9697 8.78033C18.8291 8.63968 18.7501 8.44891 18.7501 8.25C18.7501 8.05109 18.8291 7.86032 18.9697 7.71967C19.1104 7.57902 19.3011 7.5 19.5001 7.5H20.2501V6.75C20.2501 6.55109 20.3291 6.36032 20.4697 6.21967C20.6104 6.07902 20.8011 6 21.0001 6C21.199 6 21.3897 6.07902 21.5304 6.21967C21.671 6.36032 21.7501 6.55109 21.7501 6.75V7.5H22.5001C22.699 7.5 22.8897 7.57902 23.0304 7.71967C23.171 7.86032 23.2501 8.05109 23.2501 8.25Z" fill="#333333"/>
                    </svg>
                    <span style={{ marginLeft: "8px" }}>AI 자동 생성</span>
                  </div>
                  <div className="selection-desc">피드 문구를 분석하여 해시태그를 자동 생성합니다.</div>
                </div>
                <div data-instagram-tutorial="ai-setting-hashtag-basic" className={`selection-card ${hashtagMode === "BASIC" ? "active" : ""}`} onClick={() => this.setHashtagMode("BASIC")} style={{ minHeight: 90 }}>
                  {/* <div className="selection-title">📌 기본 고정만 사용</div> */}
                  <div className="selection-title">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M22.0614 7.62846L16.3717 1.93971C16.2324 1.80038 16.067 1.68986 15.885 1.61446C15.703 1.53905 15.5079 1.50024 15.3109 1.50024C15.1139 1.50024 14.9188 1.53905 14.7368 1.61446C14.5548 1.68986 14.3894 1.80038 14.2501 1.93971L9.22229 6.98346C8.22292 6.67033 5.94104 6.29252 3.55979 8.21533C3.39566 8.34731 3.26116 8.51238 3.16505 8.69978C3.06894 8.88719 3.01337 9.09273 3.00196 9.30304C2.99055 9.51334 3.02356 9.72369 3.09883 9.9204C3.17411 10.1171 3.28996 10.2957 3.43885 10.4447L7.96885 14.9728L3.96948 18.9694C3.82875 19.1101 3.74969 19.301 3.74969 19.5C3.74969 19.699 3.82875 19.8899 3.96948 20.0306C4.11021 20.1714 4.30108 20.2504 4.5001 20.2504C4.69913 20.2504 4.89 20.1714 5.03073 20.0306L9.02729 16.0313L13.5545 20.5585C13.6937 20.6982 13.859 20.8092 14.0411 20.8849C14.2232 20.9607 14.4185 20.9998 14.6157 21H14.7217C14.9355 20.9852 15.1436 20.9245 15.3319 20.8221C15.5202 20.7197 15.6843 20.578 15.8129 20.4066C17.6542 17.9597 17.477 15.9703 17.0495 14.7816L22.0623 9.75002C22.2016 9.61066 22.312 9.44524 22.3873 9.2632C22.4626 9.08115 22.5014 8.88605 22.5013 8.68904C22.5012 8.49203 22.4623 8.29697 22.3868 8.11499C22.3113 7.93301 22.2007 7.76769 22.0614 7.62846ZM21.0001 8.68971L15.631 14.0766C15.5202 14.1879 15.447 14.3312 15.422 14.4863C15.3969 14.6414 15.4212 14.8004 15.4914 14.941C16.3782 16.7156 15.3226 18.5588 14.6157 19.4991L4.5001 9.38252C5.6326 8.46939 6.71635 8.22846 7.5451 8.22846C8.07097 8.22128 8.5925 8.32443 9.07604 8.53127C9.21711 8.60187 9.37685 8.62619 9.53254 8.60078C9.68822 8.57536 9.83193 8.5015 9.94323 8.38971L15.3114 3.00002L21.0001 8.68877V8.68971Z" fill="#333333"/>
                    </svg>
                    <span style={{ marginLeft: "8px" }}>기본 고정만 사용</span>
                  </div>
                  <div className="selection-desc">지정한 해시태그만 노출합니다.</div>
                </div>
              </div>

              <div data-instagram-tutorial="ai-setting-hashtag-detail">{this.renderHashtagArea()}</div>
            </div>

            <div className="card" data-instagram-tutorial="ai-setting-schedule">
              <div className="card-header">
                <h3 className="card-title">5. 자동화 동작방식 설정</h3>
                <p className="card-subtitle">지정한 주기에 맞춰 피드를 자동 제작하고, 자동 업로드·콘텐츠 목록 저장·자동 제작 중지 여부를 설정합니다.</p>
              </div>

              <div style={{ marginBottom: "24px" }}>
                <label
                  className="form-label"
                  style={{ fontSize: "15px", marginBottom: "12px" }}
                >
                  자동 제작 후 등록방식
                </label>

                <div className="radio-group" style={{ marginBottom: 0 }} data-instagram-tutorial="ai-setting-publish-type">
                  <RadioButton
                    id="publish_type_auto"
                    name="publish_type"
                    value="PUBLISH"
                    checked={this.state.is_enabled === "Y" && this.state.publish_type === "PUBLISH"}
                    onChangeCheck={() => this.onChangepublishType("PUBLISH")}
                  >
                    <label className="radio-label" style={{marginLeft: "5px"}}>
                      <span>제작 후 자동 업로드</span>
                      <div className="tooltip-wrap">
                        <span className="tooltip-icon">!</span>
                        <div className="tooltip-content">
                          스케줄에 맞춰 콘텐츠를 자동 제작하고 인스타그램에 자동 업로드합니다.
                        </div>
                      </div>
                    </label>
                  </RadioButton>

                  <RadioButton
                    id="publish_type_draft"
                    name="publish_type"
                    value="DRAFT"
                    checked={this.state.is_enabled === "Y" && this.state.publish_type === "DRAFT"}
                    onChangeCheck={() => this.onChangepublishType("DRAFT")}
                  >
                    <label className="radio-label" style={{marginLeft: "5px"}}>
                      <span>콘텐츠 목록에 저장</span>
                      <div className="tooltip-wrap">
                        <span className="tooltip-icon">!</span>
                        <div className="tooltip-content">
                          스케줄에 맞춰 콘텐츠를 자동 제작하고 콘텐츠 목록에 저장합니다. 저장된 콘텐츠는 확인 후 바로 업로드 또는 예약 업로드할 수 있습니다.
                        </div>
                      </div>
                    </label>
                  </RadioButton>

                  <RadioButton
                    id="publish_type_stop"
                    name="publish_type"
                    value="STOP"
                    checked={this.state.is_enabled === "N"}
                    onChangeCheck={() => this.onChangepublishType("STOP")}
                  >
                    <label className="radio-label" style={{marginLeft: "5px"}}>
                      <span>자동 제작 중지</span>
                      <div className="tooltip-wrap">
                        <span className="tooltip-icon">!</span>
                        <div className="tooltip-content">
                          저장된 자동화 설정은 유지하고 스케줄에 따른 자동 제작만 중지합니다.
                        </div>
                      </div>
                    </label>
                  </RadioButton>
                </div>
              </div>

              <div id="scheduled_time_grid" data-instagram-tutorial="ai-setting-timing" style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 24 }}>
                <div data-instagram-tutorial="ai-setting-cycle">
                  <label className="form-label">자동 실행 주기</label>
                  <SelectBoxV2
                    id="publishCycle"
                    pageWrapperName="InstagramFeedAutomationMerged"
                    list={PUBLISH_CYCLE_OPTIONS}
                    optionTexttName="label"
                    optionValuetName="value"
                    selectedValue={publishCycle}
                    selectedText={getLabelByValue(PUBLISH_CYCLE_OPTIONS, publishCycle)}
                    placeHolder="주기 선택"
                    onClickOption={this.handlePublishCycleChange}
                    width="100%"
                    height="43px"
                  />
                </div>

                <div>
                  <label className="form-label">실행 시간</label>
                  <div id="scheduledTime">
                    <DatePicker
                      selected={this.getDateObjForTime()}
                      onChange={this.onChangeTimePicker}
                      showTimeSelect
                      showTimeSelectOnly
                      timeIntervals={60}
                      timeCaption="시간"
                      locale={ko}
                      placeholderText="시간 선택"
                      className="form-control"
                      dateFormat="HH:mm"
                      timeFormat="HH:mm"
                    />
                  </div>
                </div>

                <div>
                  <label className="form-label">시작일</label>
                  <div id="startDate">
                    <DatePicker
                      selected={this.getDateObjForDate()}
                      onChange={this.onChangeDatePicker}
                      dateFormat="yyyy-MM-dd"
                      locale={ko}
                      placeholderText="날짜 선택"
                      className="form-control"
                    />
                  </div>
                </div>
              </div>

              {(publishCycle === "WEEKLY" || publishCycle === "THREETIMES") && (
                <div style={{ marginTop: 20 }} data-instagram-tutorial="ai-setting-days">
                  <label className="form-label">발행 요일</label>
                  <div className="day-button-wrap">
                    {DAY_LABELS.map((day) => (
                      <button key={day} type="button" className={`day-button ${activeDays.has(day) ? "active" : ""}`} onClick={() => this.toggleDay(day)}>
                        {day}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* 인스타그램 계정 연동 후 단일 선택 팝업 */}
        {this.state.show_account_connect_popup && (
          <PopupFrameV2
            className={"account-connect-popup"}
            maxWidth={"600px"}
            onClose={() => this.setState({ show_account_connect_popup: false })}
          >
            <div className="instagram_account_page">
              {(this.state.add_account_list || []).length === 0 ? (

                  <>
                    <div
                      className="instagram-no-accounts instagram-connect-guide-modal"
                      style={{
                        padding: "28px 36px 32px",
                        textAlign: "center",
                        color: "var(--deep-navy)",
                        lineHeight: "1.6",
                        wordBreak: "keep-all",
                      }}
                    >
                      <div
                        style={{
                          width: "70px",
                          height: "70px",
                          margin: "0 auto 18px",
                          borderRadius: "50%",
                          background: "rgba(0, 226, 230, 0.18)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <div
                          style={{
                            width: "52px",
                            height: "52px",
                            borderRadius: "50%",
                            background: "var(--primary-mint)",
                            color: "#fff",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: "32px",
                            fontWeight: 900,
                            boxShadow: "0 8px 18px rgba(0, 226, 230, 0.35)",
                          }}
                        >
                          !
                        </div>
                      </div>

                      <div
                        style={{
                          color: "var(--primary-mint-hover)",
                          fontSize: "17px",
                          fontWeight: 900,
                          marginBottom: "12px",
                        }}
                      >
                        인스타그램 필수가이드 확인
                      </div>

                      <div
                        style={{
                          color: "var(--deep-navy)",
                          fontSize: "26px",
                          fontWeight: 900,
                          lineHeight: "1.35",
                          letterSpacing: "-0.8px",
                        }}
                      >
                        인스타그램 규정상 <br/>
                        아래 필수 3가지 조건을 가이드를 따라 변경 후 <br/>
                        다시 계정 추가를 진행해주세요
                      </div>

                      <div
                        style={{
                          height: "1px",
                          background: "#d1d5db",
                          margin: "28px 0 22px",
                        }}
                      />

                      <div
                        style={{
                          textAlign: "left",
                          color: "var(--deep-navy)",
                          fontSize: "15px",
                          fontWeight: 700,
                          marginBottom: "16px",
                        }}
                      >
                        인스타그램 자동관리를 사용하려면 아래 3가지가 모두 필요합니다.
                      </div>

                      <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                        {[
                          "인스타그램 프로페셔널 계정 전환 (개인 계정 연동 불가)",
                          "페이스북 비즈니스 페이지 개설",
                          "인스타그램 계정과 페이스북 페이지 상호 연결",
                        ].map((item, index) => (
                          <div
                            key={item}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "14px",
                              minHeight: "54px",
                              padding: "0 16px",
                              border: "1px solid #dfe5ec",
                              borderRadius: "8px",
                              background: "#fff",
                              color: "var(--deep-navy)",
                              fontSize: "15px",
                              fontWeight: 700,
                            }}
                          >
                            <span
                              style={{
                                width: "22px",
                                height: "22px",
                                borderRadius: "50%",
                                border: "2px solid var(--primary-mint-hover)",
                                color: "var(--primary-mint-hover)",
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: "13px",
                                fontWeight: 900,
                                flexShrink: 0,
                              }}
                            >
                              ✓
                            </span>
                            <span>{index + 1}. {item}</span>
                          </div>
                        ))}
                      </div>

                      <div
                        style={{
                          display: "flex",
                          gap: "12px",
                          alignItems: "flex-start",
                          marginTop: "22px",
                          padding: "16px 18px",
                          border: "1px solid #9ee7f0",
                          borderRadius: "8px",
                          background: "linear-gradient(180deg, #f0fdff 0%, #eafaff 100%)",
                          color: "var(--deep-navy)",
                          textAlign: "left",
                          fontSize: "14px",
                          fontWeight: 700,
                          lineHeight: "1.6",
                        }}
                      >
                        <span
                          style={{
                            width: "21px",
                            height: "21px",
                            borderRadius: "50%",
                            background: "#35a9c9",
                            color: "#fff",
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: "14px",
                            fontWeight: 900,
                            flexShrink: 0,
                            marginTop: "1px",
                          }}
                        >
                          i
                        </span>
                        <span>
                          위 3가지 중 하나라도 설정되지 않으면 계정 연동이 되지 않습니다.
                          <br />
                          설정 방법은 사용가이드에서 확인해주세요.
                        </span>
                      </div>

                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns: "1fr 1fr",
                          gap: "26px",
                          marginTop: "28px",
                        }}
                      >
                        <button
                          className="btn btn-primary"
                          style={{
                            height: "52px",
                            borderRadius: "10px",
                            fontSize: "15px",
                            fontWeight: 900,
                          }}
                          onClick={() => {
                            window.open(
                              "https://iclave.notion.site/Instagram-Facebook-2d363ab4c3ce800b9d58c0d0aaa0d9ce?source=copy_link",
                              "_blank"
                            );
                          }}
                        >
                          <span style={{ fontSize: "18px", lineHeight: 1 }}>↗</span>
                          가이드 바로 보기
                        </button>

                        <button
                          className="btn"
                          style={{
                            height: "52px",
                            borderRadius: "10px",
                            background: "#f1f5f9",
                            color: "#111827",
                            fontSize: "15px",
                            fontWeight: 900,
                            boxShadow: "none",
                          }}
                          onClick={() => this.setState({ show_account_connect_popup: false })}
                        >
                          닫기
                        </button>
                      </div>
                    </div>
                  </>
              ) : (
                <>
                  <h5
                    style={{
                      marginBottom: 8,
                      fontSize: "18px",
                      fontWeight: "bold",
                    }}
                  >
                    연결할 인스타그램 계정을 선택해주세요
                  </h5>

                  <p
                    style={{
                      fontSize: "14px",
                      color: "var(--text-sub)",
                      marginBottom: 20,
                    }}
                  >
                    자동화 피드를 발행할 1개의 계정을 선택해 주세요.
                  </p>

                  {(this.state.add_account_list || []).map((account) => (
                    <div className="instagram-accounts" key={account.token_id}>
                      {(Array.isArray(account.instagram_account_list)
                        ? account.instagram_account_list
                        : []
                      )
                        .filter((instagram) => instagram && instagram.instagram_access_token_seq)
                        .map((instagram) => {
                          const accessSeq = String(instagram.instagram_access_token_seq);
                          const isSelected = this.state.selected_ig_access_seq === accessSeq;

                          return (
                            <div
                              className="account-card"
                              key={accessSeq}
                              onClick={() => {
                                this.onChangeAccount(instagram.username, accessSeq);
                              }}
                              style={{
                                display: "flex",
                                gap: "18px",
                                cursor: "pointer",
                                userSelect: "none",
                                marginBottom: "12px",
                                border: isSelected
                                  ? "2px solid var(--primary-mint)"
                                  : "1px solid #e5e7eb",
                                background: isSelected ? "#f0fdfe" : "#fff",
                                padding: "16px",
                                borderRadius: "12px",
                                transition: "all 0.2s",
                              }}
                            >
                              <div
                                className="account-wrapper"
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  width: "100%",
                                }}
                              >
                                <div
                                  className="account-left"
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "14px",
                                  }}
                                >
                                  <div
                                    className="account-avatar"
                                    style={{
                                      width: "40px",
                                      height: "40px",
                                      borderRadius: "50%",
                                      overflow: "hidden",
                                      background: "#eee",
                                    }}
                                  >
                                    {!!instagram.profile_picture_url && (
                                      <img
                                        src={instagram.profile_picture_url}
                                        alt=""
                                        style={{
                                          width: "100%",
                                          height: "100%",
                                          objectFit: "cover",
                                        }}
                                      />
                                    )}
                                  </div>

                                  <div className="account-info">
                                    <div
                                      className="account-username"
                                      style={{
                                        fontWeight: "700",
                                        color: "var(--deep-navy)",
                                        fontSize: "15px",
                                      }}
                                    >
                                      {instagram.username || "(username 없음)"}
                                    </div>
                                  </div>
                                </div>

                                {isSelected && (
                                  <div
                                    style={{
                                      marginLeft: "auto",
                                      color: "var(--primary-mint-hover)",
                                      fontWeight: "800",
                                      fontSize: "14px",
                                    }}
                                  >
                                    ✓ 선택됨
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                    </div>
                  ))}

                  <div
                    style={{
                      display: "flex",
                      width: "100%",
                      justifyContent: "flex-end",
                      marginTop: "20px",
                      padding: "0 0 max(12px, env(safe-area-inset-bottom))",
                    }}
                    onPointerDown={(e) => e.stopPropagation()}
                    onTouchStart={(e) => e.stopPropagation()}
                  >
                    <button
                      type="button"
                      className="btn btn-primary"
                      style={{ minHeight: "44px", touchAction: "manipulation" }}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        this.updateSelectedAccountsUseYn();
                      }}
                    >
                      적용 및 닫기
                    </button>
                  </div>
                </>
              )}
            </div>
          </PopupFrameV2>
        )}

        <AICopywriterModal 
          selectedId={this.state.copywriter_preset_seq}
          isOpen={showAICopywriterModal} 
          onClose={() => this.setState({ showAICopywriterModal: false })} 
          onApply={(selectedId, selectedAIPreset) => {
              const normalizedPresetSeq = normalizeCopywriterPresetSeq(selectedId);
              this.setState({
                showAICopywriterModal: false,
                copywriter_preset_seq: normalizedPresetSeq,
                selectedAIPreset: selectedAIPreset || createBasicAIPreset(),
              });
            }
          }
          getCopywriterList={(list) => {
            this.setState({ copywriter_list: list }, () => {
              this.asyncSelectedAIPreset();
            });
          }}
        />


        {this.state.favoriteTagModalOpen && (
          <div
            className="modal-overlay active"
            onClick={(e) => {
              if (e.target === e.currentTarget) this.closeFavoriteTagModal();
            }}
          >
            <div className="modal-box favorite-tag-modal">
              <div className="favorite-tag-modal-title">즐겨찾는 태그 추가</div>
              <p className="favorite-tag-modal-description">
                # 기호를 쓰지 않아도 자동으로 붙습니다. Enter를 눌러 저장할 수 있습니다.
              </p>
              <input
                type="text"
                className="form-control"
                placeholder="예: 트렌드"
                value={this.state.newFavoriteTagInput}
                onChange={(e) => this.setState({ newFavoriteTagInput: e.target.value })}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    this.addNewFavoriteTag();
                  }
                }}
                autoFocus
              />
              <div className="favorite-tag-modal-actions">
                <button type="button" className="btn btn-outline" onClick={this.closeFavoriteTagModal}>
                  취소
                </button>
                <button type="button" className="btn btn-primary" onClick={this.addNewFavoriteTag}>
                  등록
                </button>
              </div>
            </div>
          </div>
        )}

        {/** 로딩 다이얼로그 */
          (this.state.save_dialog_show_state === true)
          ? <SaveDialogV2
              content={this.state.save_dialog_content}
              button_list={this.state.save_dialog_button_list}
          />
          : ''
        }
        {/* 안내 문구 전달용 기본 다이얼로그 */
          (this.state.basic_dialog_show_state === true)
            ? <BasicDialogV2
              content={this.state.basic_dialog_content}
              content2={this.state.basic_dialog_content2}
              button_list={this.state.basic_dialog_button_list}
            />
            : ''
        }
        {/* 경고 다이얼로그 */
          this.state.warning_dialog_show_state === true &&
          <WarningDialog
              content={this.state.warning_dialog_content}
              button_list={this.state.warning_dialog_button_list}
          />
        }
        {/* 확인 취소 다이얼로그 */
          (this.state.confirm_dialog_show_state === true)
          ? <ConfirmDialogV2
              content={this.state.confirm_dialog_content}
              button_list={this.state.confirm_dialog_button_list}
              popup_close={() => {
                  this.setState({confirm_dialog_show_state: false});
              }}
          />
          : ''
        }
        {/* 제목 셋프타일 다이얼로그 */}
        {this.state.title_content_dialog_show_state &&
              <TitleContentDialogV2
                  title={this.state.title_content_dialog_title}
                  content={this.state.title_content_dialog_content}
                  button_list={this.state.title_content_dialog_button_list}
                  popup_close={()=>{this.setState({title_content_dialog_show_state: false})}}
              />
          }
      </div>
      {this.state.show_ai_image_studio_popup && (
        <AiImageStudioWorkflowClass
          view_mode="popup"
          initial_history_seq={this.getAiStudioHistorySeqForSlide(this.state.current_ai_rule_slide_no)}
          current_slide_no={this.state.current_ai_rule_slide_no}
          automation_setting_seq={this.state.ai_studio_popup_edit_context?.settingSeq || null}
          automation_slide_no={this.state.ai_studio_popup_edit_context?.slideNo || null}
          automation_history_seq={this.state.ai_studio_popup_edit_context?.historySeq || null}
          automation_local_usage_list={this.state.ai_studio_popup_edit_context?.localUsageList || []}
          series_anchor_slide_no={this.state.series_anchor_slide_no}
          ensureSeriesContextReady={this.ensureSeriesContextReadyForGenerate}
          onClose={() => this.setState({ show_ai_image_studio_popup: false })}
          onApplyRule={this.handleApplyAiImageRule}
          service_type="01014"
          series_consistency_yn={
            this.state.selectedFeedType === "SLIDE" &&
            this.state.series_consistency_yn === "Y" &&
            Number(this.state.current_ai_rule_slide_no || 1) !== Number(this.state.series_anchor_slide_no || 1)
              ? "Y"
              : "N"
          }
          series_context_json={this.state.series_context_json}
          series_anchor_image_url={this.state.series_anchor_image_url}
          series_anchor_final_prompt_text={this.state.series_anchor_final_prompt_text}
          series_context_mode={this.state.series_context_mode}
          show_apply_rule_loading={
            this.state.selectedFeedType === "SLIDE" &&
            this.state.series_consistency_yn === "Y" &&
            Number(this.state.current_ai_rule_slide_no || 1) === Number(this.state.series_anchor_slide_no || 1)
          }
          require_series_anchor_image={
            this.state.selectedFeedType === "SLIDE" &&
            this.state.series_consistency_yn === "Y" &&
            Number(this.state.current_ai_rule_slide_no || 1) === Number(this.state.series_anchor_slide_no || 1)
              ? "Y"
              : "N"
          }
          apply_rule_loading_line1="슬라이드 일관성을 유지하기 위해 결과 이미지를 분석하고 있습니다."
          apply_rule_loading_line2="잠시만 기다려주세요."
        />
      )}
      </>
    );
  }
}

export default InstagramCreateAiImageAutomation_v2;
